/**
 * MPREmbedded Library
 * Version: 1.13.1
 * Commit: 114fcd7eef198a7571395b9c2e1a476e67099094
 * Date: 2025-06-09T13:27:14.406Z
 */

// studio/src/utils/QuaternionLookRotation.ts
import {Quaternion, Vector3} from "three";
Quaternion.prototype.setFromLookRotation = function(forward, up) {
  const zAxis = forward.clone().normalize();
  const xAxis = new Vector3().crossVectors(up, zAxis).normalize();
  const yAxis = new Vector3().crossVectors(zAxis, xAxis);
  const m00 = xAxis.x, m01 = yAxis.x, m02 = zAxis.x;
  const m10 = xAxis.y, m11 = yAxis.y, m12 = zAxis.y;
  const m20 = xAxis.z, m21 = yAxis.z, m22 = zAxis.z;
  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1);
    this.w = 0.25 / s;
    this.x = (m21 - m12) * s;
    this.y = (m02 - m20) * s;
    this.z = (m10 - m01) * s;
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22);
    this.w = (m21 - m12) / s;
    this.x = 0.25 * s;
    this.y = (m01 + m10) / s;
    this.z = (m02 + m20) / s;
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22);
    this.w = (m02 - m20) / s;
    this.x = (m01 + m10) / s;
    this.y = 0.25 * s;
    this.z = (m12 + m21) / s;
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11);
    this.w = (m10 - m01) / s;
    this.x = (m02 + m20) / s;
    this.y = (m12 + m21) / s;
    this.z = 0.25 * s;
  }
  return this.normalize();
};
// studio/src/utils/Slot.ts
function slot() {
  let subscriber = null;
  function slotFunction(args) {
    if (subscriber) {
      return subscriber(args);
    }
    return;
  }
  slotFunction.on = (callback) => {
    subscriber = callback;
    return function unsubscribe() {
      subscriber = null;
    };
  };
  return slotFunction;
}

// studio/src/core/controllers/ToolsController.ts
class ToolsController {
  hostObject;
  modes;
  serviceTools;
  tools;
  activeMode;
  enable;
  constructor(hostObject) {
    this.hostObject = hostObject;
    this.tools = new Map;
    this.enable = false;
  }
  init() {
    if (!this.enable) {
      this.enable = true;
      this.initServiceTools();
      this.activeMode?.init();
    }
  }
  dispose() {
    if (this.enable) {
      this.disposeServiceTools();
      this.tools?.forEach((tool) => tool.dispose());
      this.activeMode?.dispose();
      this.activeMode = undefined;
      this.enable = false;
    }
  }
  clear() {
    this.dispose();
    this.serviceTools = undefined;
    this.modes = undefined;
    this.tools.clear();
  }
  setServiceTools(toolTypes) {
    this.serviceTools = new Set;
    toolTypes.forEach((toolType) => this.serviceTools.add(new toolType(this.hostObject)));
  }
  initServiceTools() {
    this.serviceTools?.forEach((tool) => tool.init());
  }
  disposeServiceTools() {
    this.serviceTools?.forEach((tool) => tool.dispose());
  }
  setTools(tools) {
    let toolInstance;
    tools.forEach((tool) => {
      toolInstance = new tool(this.hostObject);
      if (!this.tools.has(tool)) {
        this.tools.set(tool, toolInstance);
      }
    });
  }
  getTools() {
    return this.tools;
  }
  getTool(name) {
    return this.tools?.get(name);
  }
  getToolByName(name) {
    for (const [key, value] of this.tools) {
      if (key.name === name)
        return value;
    }
  }
  setModes(modes) {
    if (!this.modes) {
      this.modes = modes;
      Object.keys(modes).forEach((modeName) => modes[modeName].setHostObject(this.hostObject, modeName));
    }
  }
  activateMode(name) {
    if (this.modes && this.activeMode !== this.modes[name]) {
      this.enable && this.modes[name].init();
      this.activeMode = this.modes[name];
    }
  }
  getModeName() {
    return this.activeMode?.getName();
  }
  getModes() {
    return this.modes;
  }
  getMode() {
    return this.activeMode;
  }
}

// studio/src/core/view/View.ts
import {Scene, WebGLRenderer} from "three";

// studio/src/core/Layer.ts
import {Group} from "three";

// studio/src/core/containers/Container.ts
class Container {
  objects;
  hostObject;
  constructor(hostObject, objects) {
    this.hostObject = hostObject;
    this.objects = objects;
  }
}

// studio/src/core/containers/LayerContainer.ts
class LayerContainer extends Container {
  objects;
  components;
  constructor(layer, objects) {
    super(layer, objects);
    this.objects = objects;
    this.components = new Map;
    this.deleteOne = this.deleteOne.bind(this);
    this.addOne = this.addOne.bind(this);
  }
  size() {
    return this.objects.length;
  }
  add(entity) {
    if (Array.isArray(entity)) {
      const added = entity.filter(this.addOne);
      if (added.length) {
        App2.Instance().dispatchEvent({ type: "add", entities: [...entity], container: this.hostObject });
      }
    } else {
      if (this.addOne(entity)) {
        App2.Instance().dispatchEvent({ type: "add", entities: [entity], container: this.hostObject });
      }
    }
  }
  delete(entity) {
    if (Array.isArray(entity)) {
      const deleted = entity.filter(this.deleteOne);
      if (deleted.length) {
        this.hostObject.getGroup().remove(...entity.map((item) => item.object3d));
        App2.Instance().dispatchEvent({ type: "delete", entities: deleted, container: this.hostObject });
      }
    } else {
      if (this.objects.includes(entity)) {
        if (this.deleteOne(entity)) {
          this.hostObject.getGroup().remove(entity.object3d);
          App2.Instance().dispatchEvent({ type: "delete", entities: [entity], container: this.hostObject });
        }
      }
    }
  }
  addOne(entity) {
    if (entity.getLayer() !== this.hostObject) {
      this.objects.push(entity);
      entity.setLayer(this.hostObject);
      this.hostObject.getGroup().add(entity.object3d);
      entity.components.forEach((value, key) => {
        const set = this.components.get(key);
        if (set) {
          set.add(value);
        } else {
          this.components.set(key, new Set([value]));
        }
      });
      return true;
    }
    return false;
  }
  deleteOne(entity) {
    const index = this.objects.indexOf(entity);
    if (index >= 0) {
      this.objects.splice(index, 1);
      entity.setLayer(undefined);
      entity.components.forEach((value, key) => {
        const set = this.components.get(key);
        if (set) {
          set.delete(value);
        }
      });
      return true;
    }
    return false;
  }
}

// studio/src/core/Layer.ts
class Layer {
  name;
  objects;
  on;
  lock;
  view;
  group;
  container;
  constructor(view, name, objects = []) {
    this.objects = objects;
    this.on = true;
    this.lock = false;
    this.view = view;
    this.name = name;
    this.group = new Group;
    this.group.name = name;
    this.container = new LayerContainer(this, objects);
  }
  getGroup() {
    return this.group;
  }
  getObjects(force = false) {
    if (force || this.on && !this.lock) {
      return this.objects;
    }
    return [];
  }
  getObjectByName = (name, force = false) => {
    if (!(force || this.on && !this.lock))
      return;
    return this.container.objects.find((entity) => entity.object3d.name === name);
  };
  getComponent(type) {
    if (!this.on && this.lock)
      return;
    return this.container.components.get(type);
  }
  has(obj) {
    return this.objects.includes(obj);
  }
  add(object) {
    this.container.add(object);
  }
  delete(object) {
    this.container.delete(object);
  }
  clear() {
    const tmpObjects = [...this.objects];
    this.group.remove(...this.objects.map((item) => item.object3d));
    this.objects.length = 0;
    tmpObjects.forEach((obj) => {
      obj.setLayer(undefined);
    });
    this.container.components.clear();
    App2.Instance().dispatchEvent({ type: "delete", entities: tmpObjects, container: this });
  }
  hide() {
    this.group.visible = false;
  }
  show() {
    this.group.visible = true;
  }
  getName() {
    return this.name;
  }
  getView() {
    return this.view;
  }
}

// studio/src/core/view/Viewport.ts
import {Vector4} from "three";

// studio/src/core/controllers/CameraController.ts
import {OrthographicCamera, PerspectiveCamera, Vector3 as Vector33} from "three";

// studio/src/utils/cameraHelpers/CameraHelper.ts
class CameraHelper {
  cameraController;
  distanceFactor;
  constructor(cameraController) {
    this.cameraController = cameraController;
  }
  init() {
    this.handleResize();
    this.cameraController.viewport.addEventListener("resize", this.handleResize, 10);
  }
  dispose() {
    this.cameraController.viewport.removeEventListener("resize", this.handleResize);
  }
  getDistanceFactor() {
    return this.distanceFactor;
  }
  get viewport() {
    return this.cameraController.viewport;
  }
}

// studio/src/utils/cameraHelpers/OrthographicCameraHelper.ts
class OrthographicCameraHelper extends CameraHelper {
  frustumSize;
  sizeRatio;
  constructor(cameraController) {
    super(cameraController);
    this.frustumSize = 600;
    this.sizeRatio = 1;
    this.updateDistanceFactor();
  }
  get camera() {
    return this.cameraController.getOrthographicCamera();
  }
  getSizeRatio() {
    return this.sizeRatio;
  }
  handleResize = () => {
    const sizeRatio = OrthographicCameraHelper.setSize(this.camera, this.viewport.width, this.viewport.height, this.frustumSize);
    if (sizeRatio) {
      this.sizeRatio = sizeRatio;
    }
  };
  static setSize(camera, width, height, frustumSize) {
    if (width === 0 || height === 0) {
      return;
    }
    const sizeRatio = frustumSize / height;
    const aspect = width / height;
    camera.left = -0.5 * frustumSize * aspect;
    camera.right = 0.5 * frustumSize * aspect;
    camera.top = frustumSize / 2;
    camera.bottom = -frustumSize / 2;
    camera.updateProjectionMatrix();
    return sizeRatio;
  }
  updateDistanceFactor() {
    return this.distanceFactor = 1;
  }
}

// studio/src/utils/cameraHelpers/PerspectiveCameraHelper.ts
import {Vector3 as Vector32} from "three";
class PerspectiveCameraHelper extends CameraHelper {
  subCoefficient;
  offset = new Vector32;
  constructor(cameraController) {
    super(cameraController);
    this.updateDistanceFactor();
  }
  get camera() {
    return this.cameraController.getPerspectiveCamera();
  }
  handleResize = () => {
    this.camera.aspect = this.viewport.width / this.viewport.height;
    this.camera.updateProjectionMatrix();
    this.updateSubDistCoefficient();
    this.updateDistanceFactor();
  };
  updateSubDistCoefficient() {
    this.subCoefficient = 2 * Math.tan(this.camera.fov / 2 * Math.PI / 180) / this.viewport.height;
  }
  updateDistanceFactor() {
    return this.distanceFactor = this.offset.copy(this.cameraController.getPivotPoint()).sub(this.camera.position).length() * this.subCoefficient;
  }
}

// studio/src/core/controllers/CameraController.ts
var initialProps = {
  perspective: {},
  orthographic: { left: -1, right: 1, top: 1, bottom: -1, near: -1000, far: 1000 },
  zoom: 1,
  matrix: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
  type: "camera-controller",
  key: "",
  up: [0, 1, 0],
  pivotPoint: [0, 0, 0]
};

class CameraController {
  viewport;
  orthographicCamera;
  perspectiveCamera;
  cameraX;
  cameraY;
  cameraZ;
  savedState;
  camera;
  cameraHelper;
  pivotPoint;
  perspectiveCameraHelper;
  orthographicCameraHelper;
  getDistanceFactor;
  constructor(viewport, props = initialProps) {
    this.viewport = viewport;
    var { fov, aspect, near, far } = props.perspective || {};
    this.perspectiveCamera = new PerspectiveCamera(fov, aspect, near, far);
    var { left, right, top, bottom, near, far } = props.orthographic || initialProps.orthographic;
    this.orthographicCamera = new OrthographicCamera(left, right, top, bottom, near, far);
    const matrix = props.matrix ? props.matrix : initialProps.matrix;
    this.perspectiveCamera.matrix.set(...matrix);
    this.perspectiveCamera.matrixWorldNeedsUpdate = true;
    this.orthographicCamera.matrix.set(...matrix);
    this.orthographicCamera.matrixWorldNeedsUpdate = true;
    if (props.up) {
      this.perspectiveCamera.up.set(...props.up).normalize();
      this.orthographicCamera.up.set(...props.up).normalize();
    }
    this.pivotPoint = new Vector33;
    this.cameraX = new Vector33;
    this.cameraY = new Vector33;
    this.cameraZ = new Vector33;
    this.perspectiveCameraHelper = new PerspectiveCameraHelper(this);
    this.orthographicCameraHelper = new OrthographicCameraHelper(this);
    this.orthographic();
    this.onTransform = this.onTransform.bind(this);
  }
  init() {
    this.cameraHelper.init();
    App2.Instance().addEventListener("transformCamera", this.onTransform, 10);
  }
  onTransform(event) {
    if (event.viewport === this.viewport) {
      this.camera.updateMatrixWorld();
    }
  }
  updatePivotPoint(point, fireEvent = true) {
    this.pivotPoint.copy(point);
    this.viewport.dispatchEvent({ type: "updatePivotPoint", point: this.pivotPoint, fireEvent });
  }
  getPivotPoint() {
    return this.pivotPoint;
  }
  dispose() {
    this.perspectiveCameraHelper.dispose();
    this.orthographicCameraHelper.dispose();
  }
  saveState() {
    this.savedState = this.toJson();
  }
  setPosition(v, y, z) {
    this.camera.matrix.setPosition(v, y, z);
  }
  updateMatrix(matrix) {
    this.camera.matrix.copy(matrix);
    this.camera.matrixWorldNeedsUpdate = true;
  }
  orthographic() {
    this.camera = this.orthographicCamera;
    this.cameraHelper = this.orthographicCameraHelper;
    this.perspectiveCameraHelper.dispose();
    this.orthographicCameraHelper.init();
    this.getDistanceFactor = () => this.orthographicCameraHelper.updateDistanceFactor();
    this.viewport.dispatchEvent({
      type: "cameraChanged",
      previous: this.perspectiveCamera,
      current: this.orthographicCamera
    });
  }
  perspective() {
    this.camera = this.perspectiveCamera;
    this.cameraHelper = this.perspectiveCameraHelper;
    this.orthographicCameraHelper.dispose();
    this.perspectiveCameraHelper.init();
    this.getDistanceFactor = () => this.perspectiveCameraHelper.updateDistanceFactor();
    this.viewport.dispatchEvent({
      type: "cameraChanged",
      previous: this.orthographicCamera,
      current: this.perspectiveCamera
    });
  }
  copyFromActive() {
    const camera = this.camera === this.orthographicCamera ? this.perspectiveCamera : this.orthographicCamera;
    camera.matrix.copy(this.camera.matrix);
    camera.matrix.decompose(camera.position, camera.quaternion, camera.scale);
    camera.layers = this.camera.layers;
  }
  getPerspectiveCamera() {
    return this.perspectiveCamera;
  }
  getOrthographicCamera() {
    return this.orthographicCamera;
  }
  get xDir() {
    return this.cameraX.setFromMatrixColumn(this.camera.matrix, 0);
  }
  get yDir() {
    return this.cameraY.setFromMatrixColumn(this.camera.matrix, 1);
  }
  get zDir() {
    return this.cameraZ.setFromMatrixColumn(this.camera.matrix, 2);
  }
  fromJson(props) {
    this.perspectiveCamera.matrix.fromArray(props.matrix);
    this.perspectiveCamera.matrix.decompose(this.perspectiveCamera.position, this.perspectiveCamera.quaternion, this.perspectiveCamera.scale);
    this.perspectiveCamera.matrixWorldNeedsUpdate = true;
    props.perspective.fov && (this.perspectiveCamera.fov = props.perspective.fov);
    props.perspective.aspect && (this.perspectiveCamera.aspect = props.perspective.aspect);
    props.perspective.near && (this.perspectiveCamera.near = props.perspective.near);
    props.perspective.far && (this.perspectiveCamera.far = props.perspective.far);
    this.perspectiveCamera.zoom = props.zoom;
    props.up && this.perspectiveCamera.up.fromArray(props.up);
    this.perspectiveCameraHelper.handleResize();
    this.orthographicCamera.matrix.fromArray(props.matrix);
    this.orthographicCamera.matrix.decompose(this.orthographicCamera.position, this.orthographicCamera.quaternion, this.orthographicCamera.scale);
    this.orthographicCamera.matrixWorldNeedsUpdate = true;
    this.orthographicCamera.left = props.orthographic.left;
    this.orthographicCamera.right = props.orthographic.right;
    this.orthographicCamera.top = props.orthographic.top;
    this.orthographicCamera.bottom = props.orthographic.bottom;
    props.orthographic.near && (this.orthographicCamera.near = props.orthographic.near);
    props.orthographic.far && (this.orthographicCamera.far = props.orthographic.far);
    this.orthographicCamera.zoom = props.zoom;
    props.up && this.orthographicCamera.up.fromArray(props.up);
    this.orthographicCameraHelper.handleResize();
    const pivotPoint = new Vector33().fromArray(props.pivotPoint);
    this.updatePivotPoint(pivotPoint);
    this.viewport.dispatchEvent({ type: "cameraChanged", previous: this.camera, current: this.camera });
  }
  toJson() {
    return {
      perspective: {
        fov: this.perspectiveCamera.fov,
        aspect: this.perspectiveCamera.aspect,
        near: this.perspectiveCamera.near,
        far: this.perspectiveCamera.far
      },
      orthographic: {
        left: this.orthographicCamera.left,
        right: this.orthographicCamera.right,
        top: this.orthographicCamera.top,
        bottom: this.orthographicCamera.bottom,
        near: this.orthographicCamera.near,
        far: this.orthographicCamera.far
      },
      zoom: this.camera.zoom,
      matrix: this.camera.matrix.toArray(),
      type: "camera-controller",
      key: this.camera.uuid ?? "",
      up: this.camera.up.toArray(),
      pivotPoint: this.getPivotPoint().toArray()
    };
  }
  fromSavedState() {
    if (this.savedState) {
      this.fromJson(this.savedState);
    } else {
      console.error("state is not saved");
    }
  }
}

// studio/src/core/ListenerStorage.ts
class ListenerStorage {
  listeners;
  listenersForFire;
  firedListenerIndex;
  fireArgument;
  constructor() {
    this.listeners = [];
  }
  add(callback, priority = 0, force = false) {
    if (!this.has(callback)) {
      if (priority !== 0 && this.listeners.length > 0) {
        const index = this.findIndex(this.listeners, priority, 0, this.listeners.length - 1);
        this.listeners.splice(index, 0, {
          callback,
          enable: true,
          priority
        });
      } else {
        this.listeners.push({ callback, enable: true, priority });
      }
      if (force && this.listenersForFire && typeof this.firedListenerIndex !== "undefined") {
        if (priority !== 0 && this.listeners.length > 0) {
          const index = this.findIndex(this.listenersForFire, priority, 0, this.listeners.length - 1);
          if (index > this.firedListenerIndex) {
            this.listenersForFire.splice(index, 0, {
              callback,
              enable: true,
              priority
            });
          } else if (index <= this.firedListenerIndex) {
            if (this.listenersForFire[this.firedListenerIndex + 1].priority === priority) {
              this.listenersForFire.splice(this.firedListenerIndex + 1, 0, {
                callback,
                enable: true,
                priority
              });
            }
          }
        } else {
          this.listenersForFire.push({
            callback,
            enable: true,
            priority
          });
        }
      }
    }
  }
  remove(callback, force = false) {
    let index = this.listeners.findIndex((listener) => listener.callback === callback);
    if (index >= 0) {
      this.listeners.splice(index, 1);
    }
    if (force && this.listenersForFire && typeof this.firedListenerIndex !== "undefined") {
      index = this.listenersForFire.findIndex((listener) => listener.callback === callback);
      if (index > this.firedListenerIndex) {
        this.listenersForFire.splice(index, 1);
      } else {
        console.error("listener already executed");
      }
    }
  }
  findIndex(listeners, priority, start, end) {
    let middle = Math.floor((start + end) / 2);
    if (listeners[middle].priority === priority) {
      return middle;
    } else {
      if (priority < listeners[middle].priority) {
        if (middle < end) {
          return this.findIndex(listeners, priority, middle + 1, end);
        }
        return end + 1;
      } else {
        if (start < middle) {
          return this.findIndex(listeners, priority, start, middle - 1);
        }
        return start;
      }
    }
  }
  fire(arg) {
    if (typeof arg === "undefined" && typeof this.fireArgument !== "undefined") {
      arg = this.fireArgument;
    }
    this.listenersForFire = this.getListeners();
    this.listenersForFire.forEach((listener, index) => {
      this.firedListenerIndex = index;
      if (listener.enable) {
        listener.callback(arg);
      }
    });
    this.listenersForFire = undefined;
    this.firedListenerIndex = undefined;
  }
  getListeners() {
    return [...this.listeners];
  }
  clear() {
    this.listeners.splice(0);
  }
  isEmpty() {
    return this.listeners.length === 0;
  }
  has(callback) {
    return this.listeners.findIndex((listener) => listener.callback === callback) >= 0;
  }
  setEnabled(value, callback) {
    if (callback) {
      if (callback.flag === "me") {
        const listener = this.listeners.find((listener2) => listener2.callback === callback.callback);
        if (listener) {
          listener.enable = value;
        }
      } else {
        const listener = this.listeners.filter((listener2) => listener2.callback !== callback.callback);
        listener.forEach((listener2) => listener2.enable = value);
      }
    } else {
      this.listeners.forEach((listener) => listener.enable = value);
    }
  }
  enable(callback) {
    if (callback) {
      this.setEnabled(true, { callback, flag: "me" });
    } else {
      this.setEnabled(true);
    }
  }
  disable(callback) {
    if (callback) {
      this.setEnabled(false, { callback, flag: "me" });
    } else {
      this.setEnabled(false);
    }
  }
  enableExcept(callbacks) {
    if (Array.isArray(callbacks)) {
      this.disable();
      callbacks.forEach((callback) => this.setEnabled(false, { callback, flag: "me" }));
    } else {
      this.setEnabled(true, { callback: callbacks, flag: "exceptMe" });
    }
  }
  disableExcept(callbacks) {
    if (Array.isArray(callbacks)) {
      this.disable();
      callbacks.forEach((callback) => this.setEnabled(true, { callback, flag: "me" }));
    } else {
      this.setEnabled(false, { callback: callbacks, flag: "exceptMe" });
    }
  }
}

// studio/src/core/EventDispatcher.ts
class EventDispatcher {
  listeners;
  map;
  constructor(eventsList) {
    this.listeners = new Map;
    this.map = new Map;
    if (eventsList) {
      this.setListeners(eventsList);
    }
  }
  setListeners(eventsList) {
    !this.listeners.size && eventsList.forEach((event) => this.listeners.set(event, new ListenerStorage));
  }
  deleteListeners() {
    this.clearListeners();
    this.listeners.clear();
  }
  addEventListener(type, callback, priority) {
    this.listeners.get(type).add(callback, priority);
    return () => this.removeEventListener(type, callback);
  }
  addEventListenerOnce(type, callback, priority) {
    let fnOnce = this.map.get(callback);
    if (!fnOnce) {
      fnOnce = (event) => {
        callback(event);
        this.removeEventListener(type, fnOnce);
        this.map.delete(callback);
      };
      this.map.set(callback, fnOnce);
    }
    return this.addEventListener(type, fnOnce, priority);
  }
  removeEventListener(type, callback, force) {
    this.listeners.get(type)?.remove(callback, force);
  }
  hasEventListener(type, callback) {
    return this.listeners.get(type).has(callback);
  }
  eventIsListened(eventName) {
    return this.listeners.has(eventName);
  }
  dispatchEvent(event) {
    this.listeners.get(event.type).fire(event);
  }
  clearListeners(type) {
    if (type) {
      this.listeners.get(type)?.clear();
    } else {
      this.listeners.forEach((listenerStorage) => listenerStorage.clear());
    }
  }
}

// studio/src/core/view/ViewportRenderer.ts
import {LinearEncoding, NoToneMapping} from "three";

class ViewportRenderer {
  viewport;
  constructor(viewport) {
    this.viewport = viewport;
  }
}

class StandardViewportRenderer extends ViewportRenderer {
  toneMapping;
  outputEncoding;
  constructor(viewport, toneMapping = NoToneMapping, outputEncoding = LinearEncoding) {
    super(viewport);
    this.toneMapping = toneMapping;
    this.outputEncoding = outputEncoding;
  }
  render() {
    const r = this.viewport.view.getRenderer();
    r.setViewport(this.viewport.size);
    r.setScissor(this.viewport.size);
    r.setScissorTest(true);
    r.setClearColor(this.viewport.clearColor, this.viewport.alpha);
    const toneMappingOld = r.toneMapping;
    const outputEncodingOld = r.outputEncoding;
    r.toneMapping = this.toneMapping;
    r.outputEncoding = this.outputEncoding;
    r.render(this.viewport.view.scene, this.viewport.camera);
    r.toneMapping = toneMappingOld;
    r.outputEncoding = outputEncodingOld;
  }
}

// studio/src/core/view/Viewport.ts
var viewportEventNames = ["cameraChanged", "updatePivotPoint", "resize", "reposition", "setCameraRollMode"];

class Viewport extends EventDispatcher {
  view;
  size;
  domElement;
  cameraController;
  toolController;
  modes;
  name;
  rect;
  resizeObserver;
  mutationObserver;
  requestID;
  enabled;
  mutationObserverTarget;
  clearColor;
  viewportRenderer;
  alpha;
  constructor(view, name, domElement, threeLayer = 0, {
    cameraControllerProps,
    eventNames,
    mutationObserverTarget
  } = {}) {
    super(eventNames || viewportEventNames);
    this.view = view;
    this.name = name;
    this.size = new Vector4;
    this.domElement = domElement;
    if (!this.domElement)
      console.error(`[Viewport] Reference ${name} was not found`);
    this.cameraController = new CameraController(this, cameraControllerProps);
    this.toolController = new ToolsController(this);
    this.rect = this.domElement.getBoundingClientRect();
    this.handleResize = this.handleResize.bind(this);
    this.onResizeViewport = this.onResizeViewport.bind(this);
    this.resizeObserver = new ResizeObserver(this.onResizeViewport);
    this.mutationObserver = new MutationObserver(this.onMutateViewport);
    this.enabled = false;
    this.camera.layers.enable(threeLayer);
    this.clearColor = 0;
    this.alpha = 1;
    this.viewportRenderer = new StandardViewportRenderer(this);
    this.mutationObserverTarget = mutationObserverTarget;
  }
  init() {
    if (this.enabled)
      return;
    this.resizeObserver.observe(this.domElement);
    this.mutationObserver.observe(this.mutationObserverTarget || this.view.getContainer(), {
      attributeFilter: ["style", "class"],
      subtree: true
    });
    this.handleResize();
    this.cameraController.init();
    this.toolController.init();
    const modeName = this.view.toolController.getModeName();
    if (modeName) {
      this.activateMode(modeName);
    }
    this.enabled = true;
  }
  dispose() {
    if (!this.enabled)
      return;
    this.cameraController.dispose();
    this.toolController.dispose();
    this.resizeObserver.unobserve(this.domElement);
    this.mutationObserver.disconnect();
    this.enabled = false;
  }
  setDomElement(domElement) {
    if (this.domElement === domElement)
      return;
    if (this.domElement) {
      this.resizeObserver.unobserve(this.domElement);
    }
    this.resizeObserver.observe(domElement);
    App2.Instance().mouseController.redefineDomElement(domElement, this.domElement);
    this.domElement = domElement;
  }
  getDomElement() {
    return this.domElement;
  }
  handleResize() {
    this.rect = this.domElement.getBoundingClientRect();
    const { left, bottom, width, height } = this.rect;
    const { height: parentHeight, top: parentTop, left: parentLeft } = this.view.getContainer().getBoundingClientRect();
    this.size.set(left - parentLeft, parentHeight - bottom + parentTop, Math.ceil(width), Math.ceil(height));
  }
  onResizeViewport() {
    this.handleResize();
    this.dispatchEvent({ type: "resize" });
    this.renderImmediately();
  }
  onMutateViewport = (() => {
    const cachedSize = new Vector4;
    return () => {
      this.handleResize();
      if (cachedSize.x !== this.size.x || cachedSize.y !== this.size.y) {
        this.dispatchEvent({ type: "reposition" });
        App2.Instance().dispatchEvent({ type: "render" });
      }
      cachedSize.copy(this.size);
    };
  })();
  get x() {
    return this.size.x;
  }
  set x(val) {
    this.size.x = val;
  }
  get y() {
    return this.size.y;
  }
  set y(val) {
    this.size.y = val;
  }
  get width() {
    return this.size.z;
  }
  set width(val) {
    this.size.z = val;
  }
  get height() {
    return this.size.w;
  }
  set height(val) {
    this.size.w = val;
  }
  get camera() {
    return this.cameraController.camera;
  }
  get cameraLayer() {
    const mask = this.camera.layers.mask;
    if (typeof mask === "bigint") {
      return mask === 0n ? 0 : mask.toString(2).length - 1;
    }
    return 31 - Math.clz32(mask);
  }
  activateMode(modeName) {
    this.toolController.activateMode(modeName);
  }
  renderImmediately() {
    this.viewportRenderer.render();
  }
  render() {
    if (this.requestID !== undefined)
      return;
    this.requestID = window.requestAnimationFrame(() => {
      this.renderImmediately();
      this.requestID = undefined;
    });
  }
  isEnabled() {
    return this.enabled;
  }
}

// studio/src/core/containers/SelectContainer.ts
class SelectContainer extends Container {
  constructor(hostObject, objects) {
    super(hostObject, objects);
  }
  add(selectComponent) {
    if (Array.isArray(selectComponent)) {
      selectComponent.forEach((child) => {
        this.addOne(child);
      });
    } else {
      this.addOne(selectComponent);
    }
  }
  delete(selectComponent) {
    if (Array.isArray(selectComponent)) {
      selectComponent.forEach((child) => {
        this.deleteOne(child);
      });
    } else {
      this.deleteOne(selectComponent);
    }
  }
  size() {
    return this.objects.size;
  }
  addOne(selectComponent) {
    selectComponent.select();
    const layer = selectComponent.entity.getLayer();
    if (layer) {
      const selectables = this.objects.get(layer);
      if (selectables) {
        selectables.add(selectComponent);
      } else {
        this.objects.set(layer, new Set([selectComponent]));
      }
    }
  }
  deleteOne(selectComponent) {
    selectComponent.deselect();
    const layer = selectComponent.entity.getLayer();
    if (layer) {
      const selectables = this.objects.get(layer);
      if (selectables) {
        selectables.delete(selectComponent);
      }
    }
  }
}

// studio/src/core/controllers/SelectControllers.ts
class SelectController {
  objects;
  container;
  constructor() {
    this.objects = new Map;
    this.container = new SelectContainer(this, this.objects);
  }
  getSelected(layer) {
    if (layer) {
      const selected = this.objects.get(layer);
      return selected ? [...selected] : [];
    } else {
      const selected = [];
      this.objects.forEach((objects) => {
        selected.push(...objects);
      });
      return selected;
    }
  }
  add(selectables) {
    this.container.add(selectables);
    App2.Instance().dispatchEvent({
      type: "select",
      objects: Array.isArray(selectables) ? selectables : [selectables]
    });
  }
  delete(selectables) {
    this.container.delete(selectables);
    App2.Instance().dispatchEvent({
      type: "deselect",
      objects: Array.isArray(selectables) ? selectables : [selectables]
    });
  }
  clear(layer) {
    const objects = this.getSelected(layer);
    objects?.forEach((object) => object.deselect());
    if (layer) {
      this.objects.delete(layer);
    } else {
      this.objects.clear();
    }
    App2.Instance().dispatchEvent({ type: "deselect", objects });
  }
  selectedCount(layer) {
    if (layer) {
      const selected = this.objects.get(layer);
      return selected ? selected.size : 0;
    } else {
      let selectedCount = 0;
      this.objects.forEach((objects) => {
        selectedCount += objects.size;
      });
      return selectedCount;
    }
  }
  has(obj) {
    const map = this.objects;
    for (let set of map.values()) {
      if (set.has(obj)) {
        return true;
      }
    }
    return false;
  }
}

// studio/src/core/view/View.ts
class View {
  container;
  resizeObserver;
  viewports;
  renderer;
  canvas;
  scene;
  layers;
  toolController;
  selectController;
  name;
  activeViewport;
  cameraLayers;
  constructor() {
    this.layers = new Map;
    this.scene = new Scene;
    this.viewports = new Map;
    this.toolController = new ToolsController(this);
    this.selectController = new SelectController;
    this.cameraLayers = [0];
    this.handleResize = this.handleResize.bind(this);
    this.onResizeView = this.onResizeView.bind(this);
    this.resizeObserver = new ResizeObserver(this.onResizeView);
  }
  init(props) {
    this.name = props?.name;
    this.container = props?.container || document.getElementById("container") || document.body.appendChild(document.createElement("div"));
    if (!this.container)
      console.error(`[View] Reference ${this.name} was not found`);
    if (props?.renderer) {
      this.renderer = props?.renderer;
      this.canvas = this.renderer.domElement;
    } else {
      this.renderer = new WebGLRenderer(props?.rendererProps);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvas = this.renderer.domElement;
      this.canvas.dataset.belongsTo = this.name;
      if (!props?.rendererProps?.canvas) {
        this.container.appendChild(this.canvas);
        this.canvas.style.width = "100%";
        this.canvas.style.height = "100%";
      }
    }
    this.handleResize();
    this.toolController.init();
    this.resizeObserver.observe(this.container);
    App2.Instance().addEventListener("resize", this.handleResize, 1);
    App2.Instance().dispatchEvent({ type: "render" });
    this.container.addEventListener("contextmenu", (event) => event.preventDefault(), false);
  }
  dispose() {
    this.toolController.dispose();
    this.selectController.clear();
    this.clearLayers();
    this.viewports.forEach((viewport) => {
      viewport.dispose();
    });
    if (this.container)
      this.resizeObserver.unobserve(this.container);
    App2.Instance().removeEventListener("resize", this.handleResize);
  }
  clear() {
    this.dispose();
    this.viewports.forEach((viewport) => {
      this.deleteViewport(viewport.name);
    });
    this.layers.forEach((layer) => {
      this.removeLayer(layer.getName());
    });
    this.toolController.clear();
  }
  clearLayers() {
    if (!this.layers.size)
      return;
    const layers = this.getLayers();
    for (const layer of layers) {
      layer.getObjects()?.forEach((obj) => obj.dispose());
      layer.clear();
    }
    App2.Instance().dispatchEvent({ type: "render" });
  }
  getRenderer() {
    return this.renderer;
  }
  getContainer() {
    return this.container;
  }
  getCanvas() {
    return this.canvas;
  }
  onResizeView() {
    this.handleResize();
  }
  handleResize() {
    const width = this.container.clientWidth || 0;
    const height = this.container.clientHeight || 0;
    this.renderer.setSize(width, height, true);
  }
  addViewport(name, domElement, options) {
    const viewport = new Viewport(this, name, domElement, this.getEmptyCameraLayer(), options);
    this.viewports.set(name, viewport);
    return viewport;
  }
  deleteViewport(name) {
    const viewport = this.viewports.get(name);
    if (viewport) {
      const layer = viewport.cameraLayer;
      if (layer !== 0) {
        this.cameraLayers[layer] = undefined;
      }
      viewport.dispose();
      this.viewports.delete(name);
    }
  }
  getEmptyCameraLayer() {
    for (let i = 1;i < 32; i++) {
      if (!this.cameraLayers[i]) {
        this.cameraLayers[i] = i;
        return i;
      }
    }
  }
  getViewport(name) {
    return this.viewports.get(name);
  }
  addLayer(name, objects) {
    let layer = this.layers.get(name);
    if (layer) {
      objects && layer.add(objects);
      return layer;
    }
    layer = new Layer(this, name, objects);
    this.layers.set(name, layer);
    this.scene.add(layer.getGroup());
    App2.Instance().dispatchEvent({ type: "addLayer", container: layer });
    return layer;
  }
  removeLayer(name) {
    const layer = this.layers.get(name);
    if (!layer)
      return;
    layer.getObjects()?.forEach((obj) => obj.dispose());
    layer.clear();
    this.scene.remove(layer.getGroup());
    this.layers.delete(name);
    App2.Instance().dispatchEvent({ type: "deleteLayer", container: layer });
  }
  getLayer(name) {
    return this.layers.get(name);
  }
  *getLayers() {
    for (const layer of this.layers) {
      yield layer[1];
    }
  }
  *getVisibleLayers() {
    for (const layer of this.layers) {
      if (layer[1].getGroup().visible) {
        yield layer[1];
      }
    }
  }
  activateMode(modeName) {
    const previous = this.toolController.getModeName();
    if (previous === modeName)
      return;
    this.toolController.activateMode(modeName);
    this.viewports.forEach((viewport) => viewport.activateMode(modeName));
    const mode = this.toolController.getModeName();
    App2.Instance().dispatchEvent({ type: "modeChanged", previous: previous !== undefined ? previous : "", mode: mode !== undefined ? mode : "" });
  }
  setActiveViewport(viewportName) {
    const viewport = typeof viewportName === "undefined" ? viewportName : this.viewports.get(viewportName);
    if (this.activeViewport === viewport)
      return;
    App2.Instance().dispatchEvent({ type: "ActiveViewportWillChange", current: this.activeViewport, next: viewport });
    const previousViewport = this.activeViewport;
    this.activeViewport = viewport;
    App2.Instance().dispatchEvent({ type: "ActiveViewportChanged", previous: previousViewport, current: viewport });
  }
  getActiveViewport() {
    return this.activeViewport;
  }
}

// studio/src/core/UndoStack.ts
var undoStackEventNames = ["add", "undo", "redo", "clear"];

class UndoStack extends EventDispatcher {
  stack;
  index;
  lastIndex;
  constructor() {
    super(undoStackEventNames);
    this.stack = [];
    this.index = -1;
    this.lastIndex = -1;
  }
  add(command) {
    if (this.index !== this.lastIndex) {
      this.stack.splice(this.index + 1);
    }
    this.stack.push(command);
    this.index++;
    this.lastIndex = this.index;
    this.dispatchEvent({ type: "add" });
  }
  getCommandList() {
    return [...this.stack];
  }
  removeLast() {
    this.stack.pop();
    this.index--;
    this.lastIndex = this.index;
  }
  undo() {
    if (this.index >= 0) {
      this.stack[this.index].undo();
      this.index--;
      this.dispatchEvent({ type: "undo" });
    }
  }
  redo() {
    if (this.index !== this.lastIndex) {
      this.stack[this.index + 1].execute();
      this.index++;
      this.dispatchEvent({ type: "redo" });
    }
  }
  undoAll() {
    do {
      this.undo();
    } while (this.index >= 0);
  }
  getIndex() {
    return this.index;
  }
  getLastIndex() {
    return this.lastIndex;
  }
  clear() {
    this.stack = [];
    this.index = -1;
    this.lastIndex = -1;
    this.dispatchEvent({ type: "clear" });
  }
}

// studio/src/utils/PointerEventsStorage.ts
var mouseButtons = ["left", "middle", "right"];
var anyButtonEventTypesArr = ["pointerdown", "pointerup"];
var specialEventTypesArr = ["click", "dblclick", "contextmenu", "pointermove", "pointerenter", "pointerleave", "pointerout", "pointerover", "wheel"];
var isSpecialEventType = (type) => {
  return specialEventTypesArr.some((el) => el === type);
};
class PointerEventsStorage {
  element;
  listeners;
  callbacks;
  constructor(element = document) {
    this.element = element;
    this.listeners = {};
    this.callbacks = {};
  }
  init() {
    anyButtonEventTypesArr.forEach((type) => {
      this.listeners[type] = {
        left: new ListenerStorage,
        middle: new ListenerStorage,
        right: new ListenerStorage
      };
      this.callbacks[type] = {
        callback: (event) => this.listeners[type][mouseButtons[event.button]].fire(event),
        isListening: false
      };
    });
    specialEventTypesArr.forEach((type) => {
      this.listeners[type] = new ListenerStorage;
      this.callbacks[type] = {
        callback: (event) => this.listeners[type].fire(event),
        isListening: false
      };
    });
  }
  dispose() {
    if (this.listeners) {
      this.clear();
      Object.keys(this.listeners).forEach((key) => delete this.listeners[key]);
    }
  }
  changeDomElement(element) {
    const oldDomElement = this.element;
    this.element = element;
    anyButtonEventTypesArr.forEach((eventType) => {
      oldDomElement.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      this.callbacks[eventType].isListening = false;
      this.startListening(eventType);
    });
    specialEventTypesArr.forEach((eventType) => {
      oldDomElement.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      this.callbacks[eventType].isListening = false;
      this.startListening(eventType);
    });
  }
  addAnyButtonListener(eventType, button, listener, priority, force) {
    this.listeners[eventType][button].add(listener, priority, force);
    this.startListening(eventType);
  }
  addSpecialEventListeners(eventType, listener, priority, force) {
    this.listeners[eventType].add(listener, priority, force);
    this.startListening(eventType);
  }
  removeButtonListener(eventType, button, listener, force) {
    if (this.listeners) {
      this.listeners[eventType][button].remove(listener, force);
      this.stopListening(eventType);
    }
  }
  removeMoveAndWheelListener(eventType, listener, force) {
    if (this.listeners) {
      this.listeners[eventType].remove(listener, force);
      this.stopListening(eventType);
    }
  }
  startListening(eventType) {
    if (this.callbacks[eventType].callback && !this.callbacks[eventType].isListening) {
      if (!this.isEmpty(eventType)) {
        this.callbacks[eventType].isListening = true;
        this.element.addEventListener(eventType, this.callbacks[eventType].callback, { capture: false, passive: false });
      }
    }
  }
  stopListening(eventType) {
    if (this.callbacks[eventType].callback && this.callbacks[eventType].isListening) {
      if (this.isEmpty(eventType)) {
        this.callbacks[eventType].isListening = false;
        this.element.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      }
    }
  }
  clear() {
    anyButtonEventTypesArr.forEach((eventType) => {
      Object.values(this.listeners[eventType]).forEach((value) => value.clear());
      this.stopListening(eventType);
    });
    specialEventTypesArr.forEach((eventType) => {
      this.listeners[eventType].clear();
      this.stopListening(eventType);
    });
  }
  isEmpty(eventType) {
    if (isSpecialEventType(eventType)) {
      return this.listeners[eventType].isEmpty();
    }
    return Object.values(this.listeners[eventType]).every((value) => value.isEmpty());
  }
  has(eventType, listener, button) {
    if (this.listeners[eventType] instanceof ListenerStorage) {
      return this.listeners[eventType].has(listener);
    } else if (button) {
      return this.listeners[eventType][button].has(listener);
    }
    return false;
  }
  disableButton(eventType, button, listener) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].disable(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].disable(listener);
    }
  }
  enableButton(eventType, button, listener) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].enable(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].enable(listener);
    }
  }
  disableButtonExcept(eventType, listener, button) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].disableExcept(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].disableExcept(listener);
    }
  }
  enableButtonExcept(eventType, listener, button) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].enableExcept(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].enableExcept(listener);
    }
  }
  disableWheel(listener) {
    this.listeners["wheel"].disable(listener);
  }
  enableWheel(listener) {
    this.listeners["wheel"].enable(listener);
  }
  disableMove(listener) {
    this.listeners["pointermove"].disable(listener);
  }
  enableMove(listener) {
    this.listeners["pointermove"].enable(listener);
  }
  disableMoveExcept(listeners) {
    this.listeners["pointermove"].disableExcept(listeners);
  }
  enableMoveExcept(listeners) {
    this.listeners["pointermove"].enableExcept(listeners);
  }
}

// studio/src/core/controllers/PointerController.ts
class PointerController {
  listeners;
  constructor() {
    this.listeners = new Map().set(document, new PointerEventsStorage);
  }
  init() {
    this.listeners.forEach((val) => val.init());
  }
  dispose() {
    this.listeners.forEach((val) => val.dispose());
  }
  clear() {
    this.dispose();
    this.listeners.clear();
  }
  redefineDomElement(newElement, oldElement) {
    const oldElementListeners = this.listeners.get(oldElement);
    if (oldElementListeners) {
      oldElementListeners.changeDomElement(newElement);
      this.listeners.set(newElement, oldElementListeners);
      this.listeners.delete(oldElement);
    }
  }
  addListener(eventType, listener, element = document, button, priority, force) {
    let mouseEventListeners = this.listeners.get(element);
    if (!mouseEventListeners) {
      mouseEventListeners = new PointerEventsStorage(element);
      this.listeners.set(element, mouseEventListeners);
      mouseEventListeners.init();
    }
    if (isSpecialEventType(eventType)) {
      if ((!button || typeof button === "number") && typeof priority !== "number") {
        mouseEventListeners.addSpecialEventListeners(eventType, listener, button, priority);
        return () => this.removeListener(eventType, listener, element);
      } else {
        console.error("listener is not added. Event type: " + eventType);
      }
    } else if (button !== undefined) {
      if (typeof button !== "number" && typeof priority !== "boolean") {
        mouseEventListeners.addAnyButtonListener(eventType, button, listener, priority, force);
        return () => this.removeListener(eventType, listener, element, button);
      } else {
        console.error("listener is not added. Event type: " + eventType + ", button: " + button);
      }
    } else {
      console.error("listener is not added. Event type: " + eventType + ", button: " + button);
    }
  }
  removeListener(eventType, listener, element = document, button, force = false) {
    let mouseEventListeners;
    if (element) {
      mouseEventListeners = this.listeners.get(element);
    }
    if (mouseEventListeners) {
      if (isSpecialEventType(eventType)) {
        if (typeof button === "boolean" || typeof button === "undefined") {
          mouseEventListeners.removeMoveAndWheelListener(eventType, listener, button);
        }
      } else if (button !== undefined && typeof button !== "boolean") {
        mouseEventListeners.removeButtonListener(eventType, button, listener, force);
      }
    } else {
      console.warn("listener is not found");
    }
  }
}

// studio/src/core/controllers/KeyboardController.ts
var keyBoardEventTypesArr = ["keydown", "keyup", "keypress"];
class KeyboardController {
  listeners;
  callbacks;
  init() {
    this.listeners = {
      keydown: new Map,
      keyup: new Map,
      keypress: new Map
    };
    this.callbacks = {};
    keyBoardEventTypesArr.forEach((type) => {
      this.callbacks[type] = {
        callback: (event) => this.listeners[type].get(event.code)?.fire(event),
        isListening: true
      };
    });
  }
  dispose() {
    if (this.listeners) {
      keyBoardEventTypesArr.forEach((eventType) => {
        this.listeners[eventType].forEach((ListenerStorage5) => ListenerStorage5.clear());
        this.listeners[eventType].clear();
        this.stopListening(eventType);
      });
    }
  }
  addListener(eventType, listener, eventCode, priority) {
    const listenerStorage = this.listeners[eventType].get(eventCode);
    if (listenerStorage) {
      listenerStorage.add(listener, priority);
    } else {
      const storage = new ListenerStorage;
      storage.add(listener, priority);
      this.listeners[eventType].set(eventCode, storage);
    }
    this.startListening(eventType);
  }
  removeListener(eventType, listener, code) {
    const listenerStorage = this.listeners[eventType].get(code);
    if (listenerStorage) {
      listenerStorage.remove(listener);
      this.stopListening(eventType);
    }
  }
  disableKeys(keys) {
    if (keys) {
      Object.values(this.listeners).forEach((item) => keys.forEach((key) => item.get(key)?.disable()));
    } else {
      Object.values(this.listeners).forEach((item) => item.forEach((ListenerStorage5) => ListenerStorage5.disable()));
    }
  }
  enableKeys(keys) {
    if (keys) {
      Object.values(this.listeners).forEach((item) => keys.forEach((key) => item.get(key)?.enable()));
    } else {
      Object.values(this.listeners).forEach((item) => item.forEach((ListenerStorage5) => ListenerStorage5.enable()));
    }
  }
  startListening(eventType) {
    if (!this.isEmpty(eventType)) {
      document.addEventListener(eventType, this.callbacks[eventType].callback, false);
    }
  }
  stopListening(eventType) {
    if (this.callbacks[eventType].isListening) {
      if (this.isEmpty(eventType)) {
        this.callbacks[eventType].isListening = false;
        document.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      }
    }
  }
  isEmpty(eventType) {
    return this.listeners[eventType].size === 0;
  }
}

// studio/src/core/controllers/RaycastController.ts
import {Raycaster} from "three";

// studio/src/core/components/Component.ts
class Component {
  entity;
  constructor(entity) {
    this.entity = entity;
  }
}

// studio/src/core/components/Raycast.ts
class Raycast extends Component {
  constructor(entity) {
    super(entity);
  }
  isRaycast(raycaster, recursive = true) {
    const activeViewport = App2.Instance().getActiveView().getActiveViewport();
    if (activeViewport) {
      raycaster.layers.mask = activeViewport.camera.layers.mask;
      const intersections = raycaster.intersectObject(this.entity.object3d, recursive);
      return intersections.length ? intersections : false;
    }
    return false;
  }
  *intersect(raycaster) {
    if (!this.entity.object3d.visible)
      return;
    const intersections = this.isRaycast(raycaster, true);
    if (intersections) {
      yield { raycast: this, intersections };
    }
  }
}

class RaycastChildrenOnly extends Raycast {
  *intersect(raycaster) {
    for (const child of this.entity.children) {
      const raycast = child.getComponent(Raycast);
      if (raycast) {
        yield* raycast.intersect(raycaster);
      }
    }
  }
}

// studio/src/core/highlighters/Highlighter.ts
class Highlighter {
  entity;
  constructor(entity) {
    this.entity = entity;
  }
}

// studio/src/core/highlighters/NoHighlighter.ts
class NoHighlighter extends Highlighter {
  highlight() {
  }
  unHighlight() {
  }
}

// studio/src/core/components/HoverComponent.ts
class HoverComponent extends Component {
  hovered;
  highlighter;
  constructor(entity, highlighter = new NoHighlighter(entity)) {
    super(entity);
    this.hovered = false;
    this.highlighter = highlighter;
  }
  hover() {
    this.hovered = true;
    this.highlighter.highlight();
  }
  unhover() {
    this.hovered = false;
    this.highlighter.unHighlight();
  }
  isHovered() {
    return this.hovered;
  }
}

// studio/src/core/controllers/RaycastController.ts
class RaycastController {
  raycaster;
  viewport;
  state;
  _object;
  raycastData;
  constructor() {
    this.raycaster = new Raycaster;
    this.raycaster.params.Line = { threshold: 0.01 };
    this.state = {
      transformObject: false,
      transformCamera: false
    };
    this.handleChangeActiveViewport = this.handleChangeActiveViewport.bind(this);
    this.onTransformStart = this.onTransformStart.bind(this);
    this.onTransformEnd = this.onTransformEnd.bind(this);
    this.onTransformCameraStart = this.onTransformCameraStart.bind(this);
    this.onTransformCameraEnd = this.onTransformCameraEnd.bind(this);
    this.onTransformCamera = this.onTransformCamera.bind(this);
    this.update = this.update.bind(this);
  }
  get view() {
    return App2.Instance().getActiveView();
  }
  get position() {
    return App2.Instance().cursorController.position;
  }
  get object() {
    return this._object;
  }
  set object(value) {
    if (this._object === value)
      return;
    if (this._object) {
      App2.Instance().dispatchEvent({ type: "unhover", entity: this._object });
    }
    this._object = value;
    if (this._object && this._object.getComponent(HoverComponent)) {
      App2.Instance().dispatchEvent({ type: "hover", entity: this._object });
    }
  }
  getObject() {
    return this.object;
  }
  init() {
    this.changeListenActiveViewport(this.viewport, this.view.getActiveViewport());
    App2.Instance().addEventListener("ActiveViewportWillChange", this.handleChangeActiveViewport);
    App2.Instance().addEventListener("transformStart", this.onTransformStart);
    App2.Instance().addEventListener("transformCamera", this.onTransformCamera);
    App2.Instance().addEventListener("transformCameraStart", this.onTransformCameraStart);
  }
  dispose() {
    this.changeListenActiveViewport(this.viewport);
    App2.Instance().removeEventListener("ActiveViewportWillChange", this.handleChangeActiveViewport);
    App2.Instance().removeEventListener("transformStart", this.onTransformStart);
    App2.Instance().removeEventListener("transformEnd", this.onTransformEnd);
    App2.Instance().removeEventListener("transformCamera", this.onTransformCamera);
    App2.Instance().removeEventListener("transformCameraStart", this.onTransformCameraStart);
    this.raycastData = undefined;
    this.object = undefined;
  }
  changeListenActiveViewport(currentViewport, viewport) {
    if (currentViewport) {
      App2.Instance().mouseController.removeListener("pointermove", this.update, currentViewport.getDomElement(), true);
      App2.Instance().mouseController.removeListener("pointerdown", this.update, currentViewport.getDomElement(), "left", true);
      currentViewport.removeEventListener("resize", this.update);
      currentViewport.removeEventListener("reposition", this.update);
    }
    if (viewport) {
      App2.Instance().mouseController.addListener("pointermove", this.update, viewport.getDomElement(), 20 /* RaycastController */);
      App2.Instance().mouseController.addListener("pointerdown", this.update, viewport.getDomElement(), "left", 20 /* RaycastController */);
      viewport.addEventListener("resize", this.update);
      viewport.addEventListener("reposition", this.update);
      this.updateLineThreshold(viewport);
    } else if (this.object) {
      this.raycastData = undefined;
      this.object = undefined;
    }
    this.viewport = viewport;
  }
  start() {
    this.changeListenActiveViewport(this.viewport, this.view.getActiveViewport());
    this.update();
  }
  stop() {
    this.changeListenActiveViewport(this.viewport);
  }
  handleChangeActiveViewport(event) {
    this.changeListenActiveViewport(this.viewport, event.next);
  }
  updateLineThreshold(viewport) {
    const zoom = viewport.cameraController.camera.zoom;
    this.raycaster.params.Line.threshold = 60 / zoom;
  }
  onTransformCamera(event) {
    if (event.viewport === this.viewport)
      this.updateLineThreshold(this.viewport);
  }
  onTransformCameraStart() {
    if (!this.state.transformCamera && !this.state.transformObject) {
      this.stop();
    }
    App2.Instance().addEventListener("transformCameraEnd", this.onTransformCameraEnd);
    this.state.transformCamera = true;
  }
  onTransformCameraEnd() {
    this.state.transformCamera = false;
    if (!this.state.transformObject) {
      this.start();
    }
    App2.Instance().removeEventListener("transformCameraEnd", this.onTransformCameraEnd);
  }
  onTransformStart() {
    if (!this.state.transformCamera && !this.state.transformObject) {
      this.stop();
    }
    App2.Instance().addEventListener("transformEnd", this.onTransformEnd);
    this.state.transformObject = true;
  }
  onTransformEnd() {
    this.state.transformObject = false;
    if (!this.state.transformCamera) {
      this.start();
    }
    App2.Instance().removeEventListener("transformEnd", this.onTransformEnd);
  }
  getNearest(generator) {
    const results = [];
    for (const intersection of generator) {
      intersection.intersections.sort((a, b) => a.distance - b.distance);
      results.push(intersection);
    }
    if (results.length) {
      results.sort((a, b) => a.intersections[0].distance - b.intersections[0].distance);
      return results[0];
    }
  }
  *raycast() {
    this.raycaster.setFromCamera(this.position, this.viewport.camera);
    for (const layer of this.view.getVisibleLayers()) {
      const objects = layer.getComponent(Raycast);
      if (objects) {
        for (const object of objects) {
          const generator = object.intersect(this.raycaster);
          for (const intersection of generator) {
            yield intersection;
          }
        }
      }
    }
  }
  *raycastObject(object) {
    this.raycaster.setFromCamera(this.position, this.viewport.camera);
    let generator;
    if (object instanceof Raycast) {
      generator = object.intersect(this.raycaster);
    } else {
      const raycast = object.getComponent(Raycast);
      if (!raycast)
        return;
      generator = raycast.intersect(this.raycaster);
    }
    for (const intersection of generator) {
      yield intersection;
    }
  }
  getPositionOnZeroPlane(v) {
    this.raycaster.setFromCamera(this.position, this.viewport.camera);
    const distance = -this.raycaster.ray.origin.z / this.raycaster.ray.direction.z;
    v.x = this.raycaster.ray.origin.x + this.raycaster.ray.direction.x * distance;
    v.y = this.raycaster.ray.origin.y + this.raycaster.ray.direction.y * distance;
    v.z = 0;
    return v;
  }
  update() {
    this.raycastData = this.getNearest(this.raycast());
    this.object = this.raycastData?.raycast.entity;
  }
}

// studio/src/core/controllers/CursorController.ts
import {Vector2} from "three";
class CursorController {
  position = new Vector2(0, 0);
  clientPosition = new Vector2(0, 0);
  constructor() {
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleChangePosition = this.handleChangePosition.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.onChangeActiveViewport = this.onChangeActiveViewport.bind(this);
  }
  init() {
    App2.Instance().addEventListener("ActiveViewportWillChange", this.onChangeActiveViewport);
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    if (this.viewport) {
      this.viewport.addEventListener("resize", this.handleResize, 30 /* CursorController */);
      this.viewport.addEventListener("reposition", this.handleResize, 30 /* CursorController */);
      App2.Instance().mouseController.addListener("pointermove", this.handleChangePosition, this.viewport.getDomElement(), 30 /* CursorController */);
      App2.Instance().mouseController.addListener("pointerdown", this.handleChangePosition, this.viewport.getDomElement(), "left", 30 /* CursorController */);
    }
  }
  dispose() {
    App2.Instance().removeEventListener("ActiveViewportWillChange", this.onChangeActiveViewport);
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    if (this.viewport) {
      App2.Instance().mouseController.removeListener("pointermove", this.handleChangePosition, this.viewport.getDomElement());
      App2.Instance().mouseController.removeListener("pointerdown", this.handleChangePosition, this.viewport.getDomElement(), "left");
      this.viewport.removeEventListener("resize", this.handleResize);
      this.viewport.removeEventListener("reposition", this.handleResize);
    }
  }
  get view() {
    return App2.Instance().getActiveView();
  }
  get viewport() {
    return this.view.getActiveViewport();
  }
  onChangeActiveViewport(event) {
    event.current?.removeEventListener("resize", this.handleResize);
    event.next?.addEventListener("resize", this.handleResize, 30 /* CursorController */);
    event.current?.removeEventListener("reposition", this.handleResize);
    event.next?.addEventListener("reposition", this.handleResize, 30 /* CursorController */);
    if (event.current) {
      App2.Instance().mouseController.removeListener("pointermove", this.handleChangePosition, event.current.getDomElement());
      App2.Instance().mouseController.removeListener("pointerdown", this.handleChangePosition, event.current.getDomElement(), "left");
    }
    if (event.next) {
      App2.Instance().mouseController.addListener("pointermove", this.handleChangePosition, event.next.getDomElement(), 30 /* CursorController */);
      App2.Instance().mouseController.addListener("pointerdown", this.handleChangePosition, event.next.getDomElement(), "left", 30 /* CursorController */);
    }
  }
  setPosition(x, y) {
    this.position.set((x - this.viewport.rect.left) / this.viewport.rect.width * 2 - 1, -(y - this.viewport.rect.top) / this.viewport.rect.height * 2 + 1);
  }
  handleChangePosition(event) {
    this.setPosition(event.clientX, event.clientY);
  }
  handleMouseMove(event) {
    this.clientPosition.set(event.clientX, event.clientY);
  }
  handleResize() {
    this.setPosition(this.clientPosition.x, this.clientPosition.y);
  }
}

// node_modules/@bufbuild/protobuf/dist/esm/private/assert.js
function assert(condition, msg) {
  if (!condition) {
    throw new Error(msg);
  }
}
function assertInt32(arg) {
  if (typeof arg !== "number")
    throw new Error("invalid int 32: " + typeof arg);
  if (!Number.isInteger(arg) || arg > INT32_MAX || arg < INT32_MIN)
    throw new Error("invalid int 32: " + arg);
}
function assertUInt32(arg) {
  if (typeof arg !== "number")
    throw new Error("invalid uint 32: " + typeof arg);
  if (!Number.isInteger(arg) || arg > UINT32_MAX || arg < 0)
    throw new Error("invalid uint 32: " + arg);
}
function assertFloat32(arg) {
  if (typeof arg !== "number")
    throw new Error("invalid float 32: " + typeof arg);
  if (!Number.isFinite(arg))
    return;
  if (arg > FLOAT32_MAX || arg < FLOAT32_MIN)
    throw new Error("invalid float 32: " + arg);
}
var FLOAT32_MAX = 340282346638528860000000000000000000000;
var FLOAT32_MIN = -340282346638528860000000000000000000000;
var UINT32_MAX = 4294967295;
var INT32_MAX = 2147483647;
var INT32_MIN = -2147483648;

// node_modules/@bufbuild/protobuf/dist/esm/private/enum.js
function getEnumType(enumObject) {
  const t = enumObject[enumTypeSymbol];
  assert(t, "missing enum type on enum object");
  return t;
}
function setEnumType(enumObject, typeName, values, opt) {
  enumObject[enumTypeSymbol] = makeEnumType(typeName, values.map((v) => ({
    no: v.no,
    name: v.name,
    localName: enumObject[v.no]
  })), opt);
}
function makeEnumType(typeName, values, _opt) {
  const names = Object.create(null);
  const numbers = Object.create(null);
  const normalValues = [];
  for (const value of values) {
    const n = normalizeEnumValue(value);
    normalValues.push(n);
    names[value.name] = n;
    numbers[value.no] = n;
  }
  return {
    typeName,
    values: normalValues,
    findName(name) {
      return names[name];
    },
    findNumber(no) {
      return numbers[no];
    }
  };
}
function makeEnum(typeName, values, opt) {
  const enumObject = {};
  for (const value of values) {
    const n = normalizeEnumValue(value);
    enumObject[n.localName] = n.no;
    enumObject[n.no] = n.localName;
  }
  setEnumType(enumObject, typeName, values, opt);
  return enumObject;
}
function normalizeEnumValue(value) {
  if ("localName" in value) {
    return value;
  }
  return Object.assign(Object.assign({}, value), { localName: value.name });
}
var enumTypeSymbol = Symbol("@bufbuild/protobuf/enum-type");

// node_modules/@bufbuild/protobuf/dist/esm/message.js
class Message {
  equals(other) {
    return this.getType().runtime.util.equals(this.getType(), this, other);
  }
  clone() {
    return this.getType().runtime.util.clone(this);
  }
  fromBinary(bytes, options) {
    const type = this.getType(), format = type.runtime.bin, opt = format.makeReadOptions(options);
    format.readMessage(this, opt.readerFactory(bytes), bytes.byteLength, opt);
    return this;
  }
  fromJson(jsonValue, options) {
    const type = this.getType(), format = type.runtime.json, opt = format.makeReadOptions(options);
    format.readMessage(type, jsonValue, opt, this);
    return this;
  }
  fromJsonString(jsonString, options) {
    let json;
    try {
      json = JSON.parse(jsonString);
    } catch (e) {
      throw new Error(`cannot decode ${this.getType().typeName} from JSON: ${e instanceof Error ? e.message : String(e)}`);
    }
    return this.fromJson(json, options);
  }
  toBinary(options) {
    const type = this.getType(), bin = type.runtime.bin, opt = bin.makeWriteOptions(options), writer = opt.writerFactory();
    bin.writeMessage(this, writer, opt);
    return writer.finish();
  }
  toJson(options) {
    const type = this.getType(), json = type.runtime.json, opt = json.makeWriteOptions(options);
    return json.writeMessage(this, opt);
  }
  toJsonString(options) {
    var _a;
    const value = this.toJson(options);
    return JSON.stringify(value, null, (_a = options === null || options === undefined ? undefined : options.prettySpaces) !== null && _a !== undefined ? _a : 0);
  }
  toJSON() {
    return this.toJson({
      emitDefaultValues: true
    });
  }
  getType() {
    return Object.getPrototypeOf(this).constructor;
  }
}

// node_modules/@bufbuild/protobuf/dist/esm/private/message-type.js
function makeMessageType(runtime, typeName, fields, opt) {
  var _a;
  const localName = (_a = opt === null || opt === undefined ? undefined : opt.localName) !== null && _a !== undefined ? _a : typeName.substring(typeName.lastIndexOf(".") + 1);
  const type = {
    [localName]: function(data) {
      runtime.util.initFields(this);
      runtime.util.initPartial(data, this);
    }
  }[localName];
  Object.setPrototypeOf(type.prototype, new Message);
  Object.assign(type, {
    runtime,
    typeName,
    fields: runtime.util.newFieldList(fields),
    fromBinary(bytes, options) {
      return new type().fromBinary(bytes, options);
    },
    fromJson(jsonValue, options) {
      return new type().fromJson(jsonValue, options);
    },
    fromJsonString(jsonString, options) {
      return new type().fromJsonString(jsonString, options);
    },
    equals(a, b) {
      return runtime.util.equals(type, a, b);
    }
  });
  return type;
}

// node_modules/@bufbuild/protobuf/dist/esm/google/varint.js
function varint64read() {
  let lowBits = 0;
  let highBits = 0;
  for (let shift = 0;shift < 28; shift += 7) {
    let b = this.buf[this.pos++];
    lowBits |= (b & 127) << shift;
    if ((b & 128) == 0) {
      this.assertBounds();
      return [lowBits, highBits];
    }
  }
  let middleByte = this.buf[this.pos++];
  lowBits |= (middleByte & 15) << 28;
  highBits = (middleByte & 112) >> 4;
  if ((middleByte & 128) == 0) {
    this.assertBounds();
    return [lowBits, highBits];
  }
  for (let shift = 3;shift <= 31; shift += 7) {
    let b = this.buf[this.pos++];
    highBits |= (b & 127) << shift;
    if ((b & 128) == 0) {
      this.assertBounds();
      return [lowBits, highBits];
    }
  }
  throw new Error("invalid varint");
}
function varint64write(lo, hi, bytes) {
  for (let i = 0;i < 28; i = i + 7) {
    const shift = lo >>> i;
    const hasNext = !(shift >>> 7 == 0 && hi == 0);
    const byte = (hasNext ? shift | 128 : shift) & 255;
    bytes.push(byte);
    if (!hasNext) {
      return;
    }
  }
  const splitBits = lo >>> 28 & 15 | (hi & 7) << 4;
  const hasMoreBits = !(hi >> 3 == 0);
  bytes.push((hasMoreBits ? splitBits | 128 : splitBits) & 255);
  if (!hasMoreBits) {
    return;
  }
  for (let i = 3;i < 31; i = i + 7) {
    const shift = hi >>> i;
    const hasNext = !(shift >>> 7 == 0);
    const byte = (hasNext ? shift | 128 : shift) & 255;
    bytes.push(byte);
    if (!hasNext) {
      return;
    }
  }
  bytes.push(hi >>> 31 & 1);
}
function int64FromString(dec) {
  const minus = dec[0] === "-";
  if (minus) {
    dec = dec.slice(1);
  }
  const base = 1e6;
  let lowBits = 0;
  let highBits = 0;
  function add1e6digit(begin, end) {
    const digit1e6 = Number(dec.slice(begin, end));
    highBits *= base;
    lowBits = lowBits * base + digit1e6;
    if (lowBits >= TWO_PWR_32_DBL) {
      highBits = highBits + (lowBits / TWO_PWR_32_DBL | 0);
      lowBits = lowBits % TWO_PWR_32_DBL;
    }
  }
  add1e6digit(-24, -18);
  add1e6digit(-18, -12);
  add1e6digit(-12, -6);
  add1e6digit(-6);
  return minus ? negate(lowBits, highBits) : newBits(lowBits, highBits);
}
function int64ToString(lo, hi) {
  let bits = newBits(lo, hi);
  const negative = bits.hi & 2147483648;
  if (negative) {
    bits = negate(bits.lo, bits.hi);
  }
  const result = uInt64ToString(bits.lo, bits.hi);
  return negative ? "-" + result : result;
}
function uInt64ToString(lo, hi) {
  ({ lo, hi } = toUnsigned(lo, hi));
  if (hi <= 2097151) {
    return String(TWO_PWR_32_DBL * hi + lo);
  }
  const low = lo & 16777215;
  const mid = (lo >>> 24 | hi << 8) & 16777215;
  const high = hi >> 16 & 65535;
  let digitA = low + mid * 6777216 + high * 6710656;
  let digitB = mid + high * 8147497;
  let digitC = high * 2;
  const base = 1e7;
  if (digitA >= base) {
    digitB += Math.floor(digitA / base);
    digitA %= base;
  }
  if (digitB >= base) {
    digitC += Math.floor(digitB / base);
    digitB %= base;
  }
  return digitC.toString() + decimalFrom1e7WithLeadingZeros(digitB) + decimalFrom1e7WithLeadingZeros(digitA);
}
function toUnsigned(lo, hi) {
  return { lo: lo >>> 0, hi: hi >>> 0 };
}
function newBits(lo, hi) {
  return { lo: lo | 0, hi: hi | 0 };
}
function negate(lowBits, highBits) {
  highBits = ~highBits;
  if (lowBits) {
    lowBits = ~lowBits + 1;
  } else {
    highBits += 1;
  }
  return newBits(lowBits, highBits);
}
function varint32write(value, bytes) {
  if (value >= 0) {
    while (value > 127) {
      bytes.push(value & 127 | 128);
      value = value >>> 7;
    }
    bytes.push(value);
  } else {
    for (let i = 0;i < 9; i++) {
      bytes.push(value & 127 | 128);
      value = value >> 7;
    }
    bytes.push(1);
  }
}
function varint32read() {
  let b = this.buf[this.pos++];
  let result = b & 127;
  if ((b & 128) == 0) {
    this.assertBounds();
    return result;
  }
  b = this.buf[this.pos++];
  result |= (b & 127) << 7;
  if ((b & 128) == 0) {
    this.assertBounds();
    return result;
  }
  b = this.buf[this.pos++];
  result |= (b & 127) << 14;
  if ((b & 128) == 0) {
    this.assertBounds();
    return result;
  }
  b = this.buf[this.pos++];
  result |= (b & 127) << 21;
  if ((b & 128) == 0) {
    this.assertBounds();
    return result;
  }
  b = this.buf[this.pos++];
  result |= (b & 15) << 28;
  for (let readBytes = 5;(b & 128) !== 0 && readBytes < 10; readBytes++)
    b = this.buf[this.pos++];
  if ((b & 128) != 0)
    throw new Error("invalid varint");
  this.assertBounds();
  return result >>> 0;
}
var TWO_PWR_32_DBL = 4294967296;
var decimalFrom1e7WithLeadingZeros = (digit1e7) => {
  const partial = String(digit1e7);
  return "0000000".slice(partial.length) + partial;
};

// node_modules/@bufbuild/protobuf/dist/esm/proto-int64.js
function makeInt64Support() {
  const dv = new DataView(new ArrayBuffer(8));
  const ok = typeof BigInt === "function" && typeof dv.getBigInt64 === "function" && typeof dv.getBigUint64 === "function" && typeof dv.setBigInt64 === "function" && typeof dv.setBigUint64 === "function" && (typeof process != "object" || typeof process.env != "object" || process.env.BUF_BIGINT_DISABLE !== "1");
  if (ok) {
    const MIN = BigInt("-9223372036854775808"), MAX = BigInt("9223372036854775807"), UMIN = BigInt("0"), UMAX = BigInt("18446744073709551615");
    return {
      zero: BigInt(0),
      supported: true,
      parse(value) {
        const bi = typeof value == "bigint" ? value : BigInt(value);
        if (bi > MAX || bi < MIN) {
          throw new Error(`int64 invalid: ${value}`);
        }
        return bi;
      },
      uParse(value) {
        const bi = typeof value == "bigint" ? value : BigInt(value);
        if (bi > UMAX || bi < UMIN) {
          throw new Error(`uint64 invalid: ${value}`);
        }
        return bi;
      },
      enc(value) {
        dv.setBigInt64(0, this.parse(value), true);
        return {
          lo: dv.getInt32(0, true),
          hi: dv.getInt32(4, true)
        };
      },
      uEnc(value) {
        dv.setBigInt64(0, this.uParse(value), true);
        return {
          lo: dv.getInt32(0, true),
          hi: dv.getInt32(4, true)
        };
      },
      dec(lo, hi) {
        dv.setInt32(0, lo, true);
        dv.setInt32(4, hi, true);
        return dv.getBigInt64(0, true);
      },
      uDec(lo, hi) {
        dv.setInt32(0, lo, true);
        dv.setInt32(4, hi, true);
        return dv.getBigUint64(0, true);
      }
    };
  }
  const assertInt64String = (value) => assert(/^-?[0-9]+$/.test(value), `int64 invalid: ${value}`);
  const assertUInt64String = (value) => assert(/^[0-9]+$/.test(value), `uint64 invalid: ${value}`);
  return {
    zero: "0",
    supported: false,
    parse(value) {
      if (typeof value != "string") {
        value = value.toString();
      }
      assertInt64String(value);
      return value;
    },
    uParse(value) {
      if (typeof value != "string") {
        value = value.toString();
      }
      assertUInt64String(value);
      return value;
    },
    enc(value) {
      if (typeof value != "string") {
        value = value.toString();
      }
      assertInt64String(value);
      return int64FromString(value);
    },
    uEnc(value) {
      if (typeof value != "string") {
        value = value.toString();
      }
      assertUInt64String(value);
      return int64FromString(value);
    },
    dec(lo, hi) {
      return int64ToString(lo, hi);
    },
    uDec(lo, hi) {
      return uInt64ToString(lo, hi);
    }
  };
}
var protoInt64 = makeInt64Support();

// node_modules/@bufbuild/protobuf/dist/esm/scalar.js
var ScalarType;
(function(ScalarType2) {
  ScalarType2[ScalarType2["DOUBLE"] = 1] = "DOUBLE";
  ScalarType2[ScalarType2["FLOAT"] = 2] = "FLOAT";
  ScalarType2[ScalarType2["INT64"] = 3] = "INT64";
  ScalarType2[ScalarType2["UINT64"] = 4] = "UINT64";
  ScalarType2[ScalarType2["INT32"] = 5] = "INT32";
  ScalarType2[ScalarType2["FIXED64"] = 6] = "FIXED64";
  ScalarType2[ScalarType2["FIXED32"] = 7] = "FIXED32";
  ScalarType2[ScalarType2["BOOL"] = 8] = "BOOL";
  ScalarType2[ScalarType2["STRING"] = 9] = "STRING";
  ScalarType2[ScalarType2["BYTES"] = 12] = "BYTES";
  ScalarType2[ScalarType2["UINT32"] = 13] = "UINT32";
  ScalarType2[ScalarType2["SFIXED32"] = 15] = "SFIXED32";
  ScalarType2[ScalarType2["SFIXED64"] = 16] = "SFIXED64";
  ScalarType2[ScalarType2["SINT32"] = 17] = "SINT32";
  ScalarType2[ScalarType2["SINT64"] = 18] = "SINT64";
})(ScalarType || (ScalarType = {}));
var LongType;
(function(LongType2) {
  LongType2[LongType2["BIGINT"] = 0] = "BIGINT";
  LongType2[LongType2["STRING"] = 1] = "STRING";
})(LongType || (LongType = {}));

// node_modules/@bufbuild/protobuf/dist/esm/private/scalars.js
function scalarEquals(type, a, b) {
  if (a === b) {
    return true;
  }
  if (type == ScalarType.BYTES) {
    if (!(a instanceof Uint8Array) || !(b instanceof Uint8Array)) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0;i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }
  switch (type) {
    case ScalarType.UINT64:
    case ScalarType.FIXED64:
    case ScalarType.INT64:
    case ScalarType.SFIXED64:
    case ScalarType.SINT64:
      return a == b;
  }
  return false;
}
function scalarZeroValue(type, longType) {
  switch (type) {
    case ScalarType.BOOL:
      return false;
    case ScalarType.UINT64:
    case ScalarType.FIXED64:
    case ScalarType.INT64:
    case ScalarType.SFIXED64:
    case ScalarType.SINT64:
      return longType == 0 ? protoInt64.zero : "0";
    case ScalarType.DOUBLE:
    case ScalarType.FLOAT:
      return 0;
    case ScalarType.BYTES:
      return new Uint8Array(0);
    case ScalarType.STRING:
      return "";
    default:
      return 0;
  }
}
function isScalarZeroValue(type, value) {
  switch (type) {
    case ScalarType.BOOL:
      return value === false;
    case ScalarType.STRING:
      return value === "";
    case ScalarType.BYTES:
      return value instanceof Uint8Array && !value.byteLength;
    default:
      return value == 0;
  }
}

// node_modules/@bufbuild/protobuf/dist/esm/binary-encoding.js
var WireType;
(function(WireType2) {
  WireType2[WireType2["Varint"] = 0] = "Varint";
  WireType2[WireType2["Bit64"] = 1] = "Bit64";
  WireType2[WireType2["LengthDelimited"] = 2] = "LengthDelimited";
  WireType2[WireType2["StartGroup"] = 3] = "StartGroup";
  WireType2[WireType2["EndGroup"] = 4] = "EndGroup";
  WireType2[WireType2["Bit32"] = 5] = "Bit32";
})(WireType || (WireType = {}));

class BinaryWriter {
  constructor(textEncoder) {
    this.stack = [];
    this.textEncoder = textEncoder !== null && textEncoder !== undefined ? textEncoder : new TextEncoder;
    this.chunks = [];
    this.buf = [];
  }
  finish() {
    this.chunks.push(new Uint8Array(this.buf));
    let len = 0;
    for (let i = 0;i < this.chunks.length; i++)
      len += this.chunks[i].length;
    let bytes = new Uint8Array(len);
    let offset = 0;
    for (let i = 0;i < this.chunks.length; i++) {
      bytes.set(this.chunks[i], offset);
      offset += this.chunks[i].length;
    }
    this.chunks = [];
    return bytes;
  }
  fork() {
    this.stack.push({ chunks: this.chunks, buf: this.buf });
    this.chunks = [];
    this.buf = [];
    return this;
  }
  join() {
    let chunk = this.finish();
    let prev = this.stack.pop();
    if (!prev)
      throw new Error("invalid state, fork stack empty");
    this.chunks = prev.chunks;
    this.buf = prev.buf;
    this.uint32(chunk.byteLength);
    return this.raw(chunk);
  }
  tag(fieldNo, type) {
    return this.uint32((fieldNo << 3 | type) >>> 0);
  }
  raw(chunk) {
    if (this.buf.length) {
      this.chunks.push(new Uint8Array(this.buf));
      this.buf = [];
    }
    this.chunks.push(chunk);
    return this;
  }
  uint32(value) {
    assertUInt32(value);
    while (value > 127) {
      this.buf.push(value & 127 | 128);
      value = value >>> 7;
    }
    this.buf.push(value);
    return this;
  }
  int32(value) {
    assertInt32(value);
    varint32write(value, this.buf);
    return this;
  }
  bool(value) {
    this.buf.push(value ? 1 : 0);
    return this;
  }
  bytes(value) {
    this.uint32(value.byteLength);
    return this.raw(value);
  }
  string(value) {
    let chunk = this.textEncoder.encode(value);
    this.uint32(chunk.byteLength);
    return this.raw(chunk);
  }
  float(value) {
    assertFloat32(value);
    let chunk = new Uint8Array(4);
    new DataView(chunk.buffer).setFloat32(0, value, true);
    return this.raw(chunk);
  }
  double(value) {
    let chunk = new Uint8Array(8);
    new DataView(chunk.buffer).setFloat64(0, value, true);
    return this.raw(chunk);
  }
  fixed32(value) {
    assertUInt32(value);
    let chunk = new Uint8Array(4);
    new DataView(chunk.buffer).setUint32(0, value, true);
    return this.raw(chunk);
  }
  sfixed32(value) {
    assertInt32(value);
    let chunk = new Uint8Array(4);
    new DataView(chunk.buffer).setInt32(0, value, true);
    return this.raw(chunk);
  }
  sint32(value) {
    assertInt32(value);
    value = (value << 1 ^ value >> 31) >>> 0;
    varint32write(value, this.buf);
    return this;
  }
  sfixed64(value) {
    let chunk = new Uint8Array(8), view = new DataView(chunk.buffer), tc = protoInt64.enc(value);
    view.setInt32(0, tc.lo, true);
    view.setInt32(4, tc.hi, true);
    return this.raw(chunk);
  }
  fixed64(value) {
    let chunk = new Uint8Array(8), view = new DataView(chunk.buffer), tc = protoInt64.uEnc(value);
    view.setInt32(0, tc.lo, true);
    view.setInt32(4, tc.hi, true);
    return this.raw(chunk);
  }
  int64(value) {
    let tc = protoInt64.enc(value);
    varint64write(tc.lo, tc.hi, this.buf);
    return this;
  }
  sint64(value) {
    let tc = protoInt64.enc(value), sign = tc.hi >> 31, lo = tc.lo << 1 ^ sign, hi = (tc.hi << 1 | tc.lo >>> 31) ^ sign;
    varint64write(lo, hi, this.buf);
    return this;
  }
  uint64(value) {
    let tc = protoInt64.uEnc(value);
    varint64write(tc.lo, tc.hi, this.buf);
    return this;
  }
}

class BinaryReader {
  constructor(buf, textDecoder) {
    this.varint64 = varint64read;
    this.uint32 = varint32read;
    this.buf = buf;
    this.len = buf.length;
    this.pos = 0;
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    this.textDecoder = textDecoder !== null && textDecoder !== undefined ? textDecoder : new TextDecoder;
  }
  tag() {
    let tag = this.uint32(), fieldNo = tag >>> 3, wireType = tag & 7;
    if (fieldNo <= 0 || wireType < 0 || wireType > 5)
      throw new Error("illegal tag: field no " + fieldNo + " wire type " + wireType);
    return [fieldNo, wireType];
  }
  skip(wireType, fieldNo) {
    let start = this.pos;
    switch (wireType) {
      case WireType.Varint:
        while (this.buf[this.pos++] & 128) {
        }
        break;
      case WireType.Bit64:
        this.pos += 4;
      case WireType.Bit32:
        this.pos += 4;
        break;
      case WireType.LengthDelimited:
        let len = this.uint32();
        this.pos += len;
        break;
      case WireType.StartGroup:
        for (;; ) {
          const [fn, wt] = this.tag();
          if (wt === WireType.EndGroup) {
            if (fieldNo !== undefined && fn !== fieldNo) {
              throw new Error("invalid end group tag");
            }
            break;
          }
          this.skip(wt, fn);
        }
        break;
      default:
        throw new Error("cant skip wire type " + wireType);
    }
    this.assertBounds();
    return this.buf.subarray(start, this.pos);
  }
  assertBounds() {
    if (this.pos > this.len)
      throw new RangeError("premature EOF");
  }
  int32() {
    return this.uint32() | 0;
  }
  sint32() {
    let zze = this.uint32();
    return zze >>> 1 ^ -(zze & 1);
  }
  int64() {
    return protoInt64.dec(...this.varint64());
  }
  uint64() {
    return protoInt64.uDec(...this.varint64());
  }
  sint64() {
    let [lo, hi] = this.varint64();
    let s = -(lo & 1);
    lo = (lo >>> 1 | (hi & 1) << 31) ^ s;
    hi = hi >>> 1 ^ s;
    return protoInt64.dec(lo, hi);
  }
  bool() {
    let [lo, hi] = this.varint64();
    return lo !== 0 || hi !== 0;
  }
  fixed32() {
    return this.view.getUint32((this.pos += 4) - 4, true);
  }
  sfixed32() {
    return this.view.getInt32((this.pos += 4) - 4, true);
  }
  fixed64() {
    return protoInt64.uDec(this.sfixed32(), this.sfixed32());
  }
  sfixed64() {
    return protoInt64.dec(this.sfixed32(), this.sfixed32());
  }
  float() {
    return this.view.getFloat32((this.pos += 4) - 4, true);
  }
  double() {
    return this.view.getFloat64((this.pos += 8) - 8, true);
  }
  bytes() {
    let len = this.uint32(), start = this.pos;
    this.pos += len;
    this.assertBounds();
    return this.buf.subarray(start, start + len);
  }
  string() {
    return this.textDecoder.decode(this.bytes());
  }
}

// node_modules/@bufbuild/protobuf/dist/esm/private/extensions.js
function makeExtension(runtime, typeName, extendee, field) {
  let fi;
  return {
    typeName,
    extendee,
    get field() {
      if (!fi) {
        const i = typeof field == "function" ? field() : field;
        i.name = typeName.split(".").pop();
        i.jsonName = `[${typeName}]`;
        fi = runtime.util.newFieldList([i]).list()[0];
      }
      return fi;
    },
    runtime
  };
}
function createExtensionContainer(extension) {
  const localName = extension.field.localName;
  const container = Object.create(null);
  container[localName] = initExtensionField(extension);
  return [container, () => container[localName]];
}
function initExtensionField(ext) {
  const field = ext.field;
  if (field.repeated) {
    return [];
  }
  if (field.default !== undefined) {
    return field.default;
  }
  switch (field.kind) {
    case "enum":
      return field.T.values[0].no;
    case "scalar":
      return scalarZeroValue(field.T, field.L);
    case "message":
      const T = field.T, value = new T;
      return T.fieldWrapper ? T.fieldWrapper.unwrapField(value) : value;
    case "map":
      throw "map fields are not allowed to be extensions";
  }
}
function filterUnknownFields(unknownFields, field) {
  if (!field.repeated && (field.kind == "enum" || field.kind == "scalar")) {
    for (let i = unknownFields.length - 1;i >= 0; --i) {
      if (unknownFields[i].no == field.no) {
        return [unknownFields[i]];
      }
    }
    return [];
  }
  return unknownFields.filter((uf) => uf.no === field.no);
}

// node_modules/@bufbuild/protobuf/dist/esm/proto-base64.js
var encTable = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/".split("");
var decTable = [];
for (let i = 0;i < encTable.length; i++)
  decTable[encTable[i].charCodeAt(0)] = i;
decTable["-".charCodeAt(0)] = encTable.indexOf("+");
decTable["_".charCodeAt(0)] = encTable.indexOf("/");
var protoBase64 = {
  dec(base64Str) {
    let es = base64Str.length * 3 / 4;
    if (base64Str[base64Str.length - 2] == "=")
      es -= 2;
    else if (base64Str[base64Str.length - 1] == "=")
      es -= 1;
    let bytes = new Uint8Array(es), bytePos = 0, groupPos = 0, b, p = 0;
    for (let i = 0;i < base64Str.length; i++) {
      b = decTable[base64Str.charCodeAt(i)];
      if (b === undefined) {
        switch (base64Str[i]) {
          case "=":
            groupPos = 0;
          case "\n":
          case "\r":
          case "\t":
          case " ":
            continue;
          default:
            throw Error("invalid base64 string.");
        }
      }
      switch (groupPos) {
        case 0:
          p = b;
          groupPos = 1;
          break;
        case 1:
          bytes[bytePos++] = p << 2 | (b & 48) >> 4;
          p = b;
          groupPos = 2;
          break;
        case 2:
          bytes[bytePos++] = (p & 15) << 4 | (b & 60) >> 2;
          p = b;
          groupPos = 3;
          break;
        case 3:
          bytes[bytePos++] = (p & 3) << 6 | b;
          groupPos = 0;
          break;
      }
    }
    if (groupPos == 1)
      throw Error("invalid base64 string.");
    return bytes.subarray(0, bytePos);
  },
  enc(bytes) {
    let base64 = "", groupPos = 0, b, p = 0;
    for (let i = 0;i < bytes.length; i++) {
      b = bytes[i];
      switch (groupPos) {
        case 0:
          base64 += encTable[b >> 2];
          p = (b & 3) << 4;
          groupPos = 1;
          break;
        case 1:
          base64 += encTable[p | b >> 4];
          p = (b & 15) << 2;
          groupPos = 2;
          break;
        case 2:
          base64 += encTable[p | b >> 6];
          base64 += encTable[b & 63];
          groupPos = 0;
          break;
      }
    }
    if (groupPos) {
      base64 += encTable[p];
      base64 += "=";
      if (groupPos == 1)
        base64 += "=";
    }
    return base64;
  }
};

// node_modules/@bufbuild/protobuf/dist/esm/extension-accessor.js
function getExtension(message2, extension, options) {
  assertExtendee(extension, message2);
  const opt = extension.runtime.bin.makeReadOptions(options);
  const ufs = filterUnknownFields(message2.getType().runtime.bin.listUnknownFields(message2), extension.field);
  const [container, get] = createExtensionContainer(extension);
  for (const uf of ufs) {
    extension.runtime.bin.readField(container, opt.readerFactory(uf.data), extension.field, uf.wireType, opt);
  }
  return get();
}
function setExtension(message2, extension, value, options) {
  assertExtendee(extension, message2);
  const readOpt = extension.runtime.bin.makeReadOptions(options);
  const writeOpt = extension.runtime.bin.makeWriteOptions(options);
  if (hasExtension(message2, extension)) {
    const ufs = message2.getType().runtime.bin.listUnknownFields(message2).filter((uf) => uf.no != extension.field.no);
    message2.getType().runtime.bin.discardUnknownFields(message2);
    for (const uf of ufs) {
      message2.getType().runtime.bin.onUnknownField(message2, uf.no, uf.wireType, uf.data);
    }
  }
  const writer = writeOpt.writerFactory();
  let f = extension.field;
  if (!f.opt && !f.repeated && (f.kind == "enum" || f.kind == "scalar")) {
    f = Object.assign(Object.assign({}, extension.field), { opt: true });
  }
  extension.runtime.bin.writeField(f, value, writer, writeOpt);
  const reader = readOpt.readerFactory(writer.finish());
  while (reader.pos < reader.len) {
    const [no, wireType] = reader.tag();
    const data = reader.skip(wireType, no);
    message2.getType().runtime.bin.onUnknownField(message2, no, wireType, data);
  }
}
function hasExtension(message2, extension) {
  const messageType = message2.getType();
  return extension.extendee.typeName === messageType.typeName && !!messageType.runtime.bin.listUnknownFields(message2).find((uf) => uf.no == extension.field.no);
}
function assertExtendee(extension, message2) {
  assert(extension.extendee.typeName == message2.getType().typeName, `extension ${extension.typeName} can only be applied to message ${extension.extendee.typeName}`);
}

// node_modules/@bufbuild/protobuf/dist/esm/private/reflect.js
function isFieldSet(field, target) {
  const localName = field.localName;
  if (field.repeated) {
    return target[localName].length > 0;
  }
  if (field.oneof) {
    return target[field.oneof.localName].case === localName;
  }
  switch (field.kind) {
    case "enum":
    case "scalar":
      if (field.opt || field.req) {
        return target[localName] !== undefined;
      }
      if (field.kind == "enum") {
        return target[localName] !== field.T.values[0].no;
      }
      return !isScalarZeroValue(field.T, target[localName]);
    case "message":
      return target[localName] !== undefined;
    case "map":
      return Object.keys(target[localName]).length > 0;
  }
}
function clearField(field, target) {
  const localName = field.localName;
  const implicitPresence = !field.opt && !field.req;
  if (field.repeated) {
    target[localName] = [];
  } else if (field.oneof) {
    target[field.oneof.localName] = { case: undefined };
  } else {
    switch (field.kind) {
      case "map":
        target[localName] = {};
        break;
      case "enum":
        target[localName] = implicitPresence ? field.T.values[0].no : undefined;
        break;
      case "scalar":
        target[localName] = implicitPresence ? scalarZeroValue(field.T, field.L) : undefined;
        break;
      case "message":
        target[localName] = undefined;
        break;
    }
  }
}

// node_modules/@bufbuild/protobuf/dist/esm/is-message.js
function isMessage(arg, type) {
  if (arg === null || typeof arg != "object") {
    return false;
  }
  if (!Object.getOwnPropertyNames(Message.prototype).every((m) => (m in arg) && typeof arg[m] == "function")) {
    return false;
  }
  const actualType = arg.getType();
  if (actualType === null || typeof actualType != "function" || !("typeName" in actualType) || typeof actualType.typeName != "string") {
    return false;
  }
  return type === undefined ? true : actualType.typeName == type.typeName;
}

// node_modules/@bufbuild/protobuf/dist/esm/private/field-wrapper.js
function wrapField(type, value) {
  if (isMessage(value) || !type.fieldWrapper) {
    return value;
  }
  return type.fieldWrapper.wrapField(value);
}
var wktWrapperToScalarType = {
  "google.protobuf.DoubleValue": ScalarType.DOUBLE,
  "google.protobuf.FloatValue": ScalarType.FLOAT,
  "google.protobuf.Int64Value": ScalarType.INT64,
  "google.protobuf.UInt64Value": ScalarType.UINT64,
  "google.protobuf.Int32Value": ScalarType.INT32,
  "google.protobuf.UInt32Value": ScalarType.UINT32,
  "google.protobuf.BoolValue": ScalarType.BOOL,
  "google.protobuf.StringValue": ScalarType.STRING,
  "google.protobuf.BytesValue": ScalarType.BYTES
};

// node_modules/@bufbuild/protobuf/dist/esm/private/json-format.js
function makeReadOptions(options) {
  return options ? Object.assign(Object.assign({}, jsonReadDefaults), options) : jsonReadDefaults;
}
function makeWriteOptions(options) {
  return options ? Object.assign(Object.assign({}, jsonWriteDefaults), options) : jsonWriteDefaults;
}
function makeJsonFormat() {
  return {
    makeReadOptions,
    makeWriteOptions,
    readMessage(type, json, options, message3) {
      if (json == null || Array.isArray(json) || typeof json != "object") {
        throw new Error(`cannot decode message ${type.typeName} from JSON: ${debugJsonValue(json)}`);
      }
      message3 = message3 !== null && message3 !== undefined ? message3 : new type;
      const oneofSeen = new Map;
      const registry = options.typeRegistry;
      for (const [jsonKey, jsonValue] of Object.entries(json)) {
        const field = type.fields.findJsonName(jsonKey);
        if (field) {
          if (field.oneof) {
            if (jsonValue === null && field.kind == "scalar") {
              continue;
            }
            const seen = oneofSeen.get(field.oneof);
            if (seen !== undefined) {
              throw new Error(`cannot decode message ${type.typeName} from JSON: multiple keys for oneof "${field.oneof.name}" present: "${seen}", "${jsonKey}"`);
            }
            oneofSeen.set(field.oneof, jsonKey);
          }
          readField(message3, jsonValue, field, options, type);
        } else {
          let found = false;
          if ((registry === null || registry === undefined ? undefined : registry.findExtension) && jsonKey.startsWith("[") && jsonKey.endsWith("]")) {
            const ext = registry.findExtension(jsonKey.substring(1, jsonKey.length - 1));
            if (ext && ext.extendee.typeName == type.typeName) {
              found = true;
              const [container, get] = createExtensionContainer(ext);
              readField(container, jsonValue, ext.field, options, ext);
              setExtension(message3, ext, get(), options);
            }
          }
          if (!found && !options.ignoreUnknownFields) {
            throw new Error(`cannot decode message ${type.typeName} from JSON: key "${jsonKey}" is unknown`);
          }
        }
      }
      return message3;
    },
    writeMessage(message3, options) {
      const type = message3.getType();
      const json = {};
      let field;
      try {
        for (field of type.fields.byNumber()) {
          if (!isFieldSet(field, message3)) {
            if (field.req) {
              throw `required field not set`;
            }
            if (!options.emitDefaultValues) {
              continue;
            }
            if (!canEmitFieldDefaultValue(field)) {
              continue;
            }
          }
          const value = field.oneof ? message3[field.oneof.localName].value : message3[field.localName];
          const jsonValue = writeField(field, value, options);
          if (jsonValue !== undefined) {
            json[options.useProtoFieldName ? field.name : field.jsonName] = jsonValue;
          }
        }
        const registry = options.typeRegistry;
        if (registry === null || registry === undefined ? undefined : registry.findExtensionFor) {
          for (const uf of type.runtime.bin.listUnknownFields(message3)) {
            const ext = registry.findExtensionFor(type.typeName, uf.no);
            if (ext && hasExtension(message3, ext)) {
              const value = getExtension(message3, ext, options);
              const jsonValue = writeField(ext.field, value, options);
              if (jsonValue !== undefined) {
                json[ext.field.jsonName] = jsonValue;
              }
            }
          }
        }
      } catch (e) {
        const m = field ? `cannot encode field ${type.typeName}.${field.name} to JSON` : `cannot encode message ${type.typeName} to JSON`;
        const r = e instanceof Error ? e.message : String(e);
        throw new Error(m + (r.length > 0 ? `: ${r}` : ""));
      }
      return json;
    },
    readScalar(type, json, longType) {
      return readScalar(type, json, longType !== null && longType !== undefined ? longType : LongType.BIGINT, true);
    },
    writeScalar(type, value, emitDefaultValues) {
      if (value === undefined) {
        return;
      }
      if (emitDefaultValues || isScalarZeroValue(type, value)) {
        return writeScalar(type, value);
      }
      return;
    },
    debug: debugJsonValue
  };
}
function debugJsonValue(json) {
  if (json === null) {
    return "null";
  }
  switch (typeof json) {
    case "object":
      return Array.isArray(json) ? "array" : "object";
    case "string":
      return json.length > 100 ? "string" : `"${json.split('"').join('\\"')}"`;
    default:
      return String(json);
  }
}
function readField(target, jsonValue, field, options, parentType) {
  let localName = field.localName;
  if (field.repeated) {
    assert(field.kind != "map");
    if (jsonValue === null) {
      return;
    }
    if (!Array.isArray(jsonValue)) {
      throw new Error(`cannot decode field ${parentType.typeName}.${field.name} from JSON: ${debugJsonValue(jsonValue)}`);
    }
    const targetArray = target[localName];
    for (const jsonItem of jsonValue) {
      if (jsonItem === null) {
        throw new Error(`cannot decode field ${parentType.typeName}.${field.name} from JSON: ${debugJsonValue(jsonItem)}`);
      }
      switch (field.kind) {
        case "message":
          targetArray.push(field.T.fromJson(jsonItem, options));
          break;
        case "enum":
          const enumValue = readEnum(field.T, jsonItem, options.ignoreUnknownFields, true);
          if (enumValue !== tokenIgnoredUnknownEnum) {
            targetArray.push(enumValue);
          }
          break;
        case "scalar":
          try {
            targetArray.push(readScalar(field.T, jsonItem, field.L, true));
          } catch (e) {
            let m = `cannot decode field ${parentType.typeName}.${field.name} from JSON: ${debugJsonValue(jsonItem)}`;
            if (e instanceof Error && e.message.length > 0) {
              m += `: ${e.message}`;
            }
            throw new Error(m);
          }
          break;
      }
    }
  } else if (field.kind == "map") {
    if (jsonValue === null) {
      return;
    }
    if (typeof jsonValue != "object" || Array.isArray(jsonValue)) {
      throw new Error(`cannot decode field ${parentType.typeName}.${field.name} from JSON: ${debugJsonValue(jsonValue)}`);
    }
    const targetMap = target[localName];
    for (const [jsonMapKey, jsonMapValue] of Object.entries(jsonValue)) {
      if (jsonMapValue === null) {
        throw new Error(`cannot decode field ${parentType.typeName}.${field.name} from JSON: map value null`);
      }
      let key;
      try {
        key = readMapKey(field.K, jsonMapKey);
      } catch (e) {
        let m = `cannot decode map key for field ${parentType.typeName}.${field.name} from JSON: ${debugJsonValue(jsonValue)}`;
        if (e instanceof Error && e.message.length > 0) {
          m += `: ${e.message}`;
        }
        throw new Error(m);
      }
      switch (field.V.kind) {
        case "message":
          targetMap[key] = field.V.T.fromJson(jsonMapValue, options);
          break;
        case "enum":
          const enumValue = readEnum(field.V.T, jsonMapValue, options.ignoreUnknownFields, true);
          if (enumValue !== tokenIgnoredUnknownEnum) {
            targetMap[key] = enumValue;
          }
          break;
        case "scalar":
          try {
            targetMap[key] = readScalar(field.V.T, jsonMapValue, LongType.BIGINT, true);
          } catch (e) {
            let m = `cannot decode map value for field ${parentType.typeName}.${field.name} from JSON: ${debugJsonValue(jsonValue)}`;
            if (e instanceof Error && e.message.length > 0) {
              m += `: ${e.message}`;
            }
            throw new Error(m);
          }
          break;
      }
    }
  } else {
    if (field.oneof) {
      target = target[field.oneof.localName] = { case: localName };
      localName = "value";
    }
    switch (field.kind) {
      case "message":
        const messageType = field.T;
        if (jsonValue === null && messageType.typeName != "google.protobuf.Value") {
          return;
        }
        let currentValue = target[localName];
        if (isMessage(currentValue)) {
          currentValue.fromJson(jsonValue, options);
        } else {
          target[localName] = currentValue = messageType.fromJson(jsonValue, options);
          if (messageType.fieldWrapper && !field.oneof) {
            target[localName] = messageType.fieldWrapper.unwrapField(currentValue);
          }
        }
        break;
      case "enum":
        const enumValue = readEnum(field.T, jsonValue, options.ignoreUnknownFields, false);
        switch (enumValue) {
          case tokenNull:
            clearField(field, target);
            break;
          case tokenIgnoredUnknownEnum:
            break;
          default:
            target[localName] = enumValue;
            break;
        }
        break;
      case "scalar":
        try {
          const scalarValue = readScalar(field.T, jsonValue, field.L, false);
          switch (scalarValue) {
            case tokenNull:
              clearField(field, target);
              break;
            default:
              target[localName] = scalarValue;
              break;
          }
        } catch (e) {
          let m = `cannot decode field ${parentType.typeName}.${field.name} from JSON: ${debugJsonValue(jsonValue)}`;
          if (e instanceof Error && e.message.length > 0) {
            m += `: ${e.message}`;
          }
          throw new Error(m);
        }
        break;
    }
  }
}
function readMapKey(type, json) {
  if (type === ScalarType.BOOL) {
    switch (json) {
      case "true":
        json = true;
        break;
      case "false":
        json = false;
        break;
    }
  }
  return readScalar(type, json, LongType.BIGINT, true).toString();
}
function readScalar(type, json, longType, nullAsZeroValue) {
  if (json === null) {
    if (nullAsZeroValue) {
      return scalarZeroValue(type, longType);
    }
    return tokenNull;
  }
  switch (type) {
    case ScalarType.DOUBLE:
    case ScalarType.FLOAT:
      if (json === "NaN")
        return Number.NaN;
      if (json === "Infinity")
        return Number.POSITIVE_INFINITY;
      if (json === "-Infinity")
        return Number.NEGATIVE_INFINITY;
      if (json === "") {
        break;
      }
      if (typeof json == "string" && json.trim().length !== json.length) {
        break;
      }
      if (typeof json != "string" && typeof json != "number") {
        break;
      }
      const float = Number(json);
      if (Number.isNaN(float)) {
        break;
      }
      if (!Number.isFinite(float)) {
        break;
      }
      if (type == ScalarType.FLOAT)
        assertFloat32(float);
      return float;
    case ScalarType.INT32:
    case ScalarType.FIXED32:
    case ScalarType.SFIXED32:
    case ScalarType.SINT32:
    case ScalarType.UINT32:
      let int32;
      if (typeof json == "number")
        int32 = json;
      else if (typeof json == "string" && json.length > 0) {
        if (json.trim().length === json.length)
          int32 = Number(json);
      }
      if (int32 === undefined)
        break;
      if (type == ScalarType.UINT32 || type == ScalarType.FIXED32)
        assertUInt32(int32);
      else
        assertInt32(int32);
      return int32;
    case ScalarType.INT64:
    case ScalarType.SFIXED64:
    case ScalarType.SINT64:
      if (typeof json != "number" && typeof json != "string")
        break;
      const long = protoInt64.parse(json);
      return longType ? long.toString() : long;
    case ScalarType.FIXED64:
    case ScalarType.UINT64:
      if (typeof json != "number" && typeof json != "string")
        break;
      const uLong = protoInt64.uParse(json);
      return longType ? uLong.toString() : uLong;
    case ScalarType.BOOL:
      if (typeof json !== "boolean")
        break;
      return json;
    case ScalarType.STRING:
      if (typeof json !== "string") {
        break;
      }
      try {
        encodeURIComponent(json);
      } catch (e) {
        throw new Error("invalid UTF8");
      }
      return json;
    case ScalarType.BYTES:
      if (json === "")
        return new Uint8Array(0);
      if (typeof json !== "string")
        break;
      return protoBase64.dec(json);
  }
  throw new Error;
}
function readEnum(type, json, ignoreUnknownFields, nullAsZeroValue) {
  if (json === null) {
    if (type.typeName == "google.protobuf.NullValue") {
      return 0;
    }
    return nullAsZeroValue ? type.values[0].no : tokenNull;
  }
  switch (typeof json) {
    case "number":
      if (Number.isInteger(json)) {
        return json;
      }
      break;
    case "string":
      const value = type.findName(json);
      if (value !== undefined) {
        return value.no;
      }
      if (ignoreUnknownFields) {
        return tokenIgnoredUnknownEnum;
      }
      break;
  }
  throw new Error(`cannot decode enum ${type.typeName} from JSON: ${debugJsonValue(json)}`);
}
function canEmitFieldDefaultValue(field) {
  if (field.repeated || field.kind == "map") {
    return true;
  }
  if (field.oneof) {
    return false;
  }
  if (field.kind == "message") {
    return false;
  }
  if (field.opt || field.req) {
    return false;
  }
  return true;
}
function writeField(field, value, options) {
  if (field.kind == "map") {
    assert(typeof value == "object" && value != null);
    const jsonObj = {};
    const entries = Object.entries(value);
    switch (field.V.kind) {
      case "scalar":
        for (const [entryKey, entryValue] of entries) {
          jsonObj[entryKey.toString()] = writeScalar(field.V.T, entryValue);
        }
        break;
      case "message":
        for (const [entryKey, entryValue] of entries) {
          jsonObj[entryKey.toString()] = entryValue.toJson(options);
        }
        break;
      case "enum":
        const enumType = field.V.T;
        for (const [entryKey, entryValue] of entries) {
          jsonObj[entryKey.toString()] = writeEnum(enumType, entryValue, options.enumAsInteger);
        }
        break;
    }
    return options.emitDefaultValues || entries.length > 0 ? jsonObj : undefined;
  }
  if (field.repeated) {
    assert(Array.isArray(value));
    const jsonArr = [];
    switch (field.kind) {
      case "scalar":
        for (let i = 0;i < value.length; i++) {
          jsonArr.push(writeScalar(field.T, value[i]));
        }
        break;
      case "enum":
        for (let i = 0;i < value.length; i++) {
          jsonArr.push(writeEnum(field.T, value[i], options.enumAsInteger));
        }
        break;
      case "message":
        for (let i = 0;i < value.length; i++) {
          jsonArr.push(value[i].toJson(options));
        }
        break;
    }
    return options.emitDefaultValues || jsonArr.length > 0 ? jsonArr : undefined;
  }
  switch (field.kind) {
    case "scalar":
      return writeScalar(field.T, value);
    case "enum":
      return writeEnum(field.T, value, options.enumAsInteger);
    case "message":
      return wrapField(field.T, value).toJson(options);
  }
}
function writeEnum(type, value, enumAsInteger) {
  var _a;
  assert(typeof value == "number");
  if (type.typeName == "google.protobuf.NullValue") {
    return null;
  }
  if (enumAsInteger) {
    return value;
  }
  const val = type.findNumber(value);
  return (_a = val === null || val === undefined ? undefined : val.name) !== null && _a !== undefined ? _a : value;
}
function writeScalar(type, value) {
  switch (type) {
    case ScalarType.INT32:
    case ScalarType.SFIXED32:
    case ScalarType.SINT32:
    case ScalarType.FIXED32:
    case ScalarType.UINT32:
      assert(typeof value == "number");
      return value;
    case ScalarType.FLOAT:
    case ScalarType.DOUBLE:
      assert(typeof value == "number");
      if (Number.isNaN(value))
        return "NaN";
      if (value === Number.POSITIVE_INFINITY)
        return "Infinity";
      if (value === Number.NEGATIVE_INFINITY)
        return "-Infinity";
      return value;
    case ScalarType.STRING:
      assert(typeof value == "string");
      return value;
    case ScalarType.BOOL:
      assert(typeof value == "boolean");
      return value;
    case ScalarType.UINT64:
    case ScalarType.FIXED64:
    case ScalarType.INT64:
    case ScalarType.SFIXED64:
    case ScalarType.SINT64:
      assert(typeof value == "bigint" || typeof value == "string" || typeof value == "number");
      return value.toString();
    case ScalarType.BYTES:
      assert(value instanceof Uint8Array);
      return protoBase64.enc(value);
  }
}
var jsonReadDefaults = {
  ignoreUnknownFields: false
};
var jsonWriteDefaults = {
  emitDefaultValues: false,
  enumAsInteger: false,
  useProtoFieldName: false,
  prettySpaces: 0
};
var tokenNull = Symbol();
var tokenIgnoredUnknownEnum = Symbol();

// node_modules/@bufbuild/protobuf/dist/esm/private/binary-format.js
function makeReadOptions2(options) {
  return options ? Object.assign(Object.assign({}, readDefaults), options) : readDefaults;
}
function makeWriteOptions2(options) {
  return options ? Object.assign(Object.assign({}, writeDefaults), options) : writeDefaults;
}
function makeBinaryFormat() {
  return {
    makeReadOptions: makeReadOptions2,
    makeWriteOptions: makeWriteOptions2,
    listUnknownFields(message3) {
      var _a;
      return (_a = message3[unknownFieldsSymbol]) !== null && _a !== undefined ? _a : [];
    },
    discardUnknownFields(message3) {
      delete message3[unknownFieldsSymbol];
    },
    writeUnknownFields(message3, writer) {
      const m = message3;
      const c = m[unknownFieldsSymbol];
      if (c) {
        for (const f of c) {
          writer.tag(f.no, f.wireType).raw(f.data);
        }
      }
    },
    onUnknownField(message3, no, wireType, data) {
      const m = message3;
      if (!Array.isArray(m[unknownFieldsSymbol])) {
        m[unknownFieldsSymbol] = [];
      }
      m[unknownFieldsSymbol].push({ no, wireType, data });
    },
    readMessage(message3, reader, lengthOrEndTagFieldNo, options, delimitedMessageEncoding) {
      const type = message3.getType();
      const end = delimitedMessageEncoding ? reader.len : reader.pos + lengthOrEndTagFieldNo;
      let fieldNo, wireType;
      while (reader.pos < end) {
        [fieldNo, wireType] = reader.tag();
        if (delimitedMessageEncoding === true && wireType == WireType.EndGroup) {
          break;
        }
        const field = type.fields.find(fieldNo);
        if (!field) {
          const data = reader.skip(wireType, fieldNo);
          if (options.readUnknownFields) {
            this.onUnknownField(message3, fieldNo, wireType, data);
          }
          continue;
        }
        readField2(message3, reader, field, wireType, options);
      }
      if (delimitedMessageEncoding && (wireType != WireType.EndGroup || fieldNo !== lengthOrEndTagFieldNo)) {
        throw new Error(`invalid end group tag`);
      }
    },
    readField: readField2,
    writeMessage(message3, writer, options) {
      const type = message3.getType();
      for (const field of type.fields.byNumber()) {
        if (!isFieldSet(field, message3)) {
          if (field.req) {
            throw new Error(`cannot encode field ${type.typeName}.${field.name} to binary: required field not set`);
          }
          continue;
        }
        const value = field.oneof ? message3[field.oneof.localName].value : message3[field.localName];
        writeField2(field, value, writer, options);
      }
      if (options.writeUnknownFields) {
        this.writeUnknownFields(message3, writer);
      }
      return writer;
    },
    writeField(field, value, writer, options) {
      if (value === undefined) {
        return;
      }
      writeField2(field, value, writer, options);
    }
  };
}
function readField2(target, reader, field, wireType, options) {
  let { repeated, localName } = field;
  if (field.oneof) {
    target = target[field.oneof.localName];
    if (target.case != localName) {
      delete target.value;
    }
    target.case = localName;
    localName = "value";
  }
  switch (field.kind) {
    case "scalar":
    case "enum":
      const scalarType = field.kind == "enum" ? ScalarType.INT32 : field.T;
      let read = readScalar2;
      if (field.kind == "scalar" && field.L > 0) {
        read = readScalarLTString;
      }
      if (repeated) {
        let arr = target[localName];
        const isPacked = wireType == WireType.LengthDelimited && scalarType != ScalarType.STRING && scalarType != ScalarType.BYTES;
        if (isPacked) {
          let e = reader.uint32() + reader.pos;
          while (reader.pos < e) {
            arr.push(read(reader, scalarType));
          }
        } else {
          arr.push(read(reader, scalarType));
        }
      } else {
        target[localName] = read(reader, scalarType);
      }
      break;
    case "message":
      const messageType = field.T;
      if (repeated) {
        target[localName].push(readMessageField(reader, new messageType, options, field));
      } else {
        if (isMessage(target[localName])) {
          readMessageField(reader, target[localName], options, field);
        } else {
          target[localName] = readMessageField(reader, new messageType, options, field);
          if (messageType.fieldWrapper && !field.oneof && !field.repeated) {
            target[localName] = messageType.fieldWrapper.unwrapField(target[localName]);
          }
        }
      }
      break;
    case "map":
      let [mapKey, mapVal] = readMapEntry(field, reader, options);
      target[localName][mapKey] = mapVal;
      break;
  }
}
function readMessageField(reader, message3, options, field) {
  const format = message3.getType().runtime.bin;
  const delimited = field === null || field === undefined ? undefined : field.delimited;
  format.readMessage(message3, reader, delimited ? field.no : reader.uint32(), options, delimited);
  return message3;
}
function readMapEntry(field, reader, options) {
  const length2 = reader.uint32(), end = reader.pos + length2;
  let key, val;
  while (reader.pos < end) {
    const [fieldNo] = reader.tag();
    switch (fieldNo) {
      case 1:
        key = readScalar2(reader, field.K);
        break;
      case 2:
        switch (field.V.kind) {
          case "scalar":
            val = readScalar2(reader, field.V.T);
            break;
          case "enum":
            val = reader.int32();
            break;
          case "message":
            val = readMessageField(reader, new field.V.T, options, undefined);
            break;
        }
        break;
    }
  }
  if (key === undefined) {
    key = scalarZeroValue(field.K, LongType.BIGINT);
  }
  if (typeof key != "string" && typeof key != "number") {
    key = key.toString();
  }
  if (val === undefined) {
    switch (field.V.kind) {
      case "scalar":
        val = scalarZeroValue(field.V.T, LongType.BIGINT);
        break;
      case "enum":
        val = field.V.T.values[0].no;
        break;
      case "message":
        val = new field.V.T;
        break;
    }
  }
  return [key, val];
}
function readScalarLTString(reader, type) {
  const v = readScalar2(reader, type);
  return typeof v == "bigint" ? v.toString() : v;
}
function readScalar2(reader, type) {
  switch (type) {
    case ScalarType.STRING:
      return reader.string();
    case ScalarType.BOOL:
      return reader.bool();
    case ScalarType.DOUBLE:
      return reader.double();
    case ScalarType.FLOAT:
      return reader.float();
    case ScalarType.INT32:
      return reader.int32();
    case ScalarType.INT64:
      return reader.int64();
    case ScalarType.UINT64:
      return reader.uint64();
    case ScalarType.FIXED64:
      return reader.fixed64();
    case ScalarType.BYTES:
      return reader.bytes();
    case ScalarType.FIXED32:
      return reader.fixed32();
    case ScalarType.SFIXED32:
      return reader.sfixed32();
    case ScalarType.SFIXED64:
      return reader.sfixed64();
    case ScalarType.SINT64:
      return reader.sint64();
    case ScalarType.UINT32:
      return reader.uint32();
    case ScalarType.SINT32:
      return reader.sint32();
  }
}
function writeField2(field, value, writer, options) {
  assert(value !== undefined);
  const repeated = field.repeated;
  switch (field.kind) {
    case "scalar":
    case "enum":
      let scalarType = field.kind == "enum" ? ScalarType.INT32 : field.T;
      if (repeated) {
        assert(Array.isArray(value));
        if (field.packed) {
          writePacked(writer, scalarType, field.no, value);
        } else {
          for (const item of value) {
            writeScalar2(writer, scalarType, field.no, item);
          }
        }
      } else {
        writeScalar2(writer, scalarType, field.no, value);
      }
      break;
    case "message":
      if (repeated) {
        assert(Array.isArray(value));
        for (const item of value) {
          writeMessageField(writer, options, field, item);
        }
      } else {
        writeMessageField(writer, options, field, value);
      }
      break;
    case "map":
      assert(typeof value == "object" && value != null);
      for (const [key, val] of Object.entries(value)) {
        writeMapEntry(writer, options, field, key, val);
      }
      break;
  }
}
function writeMapEntry(writer, options, field, key, value) {
  writer.tag(field.no, WireType.LengthDelimited);
  writer.fork();
  let keyValue = key;
  switch (field.K) {
    case ScalarType.INT32:
    case ScalarType.FIXED32:
    case ScalarType.UINT32:
    case ScalarType.SFIXED32:
    case ScalarType.SINT32:
      keyValue = Number.parseInt(key);
      break;
    case ScalarType.BOOL:
      assert(key == "true" || key == "false");
      keyValue = key == "true";
      break;
  }
  writeScalar2(writer, field.K, 1, keyValue);
  switch (field.V.kind) {
    case "scalar":
      writeScalar2(writer, field.V.T, 2, value);
      break;
    case "enum":
      writeScalar2(writer, ScalarType.INT32, 2, value);
      break;
    case "message":
      assert(value !== undefined);
      writer.tag(2, WireType.LengthDelimited).bytes(value.toBinary(options));
      break;
  }
  writer.join();
}
function writeMessageField(writer, options, field, value) {
  const message3 = wrapField(field.T, value);
  if (field.delimited)
    writer.tag(field.no, WireType.StartGroup).raw(message3.toBinary(options)).tag(field.no, WireType.EndGroup);
  else
    writer.tag(field.no, WireType.LengthDelimited).bytes(message3.toBinary(options));
}
function writeScalar2(writer, type, fieldNo, value) {
  assert(value !== undefined);
  let [wireType, method] = scalarTypeInfo(type);
  writer.tag(fieldNo, wireType)[method](value);
}
function writePacked(writer, type, fieldNo, value) {
  if (!value.length) {
    return;
  }
  writer.tag(fieldNo, WireType.LengthDelimited).fork();
  let [, method] = scalarTypeInfo(type);
  for (let i = 0;i < value.length; i++) {
    writer[method](value[i]);
  }
  writer.join();
}
function scalarTypeInfo(type) {
  let wireType = WireType.Varint;
  switch (type) {
    case ScalarType.BYTES:
    case ScalarType.STRING:
      wireType = WireType.LengthDelimited;
      break;
    case ScalarType.DOUBLE:
    case ScalarType.FIXED64:
    case ScalarType.SFIXED64:
      wireType = WireType.Bit64;
      break;
    case ScalarType.FIXED32:
    case ScalarType.SFIXED32:
    case ScalarType.FLOAT:
      wireType = WireType.Bit32;
      break;
  }
  const method = ScalarType[type].toLowerCase();
  return [wireType, method];
}
var unknownFieldsSymbol = Symbol("@bufbuild/protobuf/unknown-fields");
var readDefaults = {
  readUnknownFields: true,
  readerFactory: (bytes) => new BinaryReader(bytes)
};
var writeDefaults = {
  writeUnknownFields: true,
  writerFactory: () => new BinaryWriter
};

// node_modules/@bufbuild/protobuf/dist/esm/private/util-common.js
function makeUtilCommon() {
  return {
    setEnumType,
    initPartial(source, target) {
      if (source === undefined) {
        return;
      }
      const type = target.getType();
      for (const member of type.fields.byMember()) {
        const localName = member.localName, t = target, s = source;
        if (s[localName] == null) {
          continue;
        }
        switch (member.kind) {
          case "oneof":
            const sk = s[localName].case;
            if (sk === undefined) {
              continue;
            }
            const sourceField = member.findField(sk);
            let val = s[localName].value;
            if (sourceField && sourceField.kind == "message" && !isMessage(val, sourceField.T)) {
              val = new sourceField.T(val);
            } else if (sourceField && sourceField.kind === "scalar" && sourceField.T === ScalarType.BYTES) {
              val = toU8Arr(val);
            }
            t[localName] = { case: sk, value: val };
            break;
          case "scalar":
          case "enum":
            let copy = s[localName];
            if (member.T === ScalarType.BYTES) {
              copy = member.repeated ? copy.map(toU8Arr) : toU8Arr(copy);
            }
            t[localName] = copy;
            break;
          case "map":
            switch (member.V.kind) {
              case "scalar":
              case "enum":
                if (member.V.T === ScalarType.BYTES) {
                  for (const [k, v] of Object.entries(s[localName])) {
                    t[localName][k] = toU8Arr(v);
                  }
                } else {
                  Object.assign(t[localName], s[localName]);
                }
                break;
              case "message":
                const messageType = member.V.T;
                for (const k of Object.keys(s[localName])) {
                  let val2 = s[localName][k];
                  if (!messageType.fieldWrapper) {
                    val2 = new messageType(val2);
                  }
                  t[localName][k] = val2;
                }
                break;
            }
            break;
          case "message":
            const mt = member.T;
            if (member.repeated) {
              t[localName] = s[localName].map((val2) => isMessage(val2, mt) ? val2 : new mt(val2));
            } else {
              const val2 = s[localName];
              if (mt.fieldWrapper) {
                if (mt.typeName === "google.protobuf.BytesValue") {
                  t[localName] = toU8Arr(val2);
                } else {
                  t[localName] = val2;
                }
              } else {
                t[localName] = isMessage(val2, mt) ? val2 : new mt(val2);
              }
            }
            break;
        }
      }
    },
    equals(type, a, b) {
      if (a === b) {
        return true;
      }
      if (!a || !b) {
        return false;
      }
      return type.fields.byMember().every((m) => {
        const va = a[m.localName];
        const vb = b[m.localName];
        if (m.repeated) {
          if (va.length !== vb.length) {
            return false;
          }
          switch (m.kind) {
            case "message":
              return va.every((a2, i) => m.T.equals(a2, vb[i]));
            case "scalar":
              return va.every((a2, i) => scalarEquals(m.T, a2, vb[i]));
            case "enum":
              return va.every((a2, i) => scalarEquals(ScalarType.INT32, a2, vb[i]));
          }
          throw new Error(`repeated cannot contain ${m.kind}`);
        }
        switch (m.kind) {
          case "message":
            return m.T.equals(va, vb);
          case "enum":
            return scalarEquals(ScalarType.INT32, va, vb);
          case "scalar":
            return scalarEquals(m.T, va, vb);
          case "oneof":
            if (va.case !== vb.case) {
              return false;
            }
            const s = m.findField(va.case);
            if (s === undefined) {
              return true;
            }
            switch (s.kind) {
              case "message":
                return s.T.equals(va.value, vb.value);
              case "enum":
                return scalarEquals(ScalarType.INT32, va.value, vb.value);
              case "scalar":
                return scalarEquals(s.T, va.value, vb.value);
            }
            throw new Error(`oneof cannot contain ${s.kind}`);
          case "map":
            const keys = Object.keys(va).concat(Object.keys(vb));
            switch (m.V.kind) {
              case "message":
                const messageType = m.V.T;
                return keys.every((k) => messageType.equals(va[k], vb[k]));
              case "enum":
                return keys.every((k) => scalarEquals(ScalarType.INT32, va[k], vb[k]));
              case "scalar":
                const scalarType = m.V.T;
                return keys.every((k) => scalarEquals(scalarType, va[k], vb[k]));
            }
            break;
        }
      });
    },
    clone(message3) {
      const type = message3.getType(), target = new type, any = target;
      for (const member of type.fields.byMember()) {
        const source = message3[member.localName];
        let copy;
        if (member.repeated) {
          copy = source.map(cloneSingularField);
        } else if (member.kind == "map") {
          copy = any[member.localName];
          for (const [key, v] of Object.entries(source)) {
            copy[key] = cloneSingularField(v);
          }
        } else if (member.kind == "oneof") {
          const f = member.findField(source.case);
          copy = f ? { case: source.case, value: cloneSingularField(source.value) } : { case: undefined };
        } else {
          copy = cloneSingularField(source);
        }
        any[member.localName] = copy;
      }
      for (const uf of type.runtime.bin.listUnknownFields(message3)) {
        type.runtime.bin.onUnknownField(any, uf.no, uf.wireType, uf.data);
      }
      return target;
    }
  };
}
function cloneSingularField(value) {
  if (value === undefined) {
    return value;
  }
  if (isMessage(value)) {
    return value.clone();
  }
  if (value instanceof Uint8Array) {
    const c = new Uint8Array(value.byteLength);
    c.set(value);
    return c;
  }
  return value;
}
function toU8Arr(input) {
  return input instanceof Uint8Array ? input : new Uint8Array(input);
}

// node_modules/@bufbuild/protobuf/dist/esm/private/proto-runtime.js
function makeProtoRuntime(syntax, newFieldList, initFields) {
  return {
    syntax,
    json: makeJsonFormat(),
    bin: makeBinaryFormat(),
    util: Object.assign(Object.assign({}, makeUtilCommon()), {
      newFieldList,
      initFields
    }),
    makeMessageType(typeName, fields, opt) {
      return makeMessageType(this, typeName, fields, opt);
    },
    makeEnum,
    makeEnumType,
    getEnumType,
    makeExtension(typeName, extendee, field) {
      return makeExtension(this, typeName, extendee, field);
    }
  };
}

// node_modules/@bufbuild/protobuf/dist/esm/private/field-list.js
class InternalFieldList {
  constructor(fields, normalizer) {
    this._fields = fields;
    this._normalizer = normalizer;
  }
  findJsonName(jsonName) {
    if (!this.jsonNames) {
      const t = {};
      for (const f of this.list()) {
        t[f.jsonName] = t[f.name] = f;
      }
      this.jsonNames = t;
    }
    return this.jsonNames[jsonName];
  }
  find(fieldNo) {
    if (!this.numbers) {
      const t = {};
      for (const f of this.list()) {
        t[f.no] = f;
      }
      this.numbers = t;
    }
    return this.numbers[fieldNo];
  }
  list() {
    if (!this.all) {
      this.all = this._normalizer(this._fields);
    }
    return this.all;
  }
  byNumber() {
    if (!this.numbersAsc) {
      this.numbersAsc = this.list().concat().sort((a, b) => a.no - b.no);
    }
    return this.numbersAsc;
  }
  byMember() {
    if (!this.members) {
      this.members = [];
      const a = this.members;
      let o;
      for (const f of this.list()) {
        if (f.oneof) {
          if (f.oneof !== o) {
            o = f.oneof;
            a.push(o);
          }
        } else {
          a.push(f);
        }
      }
    }
    return this.members;
  }
}

// node_modules/@bufbuild/protobuf/dist/esm/private/names.js
function localFieldName(protoName, inOneof) {
  const name = protoCamelCase(protoName);
  if (inOneof) {
    return name;
  }
  return safeObjectProperty(safeMessageProperty(name));
}
function localOneofName(protoName) {
  return localFieldName(protoName, false);
}
function protoCamelCase(snakeCase) {
  let capNext = false;
  const b = [];
  for (let i = 0;i < snakeCase.length; i++) {
    let c = snakeCase.charAt(i);
    switch (c) {
      case "_":
        capNext = true;
        break;
      case "0":
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
      case "7":
      case "8":
      case "9":
        b.push(c);
        capNext = false;
        break;
      default:
        if (capNext) {
          capNext = false;
          c = c.toUpperCase();
        }
        b.push(c);
        break;
    }
  }
  return b.join("");
}
var fieldJsonName = protoCamelCase;
var reservedIdentifiers = new Set([
  "break",
  "case",
  "catch",
  "class",
  "const",
  "continue",
  "debugger",
  "default",
  "delete",
  "do",
  "else",
  "export",
  "extends",
  "false",
  "finally",
  "for",
  "function",
  "if",
  "import",
  "in",
  "instanceof",
  "new",
  "null",
  "return",
  "super",
  "switch",
  "this",
  "throw",
  "true",
  "try",
  "typeof",
  "var",
  "void",
  "while",
  "with",
  "yield",
  "enum",
  "implements",
  "interface",
  "let",
  "package",
  "private",
  "protected",
  "public",
  "static",
  "Object",
  "bigint",
  "number",
  "boolean",
  "string",
  "object",
  "globalThis",
  "Uint8Array",
  "Partial"
]);
var reservedObjectProperties = new Set([
  "constructor",
  "toString",
  "toJSON",
  "valueOf"
]);
var reservedMessageProperties = new Set([
  "getType",
  "clone",
  "equals",
  "fromBinary",
  "fromJson",
  "fromJsonString",
  "toBinary",
  "toJson",
  "toJsonString",
  "toObject"
]);
var fallback = (name) => `${name}\$`;
var safeMessageProperty = (name) => {
  if (reservedMessageProperties.has(name)) {
    return fallback(name);
  }
  return name;
};
var safeObjectProperty = (name) => {
  if (reservedObjectProperties.has(name)) {
    return fallback(name);
  }
  return name;
};

// node_modules/@bufbuild/protobuf/dist/esm/private/field.js
class InternalOneofInfo {
  constructor(name) {
    this.kind = "oneof";
    this.repeated = false;
    this.packed = false;
    this.opt = false;
    this.req = false;
    this.default = undefined;
    this.fields = [];
    this.name = name;
    this.localName = localOneofName(name);
  }
  addField(field) {
    assert(field.oneof === this, `field ${field.name} not one of ${this.name}`);
    this.fields.push(field);
  }
  findField(localName) {
    if (!this._lookup) {
      this._lookup = Object.create(null);
      for (let i = 0;i < this.fields.length; i++) {
        this._lookup[this.fields[i].localName] = this.fields[i];
      }
    }
    return this._lookup[localName];
  }
}

// node_modules/@bufbuild/protobuf/dist/esm/private/field-normalize.js
function normalizeFieldInfos(fieldInfos, packedByDefault) {
  var _a, _b, _c, _d, _e, _f;
  const r = [];
  let o;
  for (const field2 of typeof fieldInfos == "function" ? fieldInfos() : fieldInfos) {
    const f = field2;
    f.localName = localFieldName(field2.name, field2.oneof !== undefined);
    f.jsonName = (_a = field2.jsonName) !== null && _a !== undefined ? _a : fieldJsonName(field2.name);
    f.repeated = (_b = field2.repeated) !== null && _b !== undefined ? _b : false;
    if (field2.kind == "scalar") {
      f.L = (_c = field2.L) !== null && _c !== undefined ? _c : LongType.BIGINT;
    }
    f.delimited = (_d = field2.delimited) !== null && _d !== undefined ? _d : false;
    f.req = (_e = field2.req) !== null && _e !== undefined ? _e : false;
    f.opt = (_f = field2.opt) !== null && _f !== undefined ? _f : false;
    if (field2.packed === undefined) {
      if (packedByDefault) {
        f.packed = field2.kind == "enum" || field2.kind == "scalar" && field2.T != ScalarType.BYTES && field2.T != ScalarType.STRING;
      } else {
        f.packed = false;
      }
    }
    if (field2.oneof !== undefined) {
      const ooname = typeof field2.oneof == "string" ? field2.oneof : field2.oneof.name;
      if (!o || o.name != ooname) {
        o = new InternalOneofInfo(ooname);
      }
      f.oneof = o;
      o.addField(f);
    }
    r.push(f);
  }
  return r;
}

// node_modules/@bufbuild/protobuf/dist/esm/proto3.js
var proto3 = makeProtoRuntime("proto3", (fields) => {
  return new InternalFieldList(fields, (source) => normalizeFieldInfos(source, true));
}, (target) => {
  for (const member of target.getType().fields.byMember()) {
    if (member.opt) {
      continue;
    }
    const name = member.localName, t = target;
    if (member.repeated) {
      t[name] = [];
      continue;
    }
    switch (member.kind) {
      case "oneof":
        t[name] = { case: undefined };
        break;
      case "enum":
        t[name] = 0;
        break;
      case "map":
        t[name] = {};
        break;
      case "scalar":
        t[name] = scalarZeroValue(member.T, member.L);
        break;
      case "message":
        break;
    }
  }
});
// node_modules/@bufbuild/protobuf/dist/esm/google/protobuf/timestamp_pb.js
class Timestamp extends Message {
  constructor(data) {
    super();
    this.seconds = protoInt64.zero;
    this.nanos = 0;
    proto3.util.initPartial(data, this);
  }
  fromJson(json, options) {
    if (typeof json !== "string") {
      throw new Error(`cannot decode google.protobuf.Timestamp from JSON: ${proto3.json.debug(json)}`);
    }
    const matches = json.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})(?:Z|\.([0-9]{3,9})Z|([+-][0-9][0-9]:[0-9][0-9]))$/);
    if (!matches) {
      throw new Error(`cannot decode google.protobuf.Timestamp from JSON: invalid RFC 3339 string`);
    }
    const ms = Date.parse(matches[1] + "-" + matches[2] + "-" + matches[3] + "T" + matches[4] + ":" + matches[5] + ":" + matches[6] + (matches[8] ? matches[8] : "Z"));
    if (Number.isNaN(ms)) {
      throw new Error(`cannot decode google.protobuf.Timestamp from JSON: invalid RFC 3339 string`);
    }
    if (ms < Date.parse("0001-01-01T00:00:00Z") || ms > Date.parse("9999-12-31T23:59:59Z")) {
      throw new Error(`cannot decode message google.protobuf.Timestamp from JSON: must be from 0001-01-01T00:00:00Z to 9999-12-31T23:59:59Z inclusive`);
    }
    this.seconds = protoInt64.parse(ms / 1000);
    this.nanos = 0;
    if (matches[7]) {
      this.nanos = parseInt("1" + matches[7] + "0".repeat(9 - matches[7].length)) - 1e9;
    }
    return this;
  }
  toJson(options) {
    const ms = Number(this.seconds) * 1000;
    if (ms < Date.parse("0001-01-01T00:00:00Z") || ms > Date.parse("9999-12-31T23:59:59Z")) {
      throw new Error(`cannot encode google.protobuf.Timestamp to JSON: must be from 0001-01-01T00:00:00Z to 9999-12-31T23:59:59Z inclusive`);
    }
    if (this.nanos < 0) {
      throw new Error(`cannot encode google.protobuf.Timestamp to JSON: nanos must not be negative`);
    }
    let z = "Z";
    if (this.nanos > 0) {
      const nanosStr = (this.nanos + 1e9).toString().substring(1);
      if (nanosStr.substring(3) === "000000") {
        z = "." + nanosStr.substring(0, 3) + "Z";
      } else if (nanosStr.substring(6) === "000") {
        z = "." + nanosStr.substring(0, 6) + "Z";
      } else {
        z = "." + nanosStr + "Z";
      }
    }
    return new Date(ms).toISOString().replace(".000Z", z);
  }
  toDate() {
    return new Date(Number(this.seconds) * 1000 + Math.ceil(this.nanos / 1e6));
  }
  static now() {
    return Timestamp.fromDate(new Date);
  }
  static fromDate(date) {
    const ms = date.getTime();
    return new Timestamp({
      seconds: protoInt64.parse(Math.floor(ms / 1000)),
      nanos: ms % 1000 * 1e6
    });
  }
  static fromBinary(bytes, options) {
    return new Timestamp().fromBinary(bytes, options);
  }
  static fromJson(jsonValue, options) {
    return new Timestamp().fromJson(jsonValue, options);
  }
  static fromJsonString(jsonString, options) {
    return new Timestamp().fromJsonString(jsonString, options);
  }
  static equals(a, b) {
    return proto3.util.equals(Timestamp, a, b);
  }
}
Timestamp.runtime = proto3;
Timestamp.typeName = "google.protobuf.Timestamp";
Timestamp.fields = proto3.util.newFieldList(() => [
  { no: 1, name: "seconds", kind: "scalar", T: 3 },
  { no: 2, name: "nanos", kind: "scalar", T: 5 }
]);
// studio/src/protocol/model/dto_organization_job_position_pb.js
var OrganizationJobPosition = proto3.makeEnum("com.diagnocat.model.OrganizationJobPosition", [
  { no: 0, name: "OrganizationJobPositionInvalidValue" },
  { no: 1, name: "OrganizationJobPositionChiefDentalOfficer" },
  { no: 2, name: "OrganizationJobPositionTreatmentCoordinator" },
  { no: 3, name: "OrganizationJobPositionDentalAssistant" },
  { no: 4, name: "OrganizationJobPositionDentalTechnician" },
  { no: 5, name: "OrganizationJobPositionGeneralDentist" },
  { no: 6, name: "OrganizationJobPositionPeriodontist" },
  { no: 7, name: "OrganizationJobPositionProsthodontist" },
  { no: 8, name: "OrganizationJobPositionOrthodontist" },
  { no: 9, name: "OrganizationJobPositionPediatricDentist" },
  { no: 10, name: "OrganizationJobPositionOralAndMaxillofacialSurgeon" },
  { no: 11, name: "OrganizationJobPositionOralAndMaxillofacialRadiologist" },
  { no: 12, name: "OrganizationJobPositionAestheticDentist" },
  { no: 13, name: "OrganizationJobPositionDentalHygienist" },
  { no: 14, name: "OrganizationJobPositionOther" },
  { no: 15, name: "OrganizationJobPositionOfficeManager" },
  { no: 16, name: "OrganizationJobPositionEndodontist" }
]);

// studio/src/protocol/model/dto_organization_permissions_pb.js
var OrganizationPermissions = proto3.makeMessageType("com.diagnocat.model.OrganizationPermissions", () => [
  { no: 1, name: "CanView", kind: "scalar", T: 8 },
  { no: 2, name: "CanEditInfo", kind: "scalar", T: 8 },
  { no: 3, name: "CanViewSettings", kind: "scalar", T: 8 },
  { no: 4, name: "CanEditSettings", kind: "scalar", T: 8 },
  { no: 5, name: "CanEditBranches", kind: "scalar", T: 8 },
  { no: 6, name: "CanViewStaff", kind: "scalar", T: 8 },
  { no: 7, name: "CanEditStaff", kind: "scalar", T: 8 },
  { no: 11, name: "CanCreateTheirOwnPatients", kind: "scalar", T: 8 },
  { no: 12, name: "CanCreateOtherDoctorsPatients", kind: "scalar", T: 8 },
  { no: 13, name: "CanViewMyPatients", kind: "scalar", T: 8 },
  { no: 14, name: "CanViewAllPatients", kind: "scalar", T: 8 },
  { no: 15, name: "CanManageComment", kind: "scalar", T: 8 },
  { no: 21, name: "CanEditBillingInfo", kind: "scalar", T: 8 },
  { no: 22, name: "CanViewStatistics", kind: "scalar", T: 8 }
]);

// studio/src/protocol/model/dto_base_pb.js
var CalledVia = proto3.makeEnum("com.diagnocat.model.CalledVia", [
  { no: 0, name: "CalledViaUnspecified" },
  { no: 1, name: "CalledViaClient" },
  { no: 2, name: "CalledViaPartnerAPI" },
  { no: 3, name: "CalledViaMLPipelines" }
]);
var Gender = proto3.makeEnum("com.diagnocat.model.Gender", [
  { no: 0, name: "GenderUnspecified" },
  { no: 1, name: "GenderMale" },
  { no: 2, name: "GenderFemale" },
  { no: 3, name: "GenderOther" }
]);
var OptionalBoolean = proto3.makeEnum("com.diagnocat.model.OptionalBoolean", [
  { no: 0, name: "OptionalBooleanUnspecified" },
  { no: 1, name: "OptionalBooleanFalse" },
  { no: 2, name: "OptionalBooleanTrue" }
]);
var Revision = proto3.makeMessageType("com.diagnocat.model.Revision", () => [
  { no: 1, name: "Number", kind: "scalar", T: 13 },
  { no: 2, name: "UpdatedAt", kind: "message", T: Timestamp },
  { no: 3, name: "UserID", kind: "scalar", T: 9 }
]);
var Created = proto3.makeMessageType("com.diagnocat.model.Created", () => [
  { no: 1, name: "By", kind: "scalar", T: 9 },
  { no: 2, name: "At", kind: "message", T: Timestamp },
  { no: 3, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
]);
var Deleted = proto3.makeMessageType("com.diagnocat.model.Deleted", () => [
  { no: 1, name: "Deleted", kind: "scalar", T: 8 },
  { no: 2, name: "By", kind: "scalar", T: 9 },
  { no: 3, name: "At", kind: "message", T: Timestamp },
  { no: 4, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
]);
var Attribute = proto3.makeMessageType("com.diagnocat.model.Attribute", () => [
  { no: 1, name: "Key", kind: "scalar", T: 9 },
  { no: 2, name: "Value", kind: "scalar", T: 9 }
]);
var PersonalData = proto3.makeMessageType("com.diagnocat.model.PersonalData", () => [
  { no: 1, name: "FirstName", kind: "scalar", T: 9 },
  { no: 2, name: "LastName", kind: "scalar", T: 9 },
  { no: 3, name: "DateOfBirth", kind: "scalar", T: 9 },
  { no: 4, name: "Emails", kind: "scalar", T: 9, repeated: true },
  { no: 5, name: "Phones", kind: "scalar", T: 9, repeated: true }
]);
var RevisionNew = proto3.makeMessageType("com.diagnocat.model.RevisionNew", () => [
  { no: 1, name: "Number", kind: "scalar", T: 13 },
  { no: 2, name: "By", kind: "scalar", T: 9 },
  { no: 3, name: "At", kind: "message", T: Timestamp },
  { no: 4, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
]);
var DeletedNew = proto3.makeMessageType("com.diagnocat.model.DeletedNew", () => [
  { no: 1, name: "IsDeleted", kind: "scalar", T: 8 },
  { no: 2, name: "By", kind: "scalar", T: 9 },
  { no: 3, name: "At", kind: "message", T: Timestamp },
  { no: 4, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
]);

// studio/src/protocol/model/dto_organization_pb.js
var DentalNotationFormat = proto3.makeEnum("com.diagnocat.model.DentalNotationFormat", [
  { no: 0, name: "DentalNotationFormatISO" },
  { no: 1, name: "DentalNotationFormatUniversal" }
]);
var OrganizationType = proto3.makeEnum("com.diagnocat.model.OrganizationType", [
  { no: 0, name: "OrganizationTypeInvalidValue" },
  { no: 1, name: "OrganizationTypeClinic" },
  { no: 2, name: "OrganizationTypeDiagnosticCenter" },
  { no: 3, name: "OrganizationTypeSoloPractitioner" }
]);
var OrganizationUserRoleType = proto3.makeEnum("com.diagnocat.model.OrganizationUserRoleType", [
  { no: 0, name: "OrganizationUserRoleTypeInvalidValue" },
  { no: 1, name: "OrganizationUserRoleTypeOwner" },
  { no: 2, name: "OrganizationUserRoleTypeClinicalLeader" },
  { no: 3, name: "OrganizationUserRoleTypeTreatingDoctor" },
  { no: 4, name: "OrganizationUserRoleTypeNonClinicalMember" }
]);
var FeatureFlag = proto3.makeEnum("com.diagnocat.model.FeatureFlag", [
  { no: 0, name: "FeatureFlag_InvalidValue" },
  { no: 2, name: "FeatureFlag_Show_BillingShop" },
  { no: 3, name: "FeatureFlag_Show_NonDentalFindings" },
  { no: 7, name: "FeatureFlag_Show_SegmentronConditionsButtons" },
  { no: 8, name: "FeatureFlag_Show_NotForCommercialUseBanner" },
  { no: 9, name: "FeatureFlag_Show_IOSReportButton" },
  { no: 10, name: "FeatureFlag_Show_OrthoStudioAddSecondaryCBCT" },
  { no: 11, name: "FeatureFlag_Show_DentalPhotosWidget" },
  { no: 51, name: "FeatureFlag_Enable_SegmentronSerialisation" },
  { no: 61, name: "FeatureFlag_FDA_SubmissionView" },
  { no: 62, name: "FeatureFlag_FDA_NonAidedVersion" },
  { no: 101, name: "FeatureFlag_Hide_Probabilities" },
  { no: 102, name: "FeatureFlag_Hide_ConditionsMasks" },
  { no: 103, name: "FeatureFlag_Hide_PerioMasksAndSlices" },
  { no: 104, name: "FeatureFlag_Hide_CariesSigns" },
  { no: 105, name: "FeatureFlag_Hide_PBLRulesAndMeasurements" },
  { no: 106, name: "FeatureFlag_Hide_AnatomyMasks" },
  { no: 107, name: "FeatureFlag_Hide_AllCBCTMasks" }
]);
var FeatureFlagType = proto3.makeEnum("com.diagnocat.model.FeatureFlagType", [
  { no: 0, name: "FeatureFlagType_InvalidValue" },
  { no: 1, name: "FeatureFlagType_Development" },
  { no: 2, name: "FeatureFlagType_Regulatory" }
]);
var Organization = proto3.makeMessageType("com.diagnocat.model.Organization", () => [
  { no: 1, name: "ID", kind: "scalar", T: 9 },
  { no: 2, name: "Name", kind: "scalar", T: 9 },
  { no: 3, name: "Type", kind: "enum", T: proto3.getEnumType(OrganizationType) },
  { no: 4, name: "ParentID", kind: "scalar", T: 9 },
  { no: 5, name: "LogoAssetID", kind: "scalar", T: 9 },
  { no: 6, name: "StampAssetID", kind: "scalar", T: 9 },
  { no: 7, name: "Contacts", kind: "message", T: OrganizationContacts },
  { no: 8, name: "Settings", kind: "message", T: OrganizationSettings },
  { no: 9, name: "AvailableJobPositions", kind: "enum", T: proto3.getEnumType(OrganizationJobPosition), repeated: true },
  { no: 10, name: "UserRoles", kind: "message", T: OrganizationUserRole, repeated: true },
  { no: 11, name: "FeatureFlags", kind: "message", T: OrganizationFeatureFlag, repeated: true },
  { no: 12, name: "Comment", kind: "scalar", T: 9 },
  { no: 10020, name: "YourPermissions", kind: "message", T: OrganizationPermissions },
  { no: 10030, name: "Created", kind: "message", T: Created },
  { no: 10040, name: "Revision", kind: "message", T: Revision },
  { no: 10050, name: "Deleted", kind: "message", T: Deleted }
]);
var OrganizationSettings = proto3.makeMessageType("com.diagnocat.model.OrganizationSettings", () => [
  { no: 1, name: "Hack_EditUsersContacts", kind: "scalar", T: 8 },
  { no: 2, name: "OnlyTeamMembersCanViewPatient", kind: "scalar", T: 8 },
  { no: 3, name: "OnlyTeamMembersCanViewPersonalData", kind: "scalar", T: 8 },
  { no: 4, name: "OnlyTeamMembersCanRequestReport", kind: "scalar", T: 8 },
  { no: 5, name: "OnlyTeamMembersCanEditPatient", kind: "scalar", T: 8 },
  { no: 6, name: "OnlyTeamMembersCanEditReport", kind: "scalar", T: 8 },
  { no: 11, name: "DentalNotationFormat", kind: "enum", T: proto3.getEnumType(DentalNotationFormat) }
]);
var OrganizationContacts = proto3.makeMessageType("com.diagnocat.model.OrganizationContacts", () => [
  { no: 1, name: "Country", kind: "scalar", T: 9 },
  { no: 2, name: "Region", kind: "scalar", T: 9 },
  { no: 3, name: "City", kind: "scalar", T: 9 },
  { no: 4, name: "Address", kind: "scalar", T: 9 },
  { no: 5, name: "Phones", kind: "scalar", T: 9, repeated: true },
  { no: 6, name: "Emails", kind: "scalar", T: 9, repeated: true },
  { no: 7, name: "WebSite", kind: "scalar", T: 9 },
  { no: 8, name: "ZipCode", kind: "scalar", T: 9 }
]);
var OrganizationUserRole = proto3.makeMessageType("com.diagnocat.model.OrganizationUserRole", () => [
  { no: 1, name: "UserID", kind: "scalar", T: 9 },
  { no: 2, name: "PersonalData", kind: "message", T: OrganizationDefinedPersonalData },
  { no: 3, name: "Roles", kind: "enum", T: proto3.getEnumType(OrganizationUserRoleType), repeated: true },
  { no: 4, name: "JobPositions", kind: "enum", T: proto3.getEnumType(OrganizationJobPosition), repeated: true },
  { no: 5, name: "StampAssetID", kind: "scalar", T: 9 },
  { no: 6, name: "CreatedAt", kind: "message", T: Timestamp },
  { no: 7, name: "AvatarAssetID", kind: "scalar", T: 9 },
  { no: 21, name: "Attributes", kind: "message", T: Attribute, repeated: true }
]);
var OrganizationDefinedPersonalData = proto3.makeMessageType("com.diagnocat.model.OrganizationDefinedPersonalData", () => [
  { no: 1, name: "FirstName", kind: "scalar", T: 9 },
  { no: 2, name: "LastName", kind: "scalar", T: 9 },
  { no: 3, name: "Email", kind: "scalar", T: 9 }
]);
var OrganizationFeatureFlag = proto3.makeMessageType("com.diagnocat.model.OrganizationFeatureFlag", () => [
  { no: 1, name: "Flag", kind: "enum", T: proto3.getEnumType(FeatureFlag) },
  { no: 2, name: "Value", kind: "scalar", T: 8 },
  { no: 3, name: "ReadOnly", kind: "scalar", T: 8 },
  { no: 4, name: "Type", kind: "enum", T: proto3.getEnumType(FeatureFlagType) }
]);

// studio/src/utils/Environment.ts
var API_URI_BASE = process.env.API_URI_BASE?.replaceAll('"', "") || window.location.origin || "API_URI_BASE_IS_UNSET";
var URI_HOST = process.env.URI_HOST?.replaceAll('"', "") || "URI_HOST_IS_UNSET";
var API_LOGIN = process.env.API_LOGIN?.replaceAll('"', "") || "API_LOGIN_IS_UNSET";
var IS_DEVELOPMENT = true;
var IS_FEATURE_BRANCH = process.env.IS_FEATURE_BRANCH === "true";
var IS_LOCALHOST = location.hostname === "localhost";
var IS_LOCALHOST_OR_FEATURE_BRANCH = IS_LOCALHOST || IS_FEATURE_BRANCH;
var IS_WHITE_CLIENT = Boolean(process.env.IS_WHITE_CLIENT);
var IS_BLACK_CLIENT = Boolean(process.env.IS_BLACK_CLIENT);
var IS_RU_PROD = process.env.IS_RU_PROD === "true";
var IS_US_PROD = process.env.IS_US_PROD === "true";
var IS_HIDDEN_FOR_CLINICAL_TRIALS_ON_EU = process.env.IS_EU_PROD === "true";
var IS_BLACK_DEV = API_URI_BASE.includes("dev.diagnocat.com");
var DOM_ENV = process.env.DOM;
var DEBUG_PRINT_ENV = localStorage.getItem("DEBUG_PRINT_ENV") === "true";
window.printEnv = () => {
  console.log("process env API_URI_BASE=", process.env.API_URI_BASE);
  console.log("process env API_LOGIN=", process.env.API_LOGIN);
  console.log("process env TRANSPORT=", process.env.TRANSPORT);
  console.log("process env NODE_ENV=", "development");
  console.log("process env IS_FEATURE_BRANCH=", process.env.IS_FEATURE_BRANCH);
  console.log("process.env.DOM=", process.env.DOM);
  console.log("window.location.hostname=", window.location.hostname);
  console.log("API_URI_BASE=", API_URI_BASE);
  console.log("URI_HOST=", URI_HOST);
  console.log("API_LOGIN=", API_LOGIN);
  console.log("IS_DEVELOPMENT=", IS_DEVELOPMENT);
  console.log("IS_FEATURE_BRANCH=", IS_FEATURE_BRANCH);
  console.log("IS_LOCALHOST=", IS_LOCALHOST);
  console.log("IS_LOCALHOST_OR_FEATURE_BRANCH=", IS_LOCALHOST_OR_FEATURE_BRANCH);
  console.log("IS_RU_PROD=", IS_RU_PROD);
  console.log("IS_US_PROD=", IS_US_PROD);
  console.log("IS_HIDDEN_FOR_CLINICAL_TRIALS_ON_EU=", IS_HIDDEN_FOR_CLINICAL_TRIALS_ON_EU);
  console.log("IS_WHITE_CLIENT=", IS_WHITE_CLIENT);
  console.log("IS_BLACK_CLIENT=", IS_BLACK_CLIENT);
  console.log("IS_BLACK_DEV=", IS_BLACK_DEV);
  console.log("DOM_ENV=", DOM_ENV);
};
if (DEBUG_PRINT_ENV) {
  window.printEnv();
}

// studio/src/utils/featureFlags.ts
var featureFlags = {
  _darken3Dhsl: { h: 1, s: 0.35, l: 0.35 },
  _unselectedHsl: { h: 1, s: 0.7, l: 0.3 }
};
window._featureFlags = () => Object.keys(featureFlags);
window._setFeatureFlag = (name, value) => {
  localStorage.setItem(name, value);
};
window._setDarken3Dhsl = (value) => {
  featureFlags._darken3Dhsl.h = value.h;
  featureFlags._darken3Dhsl.s = value.s;
  featureFlags._darken3Dhsl.l = value.l;
};
window._setUnselectedHsl = (value) => {
  featureFlags._unselectedHsl.h = value.h;
  featureFlags._unselectedHsl.s = value.s;
  featureFlags._unselectedHsl.l = value.l;
};
var isOldFFEnableConditionsExists = Object.prototype.hasOwnProperty.call(localStorage, "FF_ENABLE_CONDITIONS");
if (isOldFFEnableConditionsExists) {
  localStorage.removeItem("FF_ENABLE_CONDITIONS");
}
var isFFEnableConditionsExists = Object.prototype.hasOwnProperty.call(localStorage, "FF_ENABLE_CONDITIONS");
if ((IS_RU_PROD || IS_US_PROD || IS_BLACK_DEV) && !isFFEnableConditionsExists) {
  localStorage.setItem("FF_ENABLE_CONDITIONS", "true");
}
var isNewFFEnableConditionsExists = Object.prototype.hasOwnProperty.call(localStorage, "FF_ENABLE_CONDITIONS_NEW");
if ((IS_US_PROD || IS_BLACK_DEV) && !isNewFFEnableConditionsExists) {
  localStorage.setItem("FF_ENABLE_CONDITIONS_NEW", "true");
}
var FF_IMPLANT_WARNINGS = localStorage.getItem("FF_IMPLANT_WARNINGS") === "true";
var FF_USE_SERIALIZATION = localStorage.getItem("FF_USE_SERIALIZATION") !== "false";
var FF_ENABLE_CONDITIONS_NEW = localStorage.getItem("FF_ENABLE_CONDITIONS_NEW") === "true";
var FF_ENABLE_CONDITIONS = localStorage.getItem("FF_ENABLE_CONDITIONS") === "true";
var FF_ENABLE_DEV_TOOLS = localStorage.getItem("FF_ENABLE_DEV_TOOLS") === "true";
var FF_ENABLE_SURGICAL_REPORT_PDF = localStorage.getItem("FF_ENABLE_SURGICAL_REPORT_PDF") === "true";
var FF_ENTITY_EDITOR_ENABLED = localStorage.getItem("FF_ENTITY_EDITOR_ENABLED") === "true";
var DEBUG_CAMERA_BODY_HELPERS = localStorage.getItem("DEBUG_CAMERA_BODY_HELPERS") === "true";
var DEBUG_SANITY_CHECK_LOGS = localStorage.getItem("DEBUG_SANITY_CHECK_LOGS") === "true";
var DEBUG_PATIENT_SPACE_HELPER = localStorage.getItem("DEBUG_PATIENT_SPACE_HELPER") === "true";
var DEBUG_ORIENTATION_MATH = localStorage.getItem("DEBUG_ORIENTATION_MATH") === "true";
var FF_TAKE_PERFORMANCE_MEASURES = IS_DEVELOPMENT ? localStorage.getItem("FF_TAKE_PERFORMANCE_MEASURES") !== "false" : localStorage.getItem("FF_TAKE_PERFORMANCE_MEASURES") === "true";
var BACKEND_FEATURE_FLAGS = {
  EnableSegmentronSerialisation: FeatureFlag[FeatureFlag.FeatureFlag_Enable_SegmentronSerialisation]
};

// studio/src/utils/PerformanceProfiler/PerformanceProfiler.ts
var OPEN_REPORT_DATASTREAM_MARK = "openReportDataStream";
var RUN_STARTUPS_MARK = "runStartups";
var APP_READY_MARK = "appReady";

class PerformanceProfiler {
  constructor() {
    performance.mark(APP_READY_MARK + ":start");
  }
  connectToApp() {
    if (!FF_TAKE_PERFORMANCE_MEASURES) {
      return;
    }
    App2.Instance().addEventListenerOnce("applicationReady", () => {
      this.takeMeasures();
    });
  }
  static prettyTime(measure) {
    return (typeof measure === "number" ? measure : measure.duration).toFixed(2).padStart(10) + "ms";
  }
  static tryMeasure(name) {
    try {
      return performance.measure(name, name + ":start", name + ":end");
    } catch (e) {
      console.error('[PerformanceProfiler] failed to measure:', name, e);
      return null;
    }
  }
  takeMeasures() {
    performance.mark(APP_READY_MARK + ":end");
    const streamIOMeasure = PerformanceProfiler.tryMeasure(OPEN_REPORT_DATASTREAM_MARK);
    if (streamIOMeasure) {
      console.log(`[PerformanceProfiler] ${PerformanceProfiler.prettyTime(streamIOMeasure)} to read data stream`);
    }
    const resources = performance.getEntriesByType("resource");
    const minstart = resources.reduce((min, resource) => Math.min(min, resource.startTime), Infinity);
    const maxend = resources.reduce((max, resource) => Math.max(max, resource.responseEnd), 0);
    console.log(`[PerformanceProfiler] ${PerformanceProfiler.prettyTime(maxend - minstart)} to load assets from server`);
    const appReadyMeasure = PerformanceProfiler.tryMeasure(APP_READY_MARK);
    if (appReadyMeasure) {
      console.log(`[PerformanceProfiler] ${PerformanceProfiler.prettyTime(appReadyMeasure)} to make the app ready`);
    }
  }
}
var PerformanceProfiler_default = new PerformanceProfiler;

// studio/src/core/App.ts
class App2 extends EventDispatcher {
  view;
  views;
  initialized = false;
  get isInitialized() {
    return this.initialized;
  }
  mouseController;
  keyboardController;
  raycastController;
  cursorController;
  undoStack;
  activeView;
  resources;
  resizeObserver;
  startupSystems;
  static instance;
  static Instance() {
    return this.instance ?? (this.instance = new App2);
  }
  constructor() {
    super();
    this.view = new View;
    this.activeView = this.view;
    this.views = {};
    this.mouseController = new PointerController;
    this.keyboardController = new KeyboardController;
    this.raycastController = new RaycastController;
    this.cursorController = new CursorController;
    this.undoStack = new UndoStack;
    this.handleResize = this.handleResize.bind(this);
    this.resources = new Map;
    this.startupSystems = [];
  }
  init(props) {
    this.mouseController.init();
    this.keyboardController.init();
    this.raycastController.init();
    this.cursorController.init();
    this.view.init(props);
    window.addEventListener("resize", this.handleResize, false);
    if (props?.container) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(props.container);
    }
    this.initialized = true;
  }
  dispose() {
    if (!this.initialized)
      return;
    this.view.dispose();
    for (const key in this.views) {
      this.views[key].dispose();
    }
    this.cursorController.dispose();
    this.raycastController.dispose();
    this.keyboardController.dispose();
    this.mouseController.dispose();
    this.undoStack.clearListeners();
    this.startupSystems = [];
    this.clearListeners();
    window.removeEventListener("resize", this.handleResize, false);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    this.initialized = false;
  }
  clear() {
    this.dispose();
    this.view.clear();
    for (const key in this.views) {
      this.views[key].clear();
    }
    this.deleteListeners();
    this.undoStack.clear();
  }
  async runStartups() {
    performance.mark(RUN_STARTUPS_MARK + ":start");
    for (let i = 0, il = this.startupSystems.length;i < il; i++) {
      const system = this.startupSystems[i];
      if (system.prototype?.constructor) {
        await new system().start();
      } else {
        await system();
      }
      this.dispatchEvent({ type: "applicationProgress", progress: (i + 1) / il });
    }
    performance.mark(RUN_STARTUPS_MARK + ":end");
  }
  executeCommand(command, immediately = true) {
    if (immediately) {
      if (command.execute()) {
        this.undoStack.add(command);
      }
    } else {
      this.undoStack.add(command);
    }
  }
  getActiveView() {
    return this.activeView;
  }
  setActiveView(view) {
    if (this.activeView === view) {
      return;
    }
    this.activeView.setActiveViewport(undefined);
    this.activeView = view;
  }
  addResources(name, resource) {
    this.resources.set(name, resource);
    this.dispatchEvent({ type: "resourceAdded", name });
  }
  getResources(name) {
    return this.resources.get(name);
  }
  handleResize() {
    this.dispatchEvent({ type: "resize" });
  }
}

// studio/src/core/Mode.ts
class Mode {
  toolConstructors;
  hostObject;
  options;
  name;
  enable;
  constructor(options) {
    if (Array.isArray(options)) {
      this.options = { tools: { toolList: options, include: true } };
    } else {
      this.options = options;
    }
    this.toolConstructors = new Set;
    this.init = this.init.bind(this);
    this.name = "";
    this.enable = false;
  }
  setHostObject(hostObject, modeName) {
    this.hostObject = hostObject;
    this.name = modeName;
    const rootTools = this.hostObject.toolController.getTools();
    if (!rootTools) {
      console.error("Tools are not defined!");
      return;
    }
    if (!this.options) {
      Array.from(rootTools.keys()).forEach((item) => this.toolConstructors.add(item));
    } else {
      if (this.options.tools) {
        if (this.options.tools.include) {
          this.options.tools.toolList.forEach((item) => this.toolConstructors.add(item));
        } else {
          Array.from(rootTools.keys()).forEach((item) => this.toolConstructors.add(item));
          this.options.tools.toolList.forEach((toolName) => this.toolConstructors.delete(toolName));
        }
      } else {
        Array.from(rootTools.keys()).forEach((item) => this.toolConstructors.add(item));
      }
    }
  }
  init() {
    if (!this.hostObject) {
      console.error("Mode hostObject is not defined before init");
      return;
    }
    const prevMode = this.hostObject.toolController.getMode();
    if (prevMode && prevMode.enable) {
      prevMode.toolConstructors.forEach((toolName) => {
        if (!this.toolConstructors.has(toolName)) {
          this.hostObject.toolController.getTool(toolName)?.dispose();
        }
      });
      this.toolConstructors.forEach((toolName) => {
        if (!prevMode.toolConstructors.has(toolName)) {
          this.hostObject.toolController.getTool(toolName)?.init();
        }
      });
    } else {
      this.toolConstructors.forEach((toolName) => {
        this.hostObject.toolController.getTool(toolName)?.init();
      });
    }
    this.enable = true;
  }
  dispose() {
    if (!this.hostObject) {
      console.error("Mode hostObject is not defined while disposing");
      return;
    }
    this.toolConstructors.forEach((toolName) => {
      this.hostObject.toolController.getTool(toolName)?.dispose();
    });
    this.enable = false;
  }
  getName() {
    return this.name;
  }
  getToolNames() {
    return this.toolConstructors;
  }
}

// studio/src/core/Tool.ts
class Tool {
  enable;
  hostObject;
  history;
  removeEventsCallbacks = [];
  constructor(viewport) {
    this.hostObject = viewport;
    this.enable = false;
    this.onConstructor();
  }
  onConstructor() {
  }
  unsubscribeAll() {
    this.removeEventsCallbacks.forEach((cb) => cb());
    this.removeEventsCallbacks.splice(0);
  }
  init() {
    if (!this.enable && this.hostObject) {
      this.activate();
      this.enable = true;
    } else {
      console.error("tool is not initialized", this.enable, typeof this.hostObject === "undefined");
    }
  }
  dispose() {
    if (this.enable) {
      this.deactivate();
      this.enable = false;
    }
  }
  getHostObject() {
    return this.hostObject;
  }
  on(type, callback, priority) {
    const removeCallback = App2.Instance().addEventListener(type, callback, priority);
    this.removeEventsCallbacks?.push(removeCallback);
    return removeCallback;
  }
  off(type, callback, force) {
    return App2.Instance().removeEventListener(type, callback, force);
  }
  deactivate() {
    this.unsubscribeAll();
  }
}

class ViewportTool extends Tool {
  constructor(viewport) {
    super(viewport);
  }
  getHostObject() {
    return this.hostObject;
  }
}

class ViewTool extends Tool {
  constructor(view) {
    super(view);
  }
  getHostObject() {
    return this.hostObject;
  }
}

// studio/src/core/tools/Render.ts
class Render extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.render = this.render.bind(this);
  }
  activate() {
    App2.Instance().addEventListener("add", this.render);
    App2.Instance().addEventListener("delete", this.render);
    App2.Instance().addEventListener("resize", this.render);
    App2.Instance().addEventListener("transformCamera", this.render);
    App2.Instance().addEventListener("select", this.render);
    App2.Instance().addEventListener("deselect", this.render);
    App2.Instance().addEventListener("transform", this.render);
    App2.Instance().addEventListener("viewportSchemeChanged", this.render);
    App2.Instance().addEventListener("render", this.render);
  }
  deactivate() {
    App2.Instance().removeEventListener("add", this.render);
    App2.Instance().removeEventListener("delete", this.render);
    App2.Instance().removeEventListener("resize", this.render);
    App2.Instance().removeEventListener("transformCamera", this.render);
    App2.Instance().removeEventListener("select", this.render);
    App2.Instance().removeEventListener("deselect", this.render);
    App2.Instance().removeEventListener("transform", this.render);
    App2.Instance().removeEventListener("viewportSchemeChanged", this.render);
    App2.Instance().removeEventListener("render", this.render);
  }
  render() {
    this.hostObject.render();
  }
}

// studio/src/core/tools/CameraToggle.ts
class CameraToggle extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.setPerspectiveCamera = this.setPerspectiveCamera.bind(this);
    this.setOrthographicCamera = this.setOrthographicCamera.bind(this);
  }
  activate() {
    App2.Instance().keyboardController.addListener("keydown", this.setPerspectiveCamera, "KeyP");
    App2.Instance().keyboardController.addListener("keydown", this.setOrthographicCamera, "KeyO");
  }
  deactivate() {
    App2.Instance().keyboardController.removeListener("keydown", this.setPerspectiveCamera, "KeyP");
    App2.Instance().keyboardController.removeListener("keydown", this.setOrthographicCamera, "KeyO");
  }
  setPerspectiveCamera() {
    this.hostObject.cameraController.copyFromActive();
    this.hostObject.cameraController.perspective();
    App2.Instance().dispatchEvent({ type: "render" });
  }
  setOrthographicCamera() {
    this.hostObject.cameraController.copyFromActive();
    this.hostObject.cameraController.orthographic();
    App2.Instance().dispatchEvent({ type: "render" });
  }
}

// studio/src/core/tools/OrbitControls.ts
import {PerspectiveCamera as PerspectiveCamera2, Quaternion as Quaternion2, Spherical, Vector2 as Vector22, Vector3 as Vector310, TOUCH, MOUSE, Color as Color3, Plane, Euler} from "three";

// studio/src/utils/debug/debugDrawPoints.ts
import {Points, PointsMaterial, Material, Float32BufferAttribute} from "three";
var debugDrawPoints = (debugObj, points, colors, { size = 5, renderOrder = 200 } = {}) => {
  let object = debugObj?.debugPoints;
  if (!object) {
    object = new Points(undefined, new PointsMaterial({ color: 16777215, vertexColors: true, size }));
    if (object.material instanceof Material)
      object.material.depthTest = false;
    if (object.material instanceof Material)
      object.material.transparent = true;
    object.renderOrder = renderOrder;
    object.frustumCulled = false;
  }
  if (debugObj && !debugObj.debugPoints) {
    debugObj.debugPoints = object;
    debugObj.scene?.add(object);
  }
  const colorsArray = [];
  colors.forEach((color) => colorsArray.push(color.r, color.g, color.b));
  object.geometry.setFromPoints(points);
  object.geometry.setAttribute("color", new Float32BufferAttribute(colorsArray, 3));
  return object;
};

// studio/src/utils/debug/debugDrawPoint.ts
import {Points as Points2, PointsMaterial as PointsMaterial2, Material as Material2} from "three";
var debugDrawPoint = (debugObj, position, { size = 5, renderOrder = 200, color = 16711680 } = {}) => {
  const object = new Points2(undefined, new PointsMaterial2({ color, size }));
  debugObj?.scene?.add(object);
  debugObj?.debugHelpers?.push(object);
  if (object.material instanceof Material2)
    object.material.depthTest = false;
  if (object.material instanceof Material2)
    object.material.transparent = true;
  object.renderOrder = renderOrder;
  object.frustumCulled = false;
  object.geometry.setFromPoints([position]);
  return object;
};

// studio/src/utils/debug/debugDrawLine.ts
import {LineSegments, LineBasicMaterial, Material as Material3} from "three";
var debugDrawLine = (debugObj, a, b, { renderOrder = 2000, color = 16711680 }) => {
  const object = new LineSegments(undefined, new LineBasicMaterial({ color, linewidth: 3, linecap: "round", linejoin: "round" }));
  debugObj?.debugHelpers?.push(object);
  debugObj?.scene?.add(object);
  if (object.material instanceof Material3)
    object.material.depthTest = false;
  if (object.material instanceof Material3)
    object.material.transparent = true;
  object.renderOrder = renderOrder;
  object.frustumCulled = false;
  object.geometry.setFromPoints([a, b]);
  return object;
};

// studio/src/utils/debug/debugDrawArrow.ts
import {ArrowHelper, Material as Material4} from "three";
var debugDrawArrow = (debugObj, pos, dir, style) => {
  const { color = 16711680, length: length2 = 1, renderOrder = 200 } = style;
  const debugArrow = new ArrowHelper(dir, pos, length2, color);
  debugObj?.debugHelpers?.push(debugArrow);
  debugObj?.scene?.add(debugArrow);
  debugArrow.renderOrder = renderOrder;
  debugArrow.frustumCulled = false;
  if (debugArrow.line.material instanceof Material4)
    debugArrow.line.material.depthTest = false;
  if (debugArrow.line.material instanceof Material4)
    debugArrow.line.material.transparent = true;
  debugArrow.line.renderOrder = renderOrder;
  debugArrow.line.frustumCulled = false;
  if (debugArrow.cone.material instanceof Material4)
    debugArrow.cone.material.depthTest = false;
  if (debugArrow.cone.material instanceof Material4)
    debugArrow.cone.material.transparent = true;
  debugArrow.cone.renderOrder = renderOrder;
  debugArrow.cone.frustumCulled = false;
  return debugArrow;
};

// studio/src/utils/debug/debugDrawClear.ts
import {Material as Material5} from "three";
var debugDrawClear = (debugObj, options = { debugLines: false, debugPoints: false, debugHelpers: true }) => {
  if (!debugObj.debugHelpers) {
    debugObj.debugHelpers = [];
  }
  if (options.debugLines && debugObj.debugLines) {
    if (debugObj.debugLines.material instanceof Material5)
      debugObj.debugLines.material.dispose();
    debugObj.debugLines.geometry.dispose();
    debugObj.scene?.remove(debugObj.debugLines);
    delete debugObj.debugLines;
  }
  if (options.debugPoints && debugObj.debugPoints) {
    if (debugObj.debugPoints.material instanceof Material5)
      debugObj.debugPoints.material.dispose();
    debugObj.debugPoints.geometry.dispose();
    debugObj.scene?.remove(debugObj.debugLines);
    delete debugObj.debugPoints;
  }
  if (options.debugHelpers && debugObj.debugHelpers) {
    debugObj.debugHelpers.forEach((debugHelper) => debugObj.scene?.remove(debugHelper));
    debugObj.debugHelpers.splice(0);
  }
};

// studio/src/core/tools/OrbitControls.ts
class OrbitControls extends ViewportTool {
  minDistance = 0;
  maxDistance = Infinity;
  minZoom = 0;
  maxZoom = Infinity;
  minPolarAngle = 0;
  maxPolarAngle = Math.PI;
  minAzimuthAngle = -Infinity;
  maxAzimuthAngle = Infinity;
  enableZoom = true;
  zoomSpeed = 1;
  enableRotate = true;
  rotateSpeed = 1;
  enablePan = true;
  panSpeed = 1;
  screenSpacePanning = true;
  keyPanSpeed = 7;
  zoomToCursor = false;
  scale = 1;
  zoomChanged = false;
  EPS = 0.000001;
  target;
  spherical = new Spherical;
  offset = new Vector310;
  lastPosition = new Vector310;
  lastQuaternion = new Quaternion2;
  sphericalDelta = new Spherical;
  panOffset = new Vector310;
  v = new Vector310;
  quat = new Quaternion2;
  quatInverse = new Quaternion2;
  dollyDirection = new Vector310;
  mouse = new Vector22;
  performCursorZoom = false;
  debugObjectCamera;
  handlers;
  enabledByTransform = true;
  _enabled = true;
  get enabled() {
    return this._enabled && this.enabledByTransform;
  }
  set enabled(value) {
    if (this._enabled === value)
      return;
    this._enabled = value;
  }
  constructor(viewport) {
    super(viewport);
    this.handlers = new OrbitControlsHandlers(this);
    this.target = this.hostObject.cameraController.getPivotPoint();
    this.update = this.update.bind(this);
    this.onUpdatePivotPoint = this.onUpdatePivotPoint.bind(this);
    this.onChangeCamera = this.onChangeCamera.bind(this);
    this.onSetRollMode = this.onSetRollMode.bind(this);
  }
  get camera() {
    return this.hostObject.camera;
  }
  activate() {
    if (this.debugObjectCamera)
      this.debugObjectCamera = { scene: App2.Instance().view.scene, onTextSync: () => App2.Instance().dispatchEvent({ type: "render" }) };
    const dir = this.hostObject.cameraController.zDir;
    const distance = -dir.dot(this.camera.position);
    const target = this.camera.position.clone().add(dir.clone().multiplyScalar(distance));
    if (Math.abs(target.x - this.camera.position.x) < 0.001 && Math.abs(target.y - this.camera.position.y) < 0.001) {
      target.y += 0.001;
    }
    this.target.set(target.x, target.y, 0);
    this.onChangeCamera({ previous: this.camera, current: this.camera });
    this.handlers.init();
    this.update();
    this.hostObject.addEventListener("cameraChanged", this.onChangeCamera);
    this.hostObject.addEventListener("updatePivotPoint", this.onUpdatePivotPoint);
    this.hostObject.addEventListener("setCameraRollMode", this.onSetRollMode);
  }
  deactivate() {
    this.handlers.dispose();
    this.hostObject.removeEventListener("cameraChanged", this.onChangeCamera);
    this.hostObject.removeEventListener("updatePivotPoint", this.onUpdatePivotPoint);
    this.hostObject.removeEventListener("setCameraRollMode", this.onSetRollMode);
  }
  rollMode = false;
  onSetRollMode(e) {
    this.rollMode = e.state;
  }
  onChangeCamera(event) {
    this.quat.setFromUnitVectors(this.camera.up, new Vector310(0, 1, 0));
    this.quatInverse.copy(this.quat).invert();
  }
  onUpdatePivotPoint(event) {
    this.update(event.fireEvent);
  }
  twoPI = 2 * Math.PI;
  update(fireEvent = true) {
    if (this.debugObjectCamera) {
      debugDrawClear(this.debugObjectCamera);
    }
    const position = this.camera.position;
    this.offset.copy(position).sub(this.target);
    if (this.debugObjectCamera) {
      debugDrawArrow(this.debugObjectCamera, this.target.clone(), this.offset.clone().normalize(), { color: 65280, length: 10 });
    }
    this.offset.applyQuaternion(this.quat);
    if (this.debugObjectCamera) {
      debugDrawArrow(this.debugObjectCamera, this.target.clone(), this.offset.clone().normalize(), { color: 16711680, length: 15 });
    }
    this.spherical.setFromVector3(this.offset);
    this.spherical.theta += this.sphericalDelta.theta;
    this.spherical.phi += this.sphericalDelta.phi;
    let min = this.minAzimuthAngle;
    let max = this.maxAzimuthAngle;
    if (isFinite(min) && isFinite(max)) {
      if (min < -Math.PI)
        min += this.twoPI;
      else if (min > Math.PI)
        min -= this.twoPI;
      if (max < -Math.PI)
        max += this.twoPI;
      else if (max > Math.PI)
        max -= this.twoPI;
      if (min <= max) {
        this.spherical.theta = Math.max(min, Math.min(max, this.spherical.theta));
      } else {
        this.spherical.theta = this.spherical.theta > (min + max) / 2 ? Math.max(min, this.spherical.theta) : Math.min(max, this.spherical.theta);
      }
    }
    this.spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.spherical.phi));
    this.spherical.makeSafe();
    this.spherical.radius *= this.scale;
    this.spherical.radius = Math.max(this.minDistance, Math.min(this.maxDistance, this.spherical.radius));
    this.target.add(this.panOffset);
    this.offset.setFromSpherical(this.spherical);
    if (this.debugObjectCamera) {
      debugDrawArrow(this.debugObjectCamera, this.target.clone(), this.offset.clone().normalize(), { color: 13434828, length: 20 });
    }
    this.offset.applyQuaternion(this.quatInverse);
    position.copy(this.target).add(this.offset);
    this.camera.lookAt(this.target);
    this.sphericalDelta.set(0, 0, 0);
    this.panOffset.set(0, 0, 0);
    this.scale = 1;
    if (this.debugObjectCamera) {
      const fontSize = 2;
      const rotation = new Euler().setFromQuaternion(this.camera.quaternion);
      const deltaTarget = this.target.clone().sub(position);
      const dirTarget = deltaTarget.clone().normalize();
      debugDrawArrow(this.debugObjectCamera, this.target.clone(), this.camera.up.clone(), { color: 16764108, length: 15 });
      debugDrawLine(this.debugObjectCamera, position.clone(), this.target.clone(), { color: 16711680 });
      debugDrawPoint(this.debugObjectCamera, position.clone().add(dirTarget.clone().multiplyScalar(0.1)).add(this.camera.up.clone().multiplyScalar(1)), { color: 16711680, size: 5 });
      debugDrawPoint(this.debugObjectCamera, this.target.clone(), { color: 255, size: 5 });
      debugDrawArrow(this.debugObjectCamera, this.target.clone(), this.offset.clone().normalize(), { color: 255, length: 25 });
    }
    if (this.zoomChanged || this.lastPosition.distanceToSquared(this.camera.position) > this.EPS || 8 * (1 - this.lastQuaternion.dot(this.camera.quaternion)) > this.EPS) {
      if (fireEvent)
        App2.Instance().dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
      this.lastPosition.copy(this.camera.position);
      this.lastQuaternion.copy(this.camera.quaternion);
      this.zoomChanged = false;
      return true;
    }
    return false;
  }
  getZoomScale() {
    return Math.pow(0.95, this.zoomSpeed);
  }
  rotateLeft(angle) {
    this.sphericalDelta.theta -= angle;
  }
  rotateUp(angle) {
    this.sphericalDelta.phi -= angle;
  }
  panLeft(distance) {
    this.v.setFromMatrixColumn(this.camera.matrix, 0);
    this.v.multiplyScalar(-distance);
    this.panOffset.add(this.v);
  }
  panUp(distance) {
    if (this.screenSpacePanning) {
      this.v.setFromMatrixColumn(this.camera.matrix, 1);
    } else {
      this.v.setFromMatrixColumn(this.camera.matrix, 0);
      this.v.crossVectors(this.camera.up, this.v);
    }
    this.v.multiplyScalar(distance);
    this.panOffset.add(this.v);
  }
  pan(deltaX, deltaY) {
    if (this.camera instanceof PerspectiveCamera2) {
      const position = this.camera.position;
      this.offset.copy(position).sub(this.target);
      let targetDistance = this.offset.length();
      targetDistance *= Math.tan(this.camera.fov / 2 * Math.PI / 180);
      this.panLeft(2 * deltaX * targetDistance / this.hostObject.height);
      this.panUp(2 * deltaY * targetDistance / this.hostObject.height);
    } else {
      this.panLeft(deltaX * (this.camera.right - this.camera.left) / this.camera.zoom / this.hostObject.height);
      this.panUp(deltaY * (this.camera.top - this.camera.bottom) / this.camera.zoom / this.hostObject.height);
    }
  }
  dollyOut(dollyScale) {
    if (this.camera instanceof PerspectiveCamera2) {
      this.scale /= dollyScale;
    } else {
      this.camera.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.camera.zoom * dollyScale));
      this.camera.updateProjectionMatrix();
      this.zoomChanged = true;
    }
  }
  dollyIn(dollyScale) {
    if (this.camera instanceof PerspectiveCamera2) {
      this.scale *= dollyScale;
    } else {
      this.camera.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.camera.zoom / dollyScale));
      this.camera.updateProjectionMatrix();
      this.zoomChanged = true;
    }
  }
  updateZoomParameters(x, y) {
    if (!this.zoomToCursor)
      return;
    this.performCursorZoom = true;
    const rect = this.hostObject.getDomElement().getBoundingClientRect();
    const dx = x - rect.left;
    const dy = y - rect.top;
    const w = rect.width;
    const h = rect.height;
    this.mouse.x = dx / w * 2 - 1;
    this.mouse.y = -(dy / h) * 2 + 1;
    this.dollyDirection.set(this.mouse.x, this.mouse.y, 1).unproject(this.camera).sub(this.camera.position).normalize();
  }
}

class OrbitControlsHandlers {
  STATE = {
    NONE: -1,
    ROTATE: 0,
    DOLLY: 1,
    PAN: 2,
    TOUCH_ROTATE: 3,
    TOUCH_PAN: 4,
    TOUCH_DOLLY_PAN: 5,
    TOUCH_DOLLY_ROTATE: 6
  };
  state = this.STATE.NONE;
  rotateStart = new Vector22;
  rotateEnd = new Vector22;
  rotateDelta = new Vector22;
  panStart = new Vector22;
  panEnd = new Vector22;
  panDelta = new Vector22;
  dollyStart = new Vector22;
  dollyEnd = new Vector22;
  dollyDelta = new Vector22;
  orbitControls;
  pointers = [];
  pointerPositions = {};
  controlActive = false;
  mouseButtons = { LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.DOLLY, RIGHT: MOUSE.PAN };
  touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN };
  tmpQuaternion = new Quaternion2;
  quaternionStart = new Quaternion2;
  rotationAxis = new Vector310;
  angleStart = 0;
  pickPlane = new Plane;
  debugObjectPickPoint;
  constructor(orbitControls) {
    this.orbitControls = orbitControls;
    this.onTransformStart = this.onTransformStart.bind(this);
    this.onTransformEnd = this.onTransformEnd.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onMouseWheel = this.onMouseWheel.bind(this);
    this.onStartMove = this.onStartMove.bind(this);
    if (this.debugObjectPickPoint)
      this.debugObjectPickPoint = { scene: App2.Instance().view.scene };
  }
  get viewport() {
    return this.orbitControls.hostObject;
  }
  get domElementMouseDown() {
    return this.viewport.domElement;
  }
  get domElementMouseMove() {
    return document;
  }
  get domElementMouseUp() {
    return document;
  }
  get domElementMouseWheel() {
    return this.viewport.domElement;
  }
  init() {
    App2.Instance().mouseController.addListener("pointerdown", this.onPointerDown, this.domElementMouseDown, "left" /* left */);
    App2.Instance().mouseController.addListener("pointerdown", this.onPointerDown, this.domElementMouseDown, "right" /* right */);
    App2.Instance().mouseController.addListener("pointerdown", this.onPointerDown, this.domElementMouseDown, "middle" /* middle */);
    App2.Instance().mouseController.addListener("wheel", this.onMouseWheel, this.domElementMouseWheel);
    App2.Instance().addEventListener("transformStart", this.onTransformStart);
    App2.Instance().addEventListener("transformEnd", this.onTransformEnd);
  }
  dispose() {
    App2.Instance().mouseController.removeListener("pointerdown", this.onPointerDown, this.domElementMouseDown, "left" /* left */);
    App2.Instance().mouseController.removeListener("pointerdown", this.onPointerDown, this.domElementMouseDown, "middle" /* middle */);
    App2.Instance().mouseController.removeListener("pointerdown", this.onPointerDown, this.domElementMouseDown, "right" /* right */);
    App2.Instance().mouseController.removeListener("wheel", this.onMouseWheel, this.domElementMouseWheel);
    App2.Instance().removeEventListener("transformStart", this.onTransformStart);
    App2.Instance().removeEventListener("transformEnd", this.onTransformEnd);
  }
  onTransformStart() {
    this.orbitControls.enabledByTransform = false;
  }
  onTransformEnd() {
    this.orbitControls.enabledByTransform = true;
  }
  handleMouseDownRotate(event) {
    this.rotateStart.set(event.clientX, event.clientY);
  }
  handleMouseDownDolly(event) {
    this.dollyStart.set(event.clientX, event.clientY);
  }
  handleMouseDownPan(event) {
    if (this.orbitControls.rollMode) {
      const pivotPoint = this.orbitControls.hostObject.cameraController.getPivotPoint();
      this.pickPlane.setFromNormalAndCoplanarPoint(this.orbitControls.hostObject.cameraController.zDir, pivotPoint);
      const raycaster = App2.Instance().raycastController.raycaster;
      raycaster.setFromCamera(App2.Instance().raycastController.position, this.orbitControls.hostObject.camera);
      const pickPoint = raycaster.ray.intersectPlane(this.pickPlane, new Vector310);
      if (!pickPoint) {
        console.warn("onRightMouseDown: no pickPoint");
        return;
      }
      if (this.debugObjectPickPoint) {
        debugDrawClear(this.debugObjectPickPoint);
        debugDrawPoints(this.debugObjectPickPoint, [pickPoint.clone(), pivotPoint.clone()], [new Color3(1, 1, 0), new Color3(1, 0, 0)]);
      }
      const origin = pivotPoint;
      const deltaV3 = pickPoint.clone().sub(origin);
      const deltaN = deltaV3.clone().normalize();
      const angle = Math.atan2(deltaN.dot(this.orbitControls.hostObject.cameraController.yDir), deltaN.dot(this.orbitControls.hostObject.cameraController.xDir));
      this.angleStart = angle;
      this.rotationAxis.copy(this.orbitControls.hostObject.cameraController.zDir);
      this.quaternionStart.copy(this.orbitControls.hostObject.camera.quaternion);
    } else {
      this.panStart.set(event.clientX, event.clientY);
    }
  }
  handleMouseMoveRotate(event) {
    this.rotateEnd.set(event.clientX, event.clientY);
    this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart).multiplyScalar(this.orbitControls.rotateSpeed);
    const element = this.orbitControls.hostObject.domElement;
    this.orbitControls.rotateLeft(2 * Math.PI * this.rotateDelta.x / element.clientHeight);
    this.orbitControls.rotateUp(2 * Math.PI * this.rotateDelta.y / element.clientHeight);
    this.rotateStart.copy(this.rotateEnd);
    this.orbitControls.update();
  }
  handleMouseMoveDolly(event) {
    this.dollyEnd.set(event.clientX, event.clientY);
    this.dollyDelta.subVectors(this.dollyEnd, this.dollyStart);
    if (this.dollyDelta.y > 0) {
      this.orbitControls.dollyOut(this.orbitControls.getZoomScale());
    } else if (this.dollyDelta.y < 0) {
      this.orbitControls.dollyIn(this.orbitControls.getZoomScale());
    }
    this.dollyStart.copy(this.dollyEnd);
    this.orbitControls.update();
  }
  handleMouseMovePan(event) {
    if (this.orbitControls.rollMode) {
      const pivotPoint = this.orbitControls.hostObject.cameraController.getPivotPoint();
      const raycaster = App2.Instance().raycastController.raycaster;
      raycaster.setFromCamera(App2.Instance().raycastController.position, this.orbitControls.hostObject.camera);
      const pickPoint = raycaster.ray.intersectPlane(this.pickPlane, new Vector310);
      if (!pickPoint) {
        console.warn("onMouseMove: no pickPoint");
        return;
      }
      if (this.debugObjectPickPoint) {
        debugDrawClear(this.debugObjectPickPoint);
        debugDrawPoints(this.debugObjectPickPoint, [pickPoint.clone()], [new Color3(0, 0, 1)]);
      }
      const origin = pivotPoint;
      const deltaV3 = pickPoint.clone().sub(origin);
      const deltaN = deltaV3.clone().normalize();
      const angle = Math.atan2(deltaN.dot(this.orbitControls.hostObject.cameraController.yDir), deltaN.dot(this.orbitControls.hostObject.cameraController.xDir));
      const angleDelta = angle - this.angleStart;
      this.tmpQuaternion.setFromAxisAngle(this.rotationAxis, -angleDelta).normalize();
      const q = this.tmpQuaternion.clone().multiply(this.quaternionStart).normalize();
      this.orbitControls.hostObject.camera.quaternion.copy(q);
      this.orbitControls.camera.up.copy(this.orbitControls.hostObject.cameraController.yDir);
      this.orbitControls.onChangeCamera({ previous: this.orbitControls.camera, current: this.orbitControls.camera });
      this.orbitControls.update();
    } else {
      this.panEnd.set(event.clientX, event.clientY);
      this.panDelta.subVectors(this.panEnd, this.panStart).multiplyScalar(this.orbitControls.panSpeed);
      this.orbitControls.pan(this.panDelta.x, this.panDelta.y);
      this.panStart.copy(this.panEnd);
      this.orbitControls.update();
    }
  }
  wheelEventEndTimeout;
  handleMouseWheel(event) {
    if (event.deltaY < 0) {
      this.orbitControls.dollyIn(this.orbitControls.getZoomScale());
    } else if (event.deltaY > 0) {
      this.orbitControls.dollyOut(this.orbitControls.getZoomScale());
    }
    this.orbitControls.update();
  }
  handleTouchStartRotate(event) {
    if (this.pointers.length === 1) {
      this.rotateStart.set(event.pageX, event.pageY);
    } else {
      const position = this.getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this.rotateStart.set(x, y);
    }
  }
  handleTouchStartPan(event) {
    if (this.pointers.length === 1) {
      this.panStart.set(event.pageX, event.pageY);
    } else {
      const position = this.getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this.panStart.set(x, y);
    }
  }
  handleTouchStartDolly(event) {
    const position = this.getSecondPointerPosition(event);
    const dx = event.pageX - position.x;
    const dy = event.pageY - position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this.dollyStart.set(0, distance);
  }
  handleTouchStartDollyPan(event) {
    if (this.orbitControls.enableZoom)
      this.handleTouchStartDolly(event);
    if (this.orbitControls.enablePan)
      this.handleTouchStartPan(event);
  }
  handleTouchStartDollyRotate(event) {
    if (this.orbitControls.enableZoom)
      this.handleTouchStartDolly(event);
    if (this.orbitControls.enableRotate)
      this.handleTouchStartRotate(event);
  }
  handleTouchMoveRotate(event) {
    if (this.pointers.length == 1) {
      this.rotateEnd.set(event.pageX, event.pageY);
    } else {
      const position = this.getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this.rotateEnd.set(x, y);
    }
    this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart).multiplyScalar(this.orbitControls.rotateSpeed);
    const element = this.orbitControls.hostObject.getDomElement();
    this.orbitControls.rotateLeft(2 * Math.PI * this.rotateDelta.x / element.clientHeight);
    this.orbitControls.rotateUp(2 * Math.PI * this.rotateDelta.y / element.clientHeight);
    this.rotateStart.copy(this.rotateEnd);
  }
  handleTouchMovePan(event) {
    if (this.pointers.length === 1) {
      this.panEnd.set(event.pageX, event.pageY);
    } else {
      const position = this.getSecondPointerPosition(event);
      const x = 0.5 * (event.pageX + position.x);
      const y = 0.5 * (event.pageY + position.y);
      this.panEnd.set(x, y);
    }
    this.panDelta.subVectors(this.panEnd, this.panStart).multiplyScalar(this.orbitControls.panSpeed);
    this.orbitControls.pan(this.panDelta.x, this.panDelta.y);
    this.panStart.copy(this.panEnd);
  }
  handleTouchMoveDolly(event) {
    const position = this.getSecondPointerPosition(event);
    const dx = event.pageX - position.x;
    const dy = event.pageY - position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    this.dollyEnd.set(0, distance);
    this.dollyDelta.set(0, Math.pow(this.dollyEnd.y / this.dollyStart.y, this.orbitControls.zoomSpeed));
    this.orbitControls.dollyOut(this.dollyDelta.y);
    this.dollyStart.copy(this.dollyEnd);
    const centerX = (event.pageX + position.x) * 0.5;
    const centerY = (event.pageY + position.y) * 0.5;
    this.orbitControls.updateZoomParameters(centerX, centerY);
  }
  handleTouchMoveDollyPan(event) {
    if (this.orbitControls.enableZoom)
      this.handleTouchMoveDolly(event);
    if (this.orbitControls.enablePan)
      this.handleTouchMovePan(event);
  }
  handleTouchMoveDollyRotate(event) {
    if (this.orbitControls.enableZoom)
      this.handleTouchMoveDolly(event);
    if (this.orbitControls.enableRotate)
      this.handleTouchMoveRotate(event);
  }
  onStartMove(event) {
    App2.Instance().dispatchEvent({ type: "transformCameraStart", viewport: this.orbitControls.hostObject });
    App2.Instance().mouseController.removeListener("pointermove", this.onStartMove, this.domElementMouseMove);
    const mask = {
      1: "left",
      2: "right",
      4: "middle"
    };
    let count = 0;
    for (let key in mask) {
      if (event.buttons & Number(key)) {
        count++;
        const onEndMove = () => {
          count--;
          if (count === 0) {
            App2.Instance().dispatchEvent({ type: "transformCameraEnd", viewport: this.orbitControls.hostObject });
          }
          App2.Instance().mouseController.removeListener("pointerup", onEndMove, this.domElementMouseUp, mask[key]);
        };
        App2.Instance().mouseController.addListener("pointerup", onEndMove, this.domElementMouseUp, mask[key]);
      }
    }
  }
  onPointerDown(e) {
    const event = e;
    if (this.orbitControls.enabled === false)
      return;
    if (this.pointers.length === 0) {
      App2.Instance().mouseController.addListener("pointermove", this.onPointerMove, this.domElementMouseMove);
      App2.Instance().mouseController.addListener("pointerup", this.onPointerUp, this.domElementMouseUp, "left" /* left */);
      App2.Instance().mouseController.addListener("pointerup", this.onPointerUp, this.domElementMouseUp, "right" /* right */);
      App2.Instance().mouseController.addListener("pointerup", this.onPointerUp, this.domElementMouseUp, "middle" /* middle */);
    }
    if (this.isTrackingPointer(event))
      return;
    this.addPointer(event);
    if (event.pointerType === "touch") {
      this.onTouchStart(event);
    } else {
      this.onMouseDown(event);
    }
    App2.Instance().mouseController.addListener("pointermove", this.onStartMove, this.domElementMouseMove);
  }
  onPointerUp(e) {
    const event = e;
    this.removePointer(event);
    switch (this.pointers.length) {
      case 0:
        App2.Instance().mouseController.removeListener("pointermove", this.onPointerMove, this.domElementMouseMove);
        App2.Instance().mouseController.removeListener("pointerup", this.onPointerUp, this.domElementMouseUp, "left" /* left */);
        App2.Instance().mouseController.removeListener("pointerup", this.onPointerUp, this.domElementMouseUp, "right" /* right */);
        App2.Instance().mouseController.removeListener("pointerup", this.onPointerUp, this.domElementMouseUp, "middle" /* middle */);
        this.state = this.STATE.NONE;
        break;
      case 1:
        const pointerId = this.pointers[0];
        const position = this.pointerPositions[pointerId];
        this.onTouchStart({ pointerId, pageX: position.x, pageY: position.y });
        break;
    }
    App2.Instance().mouseController.removeListener("pointermove", this.onStartMove, this.domElementMouseMove);
  }
  onPointerMove(e) {
    const event = e;
    if (this.orbitControls.enabled === false)
      return;
    if (event.pointerType === "touch") {
      this.onTouchMove(event);
    } else {
      this.onMouseMove(event);
    }
  }
  onMouseDown(event) {
    let mouseAction;
    switch (event.button) {
      case 0:
        mouseAction = this.mouseButtons.LEFT;
        break;
      case 1:
        mouseAction = this.mouseButtons.MIDDLE;
        break;
      case 2:
        mouseAction = this.mouseButtons.RIGHT;
        break;
      default:
        mouseAction = -1;
    }
    switch (mouseAction) {
      case MOUSE.DOLLY:
        if (this.orbitControls.enableZoom === false)
          return;
        this.handleMouseDownDolly(event);
        this.state = this.STATE.DOLLY;
        break;
      case MOUSE.ROTATE:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          if (this.orbitControls.enablePan === false)
            return;
          this.handleMouseDownPan(event);
          this.state = this.STATE.PAN;
        } else {
          if (this.orbitControls.enableRotate === false)
            return;
          this.handleMouseDownRotate(event);
          this.state = this.STATE.ROTATE;
        }
        break;
      case MOUSE.PAN:
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
          if (this.orbitControls.enableRotate === false)
            return;
          this.handleMouseDownRotate(event);
          this.state = this.STATE.ROTATE;
        } else {
          if (this.orbitControls.enablePan === false)
            return;
          this.handleMouseDownPan(event);
          this.state = this.STATE.PAN;
        }
        break;
      default:
        this.state = this.STATE.NONE;
    }
  }
  onMouseMove(event) {
    switch (this.state) {
      case this.STATE.ROTATE:
        if (this.orbitControls.enableRotate === false)
          return;
        this.handleMouseMoveRotate(event);
        break;
      case this.STATE.DOLLY:
        if (this.orbitControls.enableZoom === false)
          return;
        this.handleMouseMoveDolly(event);
        break;
      case this.STATE.PAN:
        if (this.orbitControls.enablePan === false)
          return;
        this.handleMouseMovePan(event);
        break;
    }
  }
  onMouseWheel(event) {
    if (this.orbitControls.enabled === false || this.orbitControls.enableZoom === false || this.state !== this.STATE.NONE)
      return;
    event.preventDefault();
    this.handleMouseWheel(this.customWheelEvent(event));
  }
  customWheelEvent(event) {
    const mode = event.deltaMode;
    const newEvent = { clientX: event.clientX, clientY: event.clientY, deltaY: event.deltaY };
    switch (mode) {
      case 1:
        newEvent.deltaY *= 16;
        break;
      case 2:
        newEvent.deltaY *= 100;
        break;
    }
    if (event.ctrlKey && !this.controlActive) {
      newEvent.deltaY *= 10;
    }
    return newEvent;
  }
  onTouchStart(event) {
    this.trackPointer(event);
    switch (this.pointers.length) {
      case 1:
        switch (this.touches.ONE) {
          case TOUCH.ROTATE:
            if (this.orbitControls.enableRotate === false)
              return;
            this.handleTouchStartRotate(event);
            this.state = this.STATE.TOUCH_ROTATE;
            break;
          case TOUCH.PAN:
            if (this.orbitControls.enablePan === false)
              return;
            this.handleTouchStartPan(event);
            this.state = this.STATE.TOUCH_PAN;
            break;
          default:
            this.state = this.STATE.NONE;
        }
        break;
      case 2:
        switch (this.touches.TWO) {
          case TOUCH.DOLLY_PAN:
            if (this.orbitControls.enableZoom === false && this.orbitControls.enablePan === false)
              return;
            this.handleTouchStartDollyPan(event);
            this.state = this.STATE.TOUCH_DOLLY_PAN;
            break;
          case TOUCH.DOLLY_ROTATE:
            if (this.orbitControls.enableZoom === false && this.orbitControls.enableRotate === false)
              return;
            this.handleTouchStartDollyRotate(event);
            this.state = this.STATE.TOUCH_DOLLY_ROTATE;
            break;
          default:
            this.state = this.STATE.NONE;
        }
        break;
      default:
        this.state = this.STATE.NONE;
    }
  }
  onTouchMove(event) {
    this.trackPointer(event);
    switch (this.state) {
      case this.STATE.TOUCH_ROTATE:
        if (this.orbitControls.enableRotate === false)
          return;
        this.handleTouchMoveRotate(event);
        this.orbitControls.update();
        break;
      case this.STATE.TOUCH_PAN:
        if (this.orbitControls.enablePan === false)
          return;
        this.handleTouchMovePan(event);
        this.orbitControls.update();
        break;
      case this.STATE.TOUCH_DOLLY_PAN:
        if (this.orbitControls.enableZoom === false && this.orbitControls.enablePan === false)
          return;
        this.handleTouchMoveDollyPan(event);
        this.orbitControls.update();
        break;
      case this.STATE.TOUCH_DOLLY_ROTATE:
        if (this.orbitControls.enableZoom === false && this.orbitControls.enableRotate === false)
          return;
        this.handleTouchMoveDollyRotate(event);
        this.orbitControls.update();
        break;
      default:
        this.state = this.STATE.NONE;
    }
  }
  addPointer(event) {
    this.pointers.push(event.pointerId);
  }
  removePointer(event) {
    delete this.pointerPositions[event.pointerId];
    for (let i = 0;i < this.pointers.length; i++) {
      if (this.pointers[i] == event.pointerId) {
        this.pointers.splice(i, 1);
        return;
      }
    }
  }
  isTrackingPointer(event) {
    for (let i = 0;i < this.pointers.length; i++) {
      if (this.pointers[i] == event.pointerId)
        return true;
    }
    return false;
  }
  trackPointer(event) {
    let position = this.pointerPositions[event.pointerId];
    if (position === undefined) {
      position = new Vector22;
      this.pointerPositions[event.pointerId] = position;
    }
    position.set(event.pageX, event.pageY);
  }
  getSecondPointerPosition(event) {
    const pointerId = event.pointerId === this.pointers[0] ? this.pointers[1] : this.pointers[0];
    return this.pointerPositions[pointerId];
  }
}

// studio/src/products/MPREmbedded/tools/MprVolumeSliceSpawner.ts
import {Box2, Color as Color6, OrthographicCamera as OrthographicCamera3, Vector2 as Vector24} from "three";

// studio/src/core/components/ViewportComponent.ts
class ViewportComponent extends Component {
  viewports;
  constructor(entity) {
    super(entity);
    this.viewports = new Set;
  }
  addViewport = (name, recursive = true) => {
    const viewport = name instanceof Viewport ? name : App2.Instance().view.getViewport(name);
    if (viewport) {
      const cameraLayer = viewport.cameraLayer;
      if (cameraLayer) {
        this.viewports.add(viewport);
        this.entity.object3d.layers.enable(cameraLayer);
        this.entity.object3d.layers.disable(0);
        if (recursive) {
          this.entity.children.forEach((entity) => {
            const component = entity.getComponent(ViewportComponent);
            component?.addViewport(name, true);
          });
        }
      }
    }
  };
  deleteViewport = (name, recursive = true) => {
    const viewport = name instanceof Viewport ? name : App2.Instance().view.getViewport(name);
    if (viewport) {
      const cameraLayer = viewport.cameraLayer;
      if (cameraLayer) {
        this.viewports.delete(viewport);
        this.entity.object3d.layers.disable(cameraLayer);
        if (recursive) {
          this.entity.children.forEach((entity) => {
            const component = entity.getComponent(ViewportComponent);
            component?.deleteViewport(name, true);
          });
        }
      }
    }
  };
  deleteAllViewports(recursive = true) {
    this.viewports.clear();
    this.entity.object3d.layers.set(0);
    if (recursive) {
      this.entity.children.forEach((entity) => {
        const component = entity.getComponent(ViewportComponent);
        component?.deleteAllViewports(true);
      });
    }
  }
  hasViewport(name) {
    const viewport = name instanceof Viewport ? name : App2.Instance().view.getViewport(name);
    return this.viewports.has(viewport);
  }
}

// studio/src/products/MPREmbedded/utils/MPR2Storage.ts
import {Box3 as Box32} from "three";

// studio/src/products/MPREmbedded/constants/DefaultColorProfile.ts
var DEFAULT_COLOR_PROFILE = {
  ww: 3200,
  wc: 1000
};

// studio/src/shared/primitives.ts
import {Box3, Vector3 as Vector311} from "three";
var FloatPoint3DToVector3 = (t) => {
  return new Vector311(t.X, t.Y, t.Z);
};
var OriginatedPositionToVector3Set = (originatedPosition) => {
  return {
    x: FloatPoint3DToVector3(originatedPosition.X),
    y: FloatPoint3DToVector3(originatedPosition.Y),
    z: FloatPoint3DToVector3(originatedPosition.Z)
  };
};
var Box3FromBox3Json = (box) => {
  const min = new Vector311(box.min.x, box.min.y, box.min.z);
  const max = new Vector311(box.max.x, box.max.y, box.max.z);
  return new Box3(min, max);
};

// studio/src/products/MPREmbedded/utils/MPR2Storage.ts
class MPR2Storage {
  subvolumes = [];
  boundingBox = new Box32;
  colorProfile = Object.assign({}, DEFAULT_COLOR_PROFILE);
  contrast = 0;
  brightness = 0;
  initialColorProfile = Object.assign({}, DEFAULT_COLOR_PROFILE);
  put(subvolumes, colorProfile) {
    this.subvolumes = subvolumes;
    this.colorProfile.ww = colorProfile.ww;
    this.colorProfile.wc = colorProfile.wc;
    this.initialColorProfile.ww = colorProfile.ww;
    this.initialColorProfile.wc = colorProfile.wc;
    this.subvolumes.forEach((tile) => {
      this.boundingBox.union(Box3FromBox3Json(tile.box));
    });
  }
  resetColorProfileToInitial() {
    this.colorProfile.ww = this.initialColorProfile.ww;
    this.colorProfile.wc = this.initialColorProfile.wc;
    this.contrast = 0;
    this.brightness = 0;
  }
  clear() {
    this.subvolumes = [];
    this.boundingBox = new Box32;
    this.colorProfile = Object.assign({}, DEFAULT_COLOR_PROFILE);
    this.contrast = 0;
    this.brightness = 0;
  }
}
var MPR2Storage_default = new MPR2Storage;

// studio/src/shared/MPR/entities/VolumeSliceAbstract.ts
import {PlaneGeometry as PlaneGeometry2, Plane as Plane3, Vector3 as Vector319} from "three";

// studio/src/shared/MPR/utils/VolumeTileController.ts
import {Box3 as Box36, Vector3 as Vector317, Mesh, Color as Color4, Box3Helper, Triangle} from "three";

// studio/src/utils/debug/DebugUtils.ts
import {Group as Group2} from "three";
var getContainer = (name) => {
  const scene = App2.Instance().view.scene;
  let container = scene.getObjectByName(name);
  if (!container) {
    container = new Group2;
    container.name = name;
    scene.add(container);
    return container;
  }
  return container;
};
var DebugUtils = { getContainer };

// studio/src/utils/materials/SliceMaterialBase.ts
import {RawShaderMaterial} from "three";

class SliceMaterialBase extends RawShaderMaterial {
  users = [];
  constructor(parameters) {
    super(parameters);
    if (!this.uniforms.contrast)
      this.uniforms.contrast = { value: 0 };
    if (!this.uniforms.brightness)
      this.uniforms.brightness = { value: 0 };
    if (!this.uniforms.ww)
      this.uniforms.ww = { value: 0 };
    if (!this.uniforms.wmin)
      this.uniforms.wmin = { value: 0 };
  }
  setWWWC(ww, wc) {
    this.uniforms.ww.value = ww;
    this.uniforms.wmin.value = wc - ww / 2;
  }
  increaseUsers(user) {
    if (!this.users.includes(user))
      this.users.push(user);
    else
      console.warn(this.constructor.name, ".increaseUsers: user is already added = ", user, this);
  }
  decreaseUsers(user) {
    const userIndex = this.users.indexOf(user);
    if (userIndex !== -1)
      this.users.splice(userIndex, 1);
    else
      console.warn(this.constructor.name, ".decreaseUsers: no user = ", user, this);
  }
  destroy(user) {
    this.decreaseUsers(user);
    if (this.users.length === 0) {
      this.dispose();
    }
  }
}

// studio/src/core/Entity.ts
import {Material as Material6, MathUtils as MathUtils2} from "three";

// studio/src/core/containers/EntityContainer.ts
class EntityContainer extends Container {
  constructor(parent, objects = []) {
    super(parent, objects);
  }
  size() {
    return this.objects.length;
  }
  add(entity) {
    if (Array.isArray(entity)) {
      const added = [...entity].filter(this.addOne.bind(this));
      App2.Instance().dispatchEvent({ type: "add", entities: added, container: this.hostObject });
    } else {
      if (this.addOne(entity)) {
        App2.Instance().dispatchEvent({ type: "add", entities: [entity], container: this.hostObject });
      }
    }
  }
  delete(entity) {
    if (Array.isArray(entity)) {
      const deleted = entity.filter((child) => {
        this.deleteOne(child);
      });
      App2.Instance().dispatchEvent({ type: "delete", entities: deleted, container: this.hostObject });
    } else {
      if (this.deleteOne(entity)) {
        App2.Instance().dispatchEvent({ type: "delete", entities: [entity], container: this.hostObject });
      }
    }
  }
  addOne(entity) {
    if (entity.parent !== this.hostObject) {
      if (entity.parent) {
        entity.parent.delete(entity);
      }
      this.objects.push(entity);
      entity.parent = this.hostObject;
      this.hostObject.object3d?.add(entity.object3d);
      return true;
    }
    return false;
  }
  deleteOne(entity) {
    if (entity.parent === this.hostObject) {
      const index = this.hostObject.children.indexOf(entity);
      if (index >= 0) {
        this.hostObject.children.splice(index, 1);
      }
      entity.parent = undefined;
      this.hostObject.object3d?.remove(entity.object3d);
      return true;
    }
    return false;
  }
}

// studio/src/core/Entity.ts
var entityEventNames = ["transform", "update", "delete", "add"];

class Entity extends EventDispatcher {
  children;
  parent;
  object3d;
  container;
  components;
  layer;
  uuid;
  constructor(object3D) {
    super(entityEventNames);
    this.object3d = object3D;
    this.children = [];
    this.container = new EntityContainer(this, this.children);
    this.components = new Map;
    this.uuid = MathUtils2.generateUUID();
  }
  get name() {
    return this.object3d.name;
  }
  set name(name) {
    this.object3d.name = name;
  }
  get position() {
    return this.object3d.position;
  }
  set position(value) {
    this.object3d.position.copy(value);
  }
  get quaternion() {
    return this.object3d.quaternion;
  }
  set quaternion(value) {
    this.object3d.quaternion.copy(value);
  }
  add(children) {
    this.container.add(children);
  }
  delete(children) {
    this.container.delete(children);
  }
  getBaseClass(component) {
    const prototype = Object.getPrototypeOf(component);
    if (prototype === Component) {
      return component;
    } else {
      return this.getBaseClass(prototype);
    }
  }
  getComponent(type) {
    return this.components.get(type);
  }
  setComponent(component, instance) {
    const BaseConcreteClass = this.getBaseClass(component);
    if (instance) {
      this.components.set(BaseConcreteClass, instance);
    } else if (component) {
      const instance2 = new component(this);
      !this.components.has(component) && this.components.set(BaseConcreteClass, instance2);
      return instance2;
    }
  }
  removeComponents() {
    this.components.clear();
  }
  getLayer() {
    return this.layer || this.parent?.getLayer();
  }
  setLayer(layer) {
    if (!this.layer?.has(this)) {
      if (layer?.has(this)) {
        this.layer = layer;
      } else {
        this.layer = undefined;
      }
    }
  }
  dispose() {
    this.object3d.traverse((obj) => {
      if (obj.geometry) {
        obj.geometry.dispose();
      }
      if (obj.material) {
        if (obj.material instanceof Material6) {
          obj.material.dispose();
        } else if (Array.isArray(obj.material)) {
          obj.material.forEach((material) => material instanceof Material6 && material.dispose());
        }
      }
    });
    this.object3d.clear();
  }
  toJson() {
    return {};
  }
}

// studio/src/shared/MPR/entities/VolumeTile.ts
class VolumeTile extends Entity {
  node;
  constructor(object3d, node) {
    super(object3d);
    this.node = node;
    this.object3d.frustumCulled = false;
    const viewportComponent = this.setComponent(ViewportComponent);
    viewportComponent.addViewport("Axial");
    viewportComponent.addViewport("Coronal");
    viewportComponent.addViewport("Sagittal");
  }
}

// studio/src/shared/MPR/loaders/Texture3DLoader.ts
import {Box3 as Box35, Data3DTexture as Data3DTexture2, FloatType as FloatType2, LinearFilter as LinearFilter2, RedFormat, Vector3 as Vector316} from "three";

// src/utils/parseNRRD.js
function unescapeValue(val) {
  return val.split("\\\\").map(function(s) {
    return s.replace("\\n", "\n");
  }).join("\\");
}
function parseField(identifier, descriptor) {
  switch (identifier) {
    case "content":
    case "number":
    case "sampleUnits":
      break;
    case "dimension":
    case "blockSize":
    case "lineSkip":
    case "byteSkip":
    case "spaceDimension":
      descriptor = parseNRRDInteger(descriptor);
      break;
    case "min":
    case "max":
    case "oldMin":
    case "oldMax":
      descriptor = parseNRRDFloat(descriptor);
      break;
    case "spaceOrigin":
      descriptor = parseNRRDVector(descriptor);
      break;
    case "labels":
    case "units":
    case "spaceUnits":
      descriptor = parseNRRDWhitespaceSeparatedList(descriptor, parseNRRDQuotedString);
      break;
    case "sizes":
      descriptor = parseNRRDWhitespaceSeparatedList(descriptor, parseNRRDInteger);
      break;
    case "spacings":
    case "thicknesses":
    case "axisMins":
    case "axisMaxs":
      descriptor = parseNRRDWhitespaceSeparatedList(descriptor, parseNRRDFloat);
      break;
    case "spaceDirections":
    case "measurementFrame":
      descriptor = parseNRRDWhitespaceSeparatedList(descriptor, parseNRRDVector);
      break;
    case "type":
      descriptor = parseNRRDType(descriptor);
      break;
    case "encoding":
      descriptor = parseNRRDEncoding(descriptor);
      break;
    case "endian":
      descriptor = parseNRRDEndian(descriptor);
      break;
    case "dataFile":
      descriptor = parseNRRDDataFile(descriptor);
      break;
    case "centers":
      descriptor = parseNRRDWhitespaceSeparatedList(descriptor, parseNRRDCenter);
      break;
    case "kinds":
      descriptor = parseNRRDWhitespaceSeparatedList(descriptor, parseNRRDKind);
      break;
    case "space":
      descriptor = parseNRRDSpace(descriptor);
      break;
    default:
      console.warn("Unrecognized NRRD field: " + identifier);
  }
  return descriptor;
}
function mapNRRDToJavascript(id) {
  id = id.toLowerCase();
  if (id in mapNRRDToJavascriptStatic)
    return mapNRRDToJavascriptStatic[id];
  return id;
}
function parseNRRDInteger(str) {
  var val = parseInt(str, 10);
  if (Number.isNaN(val))
    throw new Error("Malformed NRRD integer: " + str);
  return val;
}
function parseNRRDFloat(str) {
  str = str.toLowerCase();
  if (str.indexOf("nan") >= 0)
    return NaN;
  if (str.indexOf("-inf") >= 0)
    return -Infinity;
  if (str.indexOf("inf") >= 0)
    return Infinity;
  var val = parseFloat(str);
  if (Number.isNaN(val))
    throw new Error("Malformed NRRD float: " + str);
  return val;
}
function parseNRRDVector(str) {
  if (str == "none")
    return null;
  if (str.length < 2 || str[0] !== "(" || str[str.length - 1] !== ")")
    throw new Error("Malformed NRRD vector: " + str);
  return str.slice(1, -1).split(",").map(parseNRRDFloat);
}
function parseNRRDQuotedString(str) {
  if (length < 2 || str[0] != '"' || str[str.length - 1] != '"') {
    throw new Error("Invalid NRRD quoted string: " + str);
  }
  return str.slice(1, -1).replace('\\"', '"');
}
function parseNRRDWhitespaceSeparatedList(str, parseElement) {
  return str.split(whitespaceListSeparator).map(parseElement);
}
function parseNRRDType(descriptor) {
  switch (descriptor.toLowerCase()) {
    case "signed char":
    case "int8":
    case "int8_t":
      return "int8";
    case "uchar":
    case "unsigned char":
    case "uint8":
    case "uint8_t":
      return "uint8";
    case "short":
    case "short int":
    case "signed short":
    case "signed short int":
    case "int16":
    case "int16_t":
      return "int16";
    case "ushort":
    case "unsigned short":
    case "unsigned short int":
    case "uint16":
    case "uint16_t":
      return "uint16";
    case "int":
    case "signed int":
    case "int32":
    case "int32_t":
      return "int32";
    case "uint":
    case "unsigned int":
    case "uint32":
    case "uint32_t":
      return "uint32";
    case "longlong":
    case "long long":
    case "long long int":
    case "signed long long":
    case "signed long long int":
    case "int64":
    case "int64_t":
      return "int64";
    case "ulonglong":
    case "unsigned long long":
    case "unsigned long long int":
    case "uint64":
    case "uint64_t":
      return "uint64";
    case "float":
      return "float";
    case "double":
      return "double";
    case "block":
      return "block";
    default:
      console.warn("Unrecognized NRRD type: " + descriptor);
      return descriptor;
  }
}
function parseNRRDEncoding(encoding) {
  switch (encoding.toLowerCase()) {
    case "raw":
      return "raw";
    case "txt":
    case "text":
    case "ascii":
      return "ascii";
    case "hex":
      return "hex";
    case "gz":
    case "gzip":
      return "gzip";
    case "bz2":
    case "bzip2":
      return "bzip2";
    default:
      console.warn("Unrecognized NRRD encoding: " + encoding);
      return encoding;
  }
}
function parseNRRDSpace(space) {
  switch (space.toLowerCase()) {
    case "right-anterior-superior":
    case "ras":
      return "right-anterior-superior";
    case "left-anterior-superior":
    case "las":
      return "left-anterior-superior";
    case "left-posterior-superior":
    case "lps":
      return "left-posterior-superior";
    case "right-anterior-superior-time":
    case "rast":
      return "right-anterior-superior-time";
    case "left-anterior-superior-time":
    case "last":
      return "left-anterior-superior-time";
    case "left-posterior-superior-time":
    case "lpst":
      return "left-posterior-superior-time";
    case "scanner-xyz":
      return "scanner-xyz";
    case "scanner-xyz-time":
      return "scanner-xyz-time";
    case "3d-right-handed":
      return "3D-right-handed";
    case "3d-left-handed":
      return "3D-left-handed";
    case "3d-right-handed-time":
      return "3D-right-handed-time";
    case "3d-left-handed-time":
      return "3D-left-handed-time";
    default:
      console.warn("Unrecognized space: " + space);
      return space;
  }
}
function parseNRRDEndian(endian) {
  switch (endian.toLowerCase()) {
    case "little":
      return "little";
    case "big":
      return "big";
    default:
      console.warn("Unrecognized NRRD endianness: " + endian);
      return endian;
  }
}
function parseNRRDDataFile(dataFile) {
  var match = dataFileFormatRE.exec(dataFile);
  if (match) {
    if (match.length == 5 && match[4]) {
      return {
        format: dataFile.substring(0, match.index),
        min: parseNRRDInteger(match[1]),
        max: parseNRRDInteger(match[2]),
        step: parseNRRDInteger(match[3]),
        subdim: parseNRRDInteger(match[4])
      };
    } else {
      return {
        format: dataFile.substring(0, match.index),
        min: parseNRRDInteger(match[1]),
        max: parseNRRDInteger(match[2]),
        step: parseNRRDInteger(match[3])
      };
    }
  } else {
    return dataFile;
  }
}
function parseNRRDCenter(center) {
  switch (center.toLowerCase()) {
    case "cell":
      return "cell";
    case "node":
      return "node";
    case "???":
    case "none":
      return null;
    default:
      console.warn("Unrecognized NRRD center: " + center);
      return center;
  }
}
function parseNRRDKind(kind) {
  var kindLC = kind.toLowerCase();
  if (kindLC in NRRDKinds)
    return NRRDKinds[kindLC];
  console.warn("Unrecognized NRRD kind: " + kind);
  return kind;
}
function parseNRRDRawData(buffer, type, sizes, options) {
  var i, arr, view, totalLen = 1, endianFlag;
  for (i = 0;i < sizes.length; i++) {
    if (sizes[i] <= 0)
      throw new Error("Sizes should be a list of positive (>0) integers!");
    totalLen *= sizes[i];
  }
  if (type == "block") {
    return buffer.slice(0, totalLen * options.blockSize);
  } else if (type == "int8" || type == "uint8" || options.endian == systemEndianness) {
    switch (type) {
      case "int8":
        checkSize(1);
        return new Int8Array(buffer.slice(0, totalLen));
      case "uint8":
        checkSize(1);
        return new Uint8Array(buffer.slice(0, totalLen));
      case "int16":
        checkSize(2);
        return new Int16Array(buffer.slice(0, totalLen * 2));
      case "uint16":
        checkSize(2);
        return new Uint16Array(buffer.slice(0, totalLen * 2));
      case "int32":
        checkSize(4);
        return new Int32Array(buffer.slice(0, totalLen * 4));
      case "uint32":
        checkSize(4);
        return new Uint32Array(buffer.slice(0, totalLen * 4));
      case "float":
        checkSize(4);
        return new Float32Array(buffer.slice(0, totalLen * 4));
      case "double":
        checkSize(8);
        return new Float64Array(buffer.slice(0, totalLen * 8));
      default:
        console.warn("Unsupported NRRD type: " + type + ", returning raw buffer.");
        return;
    }
  } else {
    switch (options.endian) {
      case "big":
        endianFlag = false;
        break;
      case "little":
        endianFlag = true;
        break;
      default:
        console.warn("Unsupported endianness in NRRD file: " + options.endian);
        return;
    }
    view = new DataView(buffer);
    switch (type) {
      case "int8":
        arr = new Int8Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getInt8(i);
        }
        return arr;
      case "uint8":
        arr = new Uint8Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getUint8(i);
        }
        return arr;
      case "int16":
        arr = new Int16Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getInt16(i * 2);
        }
        return arr;
      case "uint16":
        arr = new Uint16Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getUint16(i * 2);
        }
        return arr;
      case "int32":
        arr = new Int32Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getInt32(i * 4);
        }
        return arr;
      case "uint32":
        arr = new Uint32Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getUint32(i * 4);
        }
        return arr;
      case "float":
        arr = new Float32Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getFloat32(i * 4);
        }
        return arr;
      case "double":
        arr = new Float64Array(totalLen);
        for (i = 0;i < totalLen; i++) {
          arr[i] = view.getFloat64(i * 8);
        }
        return arr;
      default:
        console.warn("Unsupported NRRD type: " + type + ", returning raw buffer.");
        return;
    }
  }
  function checkSize(sizeOfType) {
    if (buffer.byteLength < totalLen * sizeOfType)
      throw new Error("NRRD file does not contain enough data!");
  }
}
function parseNRRDTextData(buffer, type, sizes) {
  var i, buf8, str, strList, totalLen = 1;
  for (i = 0;i < sizes.length; i++) {
    if (sizes[i] <= 0)
      throw new Error("Sizes should be a list of positive (>0) integers!");
    totalLen *= sizes[i];
  }
  buf8 = new Uint8Array(buffer);
  str = String.fromCharCode.apply(null, buf8);
  strList = str.split(whitespaceDataValueListSeparatorRE);
  if (strList.length < totalLen) {
    throw new Error("Not enough data in NRRD file!");
  } else if (strList.length > totalLen) {
    if (strList[0] === "")
      strList = strList.slice(1);
    strList = strList.slice(0, totalLen);
  }
  switch (type) {
    case "int8":
      return new Int8Array(strList.map(parseNRRDInteger));
    case "uint8":
      return new Uint8Array(strList.map(parseNRRDInteger));
    case "int16":
      return new Int16Array(strList.map(parseNRRDInteger));
    case "uint16":
      return new Uint16Array(strList.map(parseNRRDInteger));
    case "int32":
      return new Int32Array(strList.map(parseNRRDInteger));
    case "uint32":
      return new Uint32Array(strList.map(parseNRRDInteger));
    case "float":
      return new Float32Array(strList.map(parseNRRDFloat));
    case "double":
      return new Float64Array(strList.map(parseNRRDFloat));
    default:
      console.warn("Unsupported NRRD type: " + type + ".");
      return;
  }
}
function checkNRRD(ret) {
  if (ret.dimension === undefined) {
    throw new Error("Dimension missing from NRRD file!");
  } else if (ret.type === undefined) {
    throw new Error("Type missing from NRRD file!");
  } else if (ret.encoding === undefined) {
    throw new Error("Encoding missing from NRRD file!");
  } else if (ret.sizes === undefined) {
    throw new Error("Sizes missing from NRRD file!");
  }
  if (ret.type != "block" && ret.type != "int8" && ret.type != "uint8" && ret.encoding != "ascii" && ret.endian === undefined) {
    throw new Error("Endianness missing from NRRD file!");
  } else if (ret.type == "block" && ret.blockSize === undefined) {
    throw new Error("Missing block size in NRRD file!");
  }
  if (ret.dimension === 0) {
    throw new Error("Zero-dimensional NRRD file?");
  } else if (ret.dimension != ret.sizes.length) {
    throw new Error("Length of 'sizes' is different from 'dimension' in an NRRD file!");
  } else if (ret.spacings && ret.dimension != ret.spacings.length) {
    throw new Error("Length of 'spacings' is different from 'dimension' in an NRRD file!");
  } else if (ret.thicknesses && ret.dimension != ret.thicknesses.length) {
    throw new Error("Length of 'thicknesses' is different from 'dimension' in an NRRD file!");
  } else if (ret.axisMins && ret.dimension != ret.axisMins.length) {
    throw new Error("Length of 'axis mins' is different from 'dimension' in an NRRD file!");
  } else if (ret.axisMaxs && ret.dimension != ret.axisMaxs.length) {
    throw new Error("Length of 'axis maxs' is different from 'dimension' in an NRRD file!");
  } else if (ret.centers && ret.dimension != ret.centers.length) {
    throw new Error("Length of 'centers' is different from 'dimension' in an NRRD file!");
  } else if (ret.labels && ret.dimension != ret.labels.length) {
    throw new Error("Length of 'labels' is different from 'dimension' in an NRRD file!");
  } else if (ret.units && ret.dimension != ret.units.length) {
    throw new Error("Length of 'units' is different from 'dimension' in an NRRD file!");
  } else if (ret.kinds && ret.dimension != ret.kinds.length) {
    throw new Error("Length of 'kinds' is different from 'dimension' in an NRRD file!");
  }
  if ((ret.data === undefined || ret.data.length === 0) && (ret.buffer === undefined || ret.buffer.byteLength === 0) && ret.dataFile === undefined) {
    throw new Error("NRRD file has neither inline or external data!");
  }
}
var lineSeparatorRE = /[ \f\t\v]*\r?\n/;
var NRRDMagicRE = /^NRRD\d{4}$/;
var lineRE = /^([^:]*)(:[ =])(.*)$/;
var dataFileListRE = /^LIST(?: (\d+))?$/;
var parseNRRD = function(buffer, fflate) {
  var i, header, dataStart, ret = {
    data: undefined,
    buffer: undefined,
    keys: {},
    version: undefined
  }, lines, match, match2, buf8 = new Uint8Array(buffer);
  if (buf8.buffer !== buffer)
    buffer = buf8.buffer;
  i = 2;
  while (i < buf8.length) {
    if (buf8[i] == 10) {
      if (buf8[i - 1] == 10 || buf8[i - 1] == 13 && buf8[i - 2] == 10) {
        dataStart = i + 1;
        break;
      } else {
        i++;
      }
    } else if (buf8[i] == 13) {
      i++;
    } else {
      i += 2;
    }
  }
  if (dataStart === undefined) {
    header = String.fromCharCode.apply(null, buf8);
  } else {
    header = String.fromCharCode.apply(null, buf8.subarray(0, dataStart));
    ret.buffer = buffer.slice(dataStart);
  }
  lines = header.split(lineSeparatorRE);
  lines = lines.filter(function(l) {
    return l.length > 0 && l[0] != "#";
  });
  if (!NRRDMagicRE.test(lines[0])) {
    throw new Error("File is not an NRRD file!");
  }
  ret.version = parseInt(lines[0].substring(4, 8), 10);
  if (ret.version > 5) {
    console.warn("Reading an unsupported version of the NRRD format; things may go haywire.");
  }
  for (i = 1;i < lines.length; i++) {
    match = lineRE.exec(lines[i]);
    if (!match) {
      console.warn("Unrecognized line in NRRD header: " + lines[i]);
      continue;
    }
    if (match[2] == ": ") {
      match[1] = mapNRRDToJavascript(match[1]);
      if (match[1] == "dataFile" && (match2 = dataFileListRE.exec(match[3]))) {
        if (match2.length == 2 && match2[1]) {
          ret[match[1]] = {
            files: lines.slice(i + 1),
            subdim: parseNRRDInteger(match2[1])
          };
        } else {
          ret[match[1]] = lines.slice(i + 1);
        }
        lines.length = i;
      } else {
        ret[match[1]] = parseField(match[1], match[3]);
      }
    } else if (match[2] == ":=") {
      ret.keys[match[1]] = unescapeValue(match[3]);
    } else {
      throw new Error("Logic error in NRRD parser.");
    }
  }
  checkNRRD(ret);
  if ("dataFile" in ret) {
    console.warn("No support for external data yet!");
  } else {
    switch (ret.encoding) {
      case "raw":
        ret.data = parseNRRDRawData(ret.buffer, ret.type, ret.sizes, {
          endian: ret.endian,
          blockSize: ret.blockSize
        });
        break;
      case "ascii":
        ret.data = parseNRRDTextData(ret.buffer, ret.type, ret.sizes);
        break;
      case "gzip":
        ret.buffer = fflate.gunzipSync(new Uint8Array(ret.buffer)).buffer;
        ret.data = parseNRRDRawData(ret.buffer, ret.type, ret.sizes, {
          endian: ret.endian,
          blockSize: ret.blockSize
        });
        break;
      default:
        console.warn("Unsupported NRRD encoding: " + ret.encoding);
    }
  }
  return ret;
};
var mapNRRDToJavascriptStatic = {
  "block size": "blockSize",
  blocksize: "blockSize",
  "old min": "oldMin",
  oldmin: "oldMin",
  "old max": "oldMax",
  oldmax: "oldMax",
  "data file": "dataFile",
  datafile: "dataFile",
  "line skip": "lineSkip",
  lineskip: "lineSkip",
  "byte skip": "byteSkip",
  byteskip: "byteSkip",
  "sample units": "sampleUnits",
  sampleunits: "sampleUnits",
  "axis mins": "axisMins",
  "axis maxs": "axisMaxs",
  centers: "centers",
  centerings: "centers",
  "space dimension": "spaceDimension",
  "space units": "spaceUnits",
  "space origin": "spaceOrigin",
  "space directions": "spaceDirections",
  "measurement frame": "measurementFrame"
};
var whitespaceListSeparator = /[ \t]+/;
var dataFileFormatRE = / (-?\d+) (-?\d+) (-?\d+)(?: (\d+))?$/;
var NRRDKinds = {
  domain: "domain",
  space: "space",
  time: "time",
  list: "list",
  point: "point",
  vector: "vector",
  "covariant-vector": "covariant-vector",
  normal: "normal",
  stub: "stub",
  scalar: "scalar",
  complex: "complex",
  "2-vector": "2-vector",
  "3-color": "3-color",
  "rgb-color": "RGB-color",
  "hsv-color": "HSV-color",
  "xyz-color": "XYZ-color",
  "4-color": "4-color",
  "rgba-color": "RGBA-color",
  "3-vector": "3-vector",
  "3-gradient": "3-gradient",
  "3-normal": "3-normal",
  "4-vector": "4-vector",
  quaternion: "quaternion",
  "2d-symmetric-matrix": "2D-symmetric-matrix",
  "2d-masked-symmetric-matrix": "2D-masked-symmetric-matrix",
  "2d-matrix": "2D-matrix",
  "2d-masked-matrix": "2D-masked-matrix",
  "3d-symmetric-matrix": "3D-symmetric-matrix",
  "3d-masked-symmetric-matrix": "3D-masked-symmetric-matrix",
  "3d-matrix": "3D-matrix",
  "3d-masked-matrix": "3D-masked-matrix",
  "???": null,
  none: null
};
var systemEndianness = function() {
  var buf = new ArrayBuffer(4), intArr = new Uint32Array(buf), byteArr = new Uint8Array(buf);
  intArr[0] = 16909060;
  if (byteArr[0] == 1 && byteArr[1] == 2 && byteArr[2] == 3 && byteArr[3] == 4) {
    return "big";
  } else if (byteArr[0] == 4 && byteArr[1] == 3 && byteArr[2] == 2 && byteArr[3] == 1) {
    return "little";
  }
  console.warn("Unrecognized system endianness!");
  return;
}();
var whitespaceDataValueListSeparatorRE = /[ \t\n\r\v\f]+/;

// studio/src/shared/MPR/loaders/Texture3DLoader.ts
import * as fflate from "fflate";

// studio/src/utils/materials/SliceMaterial.ts
import {DoubleSide, Matrix4 as Matrix42, Vector3 as Vector314} from "three";

// studio/src/utils/shaders/mpr/slice.vs.glsl
var slice_vs_default = "#version 300 es\nprecision mediump float;\n\nuniform mat4 modelMatrix;\nuniform mat4 projectionMatrix;\nuniform mat4 viewMatrix;\n\nuniform mat4 xyztoijk;\nin vec4 position;\nout vec3 ijk;\n\nvoid main() {\n    // Apply translation, rotation and scale\n    vec4 xyz = modelMatrix * position;\n    // Transform xyz in terms of ijk\n    ijk = (xyztoijk * xyz).xyz;\n    // Clip-space vertex position\n    gl_Position = projectionMatrix * viewMatrix * xyz;\n}\n";

// studio/src/utils/shaders/mpr/slice.fs.glsl
var slice_fs_default = "#version 300 es\nprecision highp float;\n\nuniform mediump sampler3D volume;\nuniform float ww; // window width (WW)\nuniform float wmin; // window minimum = window level (WL) - half window width\nuniform float contrast; // https://github.com/evanw/glfx.js/blob/master/src/filters/adjust/brightnesscontrast.js\nuniform float brightness; // https://evanw.github.io/webgl-filter\nin vec3 ijk;\n\nlayout(location = 0) out vec4 fragColor;\n\nvoid main() {\n\t// Compare ijk coordinate with boundaries\n    vec3 inBounds = step(vec3(0.0), ijk) * step(ijk, vec3(1.0));\n\t// If any component is out-of-bounds then discard\n    if (inBounds.x * inBounds.y * inBounds.z < 1.0) {\n        discard;\n    }\n\t// get r component because texture have meaningful data in red channel only\n    float value = texture(volume, ijk).r;\n\t// windowing\n    float v = (value - wmin) / ww;\n\n    fragColor = vec4(v, v, v, 1.0);\n    fragColor.rgb += brightness;\n    if (contrast > 0.0) {\n        fragColor.rgb = (fragColor.rgb - 0.5) / (1.0 - contrast) + 0.5;\n    } else {\n        fragColor.rgb = (fragColor.rgb - 0.5) * (1.0 + contrast) + 0.5;\n    }\n}\n";

// studio/src/utils/materials/SliceMaterial.ts
class SliceMaterial extends SliceMaterialBase {
  constructor(data3DTexture, box, ww = 3200, wc = 1000) {
    const size = box.getSize(new Vector314);
    const scale = new Vector314(1, 1, 1).divide(size);
    const translate = box.min.clone().negate().divide(size);
    const xyz_to_ijk = new Matrix42().makeScale(scale.x, scale.y, scale.z).setPosition(translate);
    super({
      side: DoubleSide,
      vertexShader: slice_vs_default,
      fragmentShader: slice_fs_default,
      uniforms: {
        volume: { value: data3DTexture },
        xyztoijk: { value: xyz_to_ijk },
        ww: { value: ww },
        wmin: { value: wc - ww / 2 },
        contrast: { value: 0 },
        brightness: { value: 0 }
      }
    });
  }
}

// studio/src/utils/materials/SliceMaterialWebGL1.ts
import {AlphaFormat, ClampToEdgeWrapping, DataTexture, DoubleSide as DoubleSide2, FloatType, LinearFilter, MathUtils as MathUtils3, Matrix4 as Matrix43, UnsignedByteType, Vector2 as Vector23, Vector3 as Vector315} from "three";

// studio/src/utils/shaders/mpr/sliceWebGL1.vs.glsl
var sliceWebGL1_vs_default = "precision mediump float;\n\nuniform mat4 modelMatrix;\nuniform mat4 projectionMatrix;\nuniform mat4 viewMatrix;\n\nuniform mat4 xyztoijk;\nattribute vec4 position;\nvarying vec3 ijk;\n\nvoid main() {\n    // Apply translation, rotation and scale\n    vec4 xyz = modelMatrix * position;\n    // Transform xyz in terms of ijk\n    ijk = (xyztoijk * xyz).xyz;\n    // Clip-space vertex position\n    gl_Position = projectionMatrix * viewMatrix * xyz;\n}\n";

// studio/src/utils/shaders/mpr/sliceWebGL1.fs.glsl
var sliceWebGL1_fs_default = "precision highp float;\n\nuniform sampler2D volume;\n\nuniform float contrast; // https://github.com/evanw/glfx.js/blob/master/src/filters/adjust/brightnesscontrast.js\nuniform float brightness; // https://evanw.github.io/webgl-filter\nuniform vec3 texSize;\nuniform vec2 texSize2D;\n\nvarying vec3 ijk;\n\n//https://webgl2fundamentals.org/webgl/lessons/webgl-qna-how-to-simulate-a-3d-texture-in-webgl.html\nvec4 sampleAs3DTexture(sampler2D tex, vec3 texCoord) {\n    float sizeSide = texSize.x;\n    float sliceSize = 1.0 / texSize.z;// space of 1 slice\n    float slicePixelSize = sliceSize / sizeSide;// space of 1 pixel\n    float sliceInnerSize = slicePixelSize * (sizeSide - 1.0);// space of size pixels\n    float zSlice0 = min(floor(texCoord.z * texSize.z), texSize.z - 1.0);\n    float zSlice1 = min(zSlice0 + 1.0, texSize.z - 1.0);\n    float xOffset = slicePixelSize * 0.5 + texCoord.x * sliceInnerSize;\n    float s0 = xOffset + (zSlice0 * sliceSize);\n    float s1 = xOffset + (zSlice1 * sliceSize);\n    vec4 slice0Color = texture2D(tex, vec2(s0, texCoord.y));\n    vec4 slice1Color = texture2D(tex, vec2(s1, texCoord.y));\n    float zOffset = mod(texCoord.z * sizeSide, 1.0);\n    return mix(slice0Color, slice1Color, zOffset);\n}\n\nvec4 textureTrilinear(sampler2D tex, vec3 texCoord) {\n    // texCoord [0 - 1] - cube tile coordinates\n    // TODO use dFdx - we can use directions for ijk to better sample\n    float slices = texSize.z;\n    float sliceSizeX = texSize.x;\n    float sliceSizeY = texSize.y;\n\n    float xOffset = texCoord.x * sliceSizeX; //(0, 1, 2, 3, 4, ..., texSize.x)\n    float zSliceNumber = texCoord.z * slices; //(0, 1, 2, 3, 4, ..., texSize.z)\n    float zMin = floor(zSliceNumber);\n    float zMax = ceil(zSliceNumber);\n\n    float offsetXminZ = zMin * sliceSizeX;  //(0, 1, 2, 3, 4, ...)\n    float offsetXmaxZ = zMax * sliceSizeX;\n\n    float texUminZ = (floor(texCoord.z * (texSize.z - 1.0)) + texCoord.x) / texSize.z;\n    float texUmaxZ = (ceil(texCoord.z * (texSize.z - 1.0)) + texCoord.x) / texSize.z;\n    float texV = texCoord.y;\n    float texZ = texCoord.z;\n\n    vec4 colorMinZ = texture2D(tex, vec2(texUminZ, texV));\n    vec4 colorMaxZ = texture2D(tex, vec2(texUmaxZ, texV));\n\n    vec4 color = mix(colorMinZ, colorMaxZ, zMin / slices);\n    return color;\n}\n\nvoid main() {\n\t// Compare ijk coordinate with boundaries\n    vec3 inBounds = step(vec3(0.0), ijk) * step(ijk, vec3(1.0));\n\t// If any component is out-of-bounds then discard\n    if (inBounds.x * inBounds.y * inBounds.z < 1.0) {\n        discard;\n    }\n\t\n    vec4 texVal = sampleAs3DTexture(volume, ijk);\n#ifdef useFloat\n    float value = texVal.r; // get r component because texture have meaningful data in red channel only\n#else\n    float value = texVal.a;\n#endif\n    float v = value;\n\n    vec4 fragColor = vec4(v, v, v, 1.0);\n    fragColor.rgb += brightness;\n    if (contrast > 0.0) {\n        fragColor.rgb = (fragColor.rgb - 0.5) / (1.0 - contrast) + 0.5;\n    } else {\n        fragColor.rgb = (fragColor.rgb - 0.5) * (1.0 + contrast) + 0.5;\n    }\n    gl_FragColor = fragColor;\n}\n";

// studio/src/utils/materials/SliceMaterialWebGL1.ts
class SliceMaterialWebGL1 extends SliceMaterialBase {
  constructor(nrrd, box, ww = 3200, wl = 1000) {
    const size = box.getSize(new Vector315);
    const scale = new Vector315(1, 1, 1).divide(size);
    const translate = box.min.clone().negate().divide(size);
    const xyz_to_ijk = new Matrix43().makeScale(scale.x, scale.y, scale.z).setPosition(translate);
    const sizeX = nrrd.sizes[0], sizeY = nrrd.sizes[1], sizeZ = nrrd.sizes[2];
    const texSize = new Vector315(sizeX, sizeY, sizeZ);
    const wmin = wl - ww / 2;
    const useFloat = false;
    let texture;
    const zFramesByX = true;
    let pixelSize;
    if (useFloat) {
      pixelSize = 4;
      if (zFramesByX)
        texture = new DataTexture(new Float32Array(texSize.x * texSize.y * texSize.z * pixelSize), texSize.x * texSize.z, texSize.y);
      else
        texture = new DataTexture(new Float32Array(texSize.x * texSize.y * texSize.z * pixelSize), texSize.x, texSize.y * texSize.z);
      texture.type = FloatType;
    } else {
      pixelSize = 1;
      if (zFramesByX)
        texture = new DataTexture(new Uint8Array(texSize.x * texSize.y * texSize.z * pixelSize), texSize.x * texSize.z, texSize.y);
      else
        texture = new DataTexture(new Uint8Array(texSize.x * texSize.y * texSize.z * pixelSize), texSize.x, texSize.y * texSize.z);
      texture.format = AlphaFormat;
      texture.type = UnsignedByteType;
    }
    texture.wrapT = texture.wrapS = ClampToEdgeWrapping;
    texture.minFilter = texture.magFilter = LinearFilter;
    texture.needsUpdate = true;
    const pixelSizeSrc = 1;
    const nrrdData = nrrd.data;
    const data = useFloat ? texture.source.data.data : texture.source.data.data;
    if (zFramesByX) {
      for (let z = 0;z < texSize.z; z++) {
        for (let by = 0;by < texSize.y; by++) {
          for (let bx = 0;bx < texSize.x; bx++) {
            const posD = (z * texSize.x + by * texSize.x * texSize.z + bx) * pixelSize;
            const posS = (z * texSize.x * texSize.y + by * texSize.x + bx) * pixelSizeSrc;
            let valSrc = nrrdData[posS + 0];
            valSrc = (valSrc - wmin) / ww;
            if (useFloat)
              data[posD + 0] = valSrc;
            else
              data[posD + 0] = MathUtils3.clamp(valSrc * 255, 0, 255);
            if (pixelSize === 4)
              data[posD + 3] = 1;
          }
        }
      }
    } else {
      for (let bc = 0, bcl = nrrdData.length;bc < bcl; bc++) {
        const posD = bc * pixelSize;
        let valSrc = nrrdData[bc];
        valSrc = (valSrc - wmin) / ww;
        if (useFloat)
          data[posD + 0] = valSrc;
        else
          data[posD + 0] = MathUtils3.clamp(valSrc * 255, 0, 255);
        if (pixelSize === 4)
          data[posD + 3] = 1;
      }
    }
    super({
      side: DoubleSide2,
      vertexShader: sliceWebGL1_vs_default,
      fragmentShader: sliceWebGL1_fs_default,
      uniforms: {
        volume: { value: texture },
        xyztoijk: { value: xyz_to_ijk },
        contrast: { value: 0 },
        brightness: { value: 0 },
        texSize: { value: texSize },
        texSize2D: { value: new Vector23(texture.source.data.width, texture.source.data.height) }
      },
      defines: {
        useFloat
      }
    });
  }
}

// studio/src/utils/Services.ts
class Services {
  static services = new Map;
  static register(key, serviceInstance) {
    this.services.set(key, serviceInstance);
  }
  static get(key) {
    if (!this.services.has(key)) {
      throw new Error(`Service "${key}" not registered`);
    }
    return this.services.get(key);
  }
}

// studio/src/shared/MPR/loaders/Texture3DLoader.ts
var MAX_ERROR_AMOUNT = 5;
var RETRY_FETCH_TIMEOUT = 1000;
var TEXTURE3DLOADER_NODES_ERROR_LOGS = localStorage.getItem("TEXTURE3DLOADER_NODES_ERROR_LOGS") === "true";

class Texture3DLoader {
  onReady = [];
  bvh;
  _isReady = false;
  leafNodes = [];
  errors = [];
  loadingNodes = [];
  loadingNodesQueue = [];
  queueSize = 100;
  colorProfile;
  get isReady() {
    return this._isReady;
  }
  filesFetcher;
  constructor() {
  }
  destroy() {
    this._isReady = false;
    this.onReady.splice(0);
    this.bvh = undefined;
    this.leafNodes.splice(0);
    this.errors.splice(0);
    this.loadingNodes.splice(0);
    this.loadingNodesQueue.splice(0);
  }
  static convertInboundTilesToInternalTiles(tiles) {
    return tiles.map((tile, i) => ({
      box: tile.box,
      data: {
        fileId: tile.fileId,
        requesters: [],
        errors: [],
        index: i
      }
    }));
  }
  async start(tiles, grandBoundingBox, colorProfile) {
    this.filesFetcher = Services.get("fileFetcherService");
    this.colorProfile = colorProfile;
    const internalTiles = Texture3DLoader.convertInboundTilesToInternalTiles(tiles);
    this.bvh = new NodeBVH(grandBoundingBox, 0);
    this.buildBVHForNode(internalTiles, this.bvh);
    if (this.errors.length > 0) {
      console.warn("[buildBVH] errors=", this);
      console.dir(this.errors);
    }
    this._isReady = true;
    this.onReady.forEach((callback) => callback());
    this.onReady.length = 0;
  }
  async fetchTexture(node) {
    const tile = node.data;
    if (!tile)
      return;
    if (tile.controller?.signal.aborted) {
      this.onNodeAborted(node);
      return;
    }
    try {
      const buffer = await this.filesFetcher.getFileByIDAsBinary(tile.fileId, tile.controller?.signal);
      const nrrd = parseNRRD(buffer, fflate);
      const isWebGL2 = App2.Instance().view.getRenderer().capabilities.isWebGL2;
      if (isWebGL2) {
        const texture = new Data3DTexture2(new Float32Array(nrrd.data), nrrd.sizes[0], nrrd.sizes[1], nrrd.sizes[2]);
        texture.internalFormat = "R16F";
        texture.format = RedFormat;
        texture.type = FloatType2;
        texture.minFilter = LinearFilter2;
        texture.magFilter = LinearFilter2;
        texture.unpackAlignment = 1;
        texture.needsUpdate = true;
        tile.material = new SliceMaterial(texture, node.box, this.colorProfile.ww, this.colorProfile.wc);
      } else {
        tile.material = new SliceMaterialWebGL1(nrrd, node.box, this.colorProfile.ww, this.colorProfile.wc);
      }
      this.onNodeLoaded(node);
    } catch (error) {
      if (error.name === "AbortError") {
        this.onNodeAborted(node);
      } else {
        this.onNodeError(node, error);
      }
    }
  }
  async onNodeError(node, error) {
    const tile = node.data;
    if (!tile)
      return;
    if (TEXTURE3DLOADER_NODES_ERROR_LOGS) {
      console.error("[Texture3DLoader] Node Error", {
        error,
        index: tile.index,
        node
      });
    }
    tile.errors.push(error);
    if (tile.errors.length < MAX_ERROR_AMOUNT) {
      const timeout = setTimeout(() => {
        this.fetchTexture(node);
      }, RETRY_FETCH_TIMEOUT);
      tile.controller?.signal.addEventListener("abort", (abortEvent) => {
        clearTimeout(timeout);
      });
    } else {
      this.removeLoadingNode(node, "onNodeError");
      tile.status = undefined;
      tile.requesters.forEach((requester) => requester.onNodeError(node));
      tile.requesters.splice(0);
      for (let node2 of this.loadingNodes) {
        if (this.loadingNodesQueue.length < this.queueSize) {
          if (!this.loadingNodesQueue.includes(node2)) {
            this.loadingNodesQueue.push(node2);
            this.fetchTexture(node2);
          }
        } else
          break;
      }
    }
  }
  async onNodeAborted(node) {
    const tile = node.data;
    if (!tile)
      return;
  }
  removeLoadingNode(node, str) {
    let nodeIndex = this.loadingNodes.indexOf(node);
    if (nodeIndex !== -1) {
      this.loadingNodes.splice(nodeIndex, 1);
    }
    nodeIndex = this.loadingNodesQueue.indexOf(node);
    if (nodeIndex !== -1) {
      this.loadingNodesQueue.splice(nodeIndex, 1);
    }
  }
  onNodeLoaded(node) {
    if (!node.data)
      return;
    this.removeLoadingNode(node, "onNodeLoaded");
    node.data.status = "fetched";
    node.data.requesters.forEach((requester) => requester.onNodeLoaded(node));
    node.data.requesters.splice(0);
    for (let node2 of this.loadingNodes) {
      if (this.loadingNodesQueue.length < this.queueSize) {
        if (!this.loadingNodesQueue.includes(node2)) {
          this.loadingNodesQueue.push(node2);
          this.fetchTexture(node2);
        }
      } else
        break;
    }
    if (this.loadingNodes.length === 0 && this.loadingNodesQueue.length === 0) {
      this.waitersLoadingFinish.forEach((waiter) => waiter.resolve(waiter.key));
      this.waitersLoadingFinish.splice(0);
    }
  }
  waitersLoadingFinish = [];
  waitLoadingFinish(key) {
    return new Promise((resolve, reject) => {
      if (this.loadingNodes.length === 0 && this.loadingNodesQueue.length === 0) {
        resolve(key);
        return;
      }
      this.waitersLoadingFinish.push({ key, resolve });
    });
  }
  setNodesRequests(nodesToAdd, nodesToRemove, requester) {
    if (!this._isReady) {
      console.warn("no isReady=", requester.name);
      return;
    }
    const nodesToLoad = nodesToAdd.filter((n) => (!n.data?.status || n.data?.status === "fetching") && !n.data?.material);
    const nodesToAbort = this.loadingNodes.filter((n) => nodesToRemove.includes(n));
    const nodesAborted = [];
    nodesToAbort.forEach((n) => {
      if (!n.data)
        return;
      const rIndex = n.data.requesters.indexOf(requester);
      if (rIndex !== -1) {
        n.data.requesters.splice(rIndex, 1);
        if (n.data.requesters.length === 0) {
          n.data.controller?.abort();
          this.removeLoadingNode(n, "abort");
          n.data.status = undefined;
          nodesAborted.push(n);
        }
      }
    });
    if (nodesToLoad.length) {
      const nodesToFetch = [];
      for (let node of nodesToLoad) {
        const tile = node.data;
        if (!tile)
          continue;
        if (!tile.requesters.includes(requester))
          tile.requesters.push(requester);
        else
          console.warn("node is already requested by this requester=", node, requester, this);
        if (!(!tile.status && !tile.material))
          continue;
        tile.controller = new AbortController;
        if (!this.loadingNodes.includes(node))
          this.loadingNodes.push(node);
        else
          console.warn("node is already in loadingNodes=", node, this);
        tile.status = "fetching";
        nodesToFetch.push(node);
      }
      for (let node of nodesToFetch) {
        if (this.loadingNodesQueue.length < this.queueSize) {
          if (!this.loadingNodesQueue.includes(node)) {
            this.loadingNodesQueue.push(node);
            this.fetchTexture(node);
          } else
            console.warn("node is already in loadingNodesQueue=", node, this);
        } else
          break;
      }
    }
  }
  findTheLongestDimensionName(boxDimensions) {
    const { x, y, z } = boxDimensions;
    if (x >= y && x >= z)
      return "x";
    if (y >= x && y >= z)
      return "y";
    return "z";
  }
  buildBVHForNode(tiles, node) {
    if (!tiles.length)
      return;
    if (tiles.length === 1) {
      node.data = { ...tiles[0].data };
      this.leafNodes.push(node);
      return;
    }
    const data = {
      left: { boxes: [], expandBox: new Box35 },
      right: { boxes: [], expandBox: new Box35 }
    };
    const vector = new Vector316;
    const longestDimensionName = this.findTheLongestDimensionName(node.box.getSize(vector));
    const centerOfTheLongestDimension = node.box.getCenter(vector)[longestDimensionName];
    for (let i = 0, l = tiles.length;i < l; i++) {
      const tileBox = tiles[i].box;
      let side = tileBox.getCenter(vector)[longestDimensionName] < centerOfTheLongestDimension ? "left" : "right";
      data[side].boxes.push(tiles[i]);
      data[side].expandBox.expandByPoint(tileBox.min);
      data[side].expandBox.expandByPoint(tileBox.max);
    }
    if (!data.left.boxes.length && data.right.boxes.length) {
      data.left.boxes.push(data.right.boxes.pop());
      this.errors.push("Possibly incorrect data");
    } else if (!data.right.boxes.length && data.left.boxes.length) {
      data.right.boxes.push(data.left.boxes.pop());
      this.errors.push("Possibly incorrect data");
    }
    node.left = new NodeBVH(data.left.expandBox, node.depth + 1);
    node.right = new NodeBVH(data.right.expandBox, node.depth + 1);
    if (data.left.boxes.length > 1) {
      this.buildBVHForNode(data.left.boxes, node.left);
    } else {
      node.left.data = { ...data.left.boxes[0].data };
      this.leafNodes.push(node.left);
    }
    if (data.right.boxes.length > 1) {
      this.buildBVHForNode(data.right.boxes, node.right);
    } else {
      node.right.data = { ...data.right.boxes[0].data };
      this.leafNodes.push(node.right);
    }
  }
  traverseBVH(condition, cbOk) {
    const bvh = this.bvh;
    if (!bvh) {
      console.warn("traverseBVH: no bvh=", this);
      return;
    }
    this._traverseBVH(condition, bvh, cbOk);
  }
  _traverseBVH(condition, bvh, cbOk) {
    if (condition(bvh)) {
      if (bvh.left) {
        this._traverseBVH(condition, bvh.left, cbOk);
      }
      if (bvh.right) {
        this._traverseBVH(condition, bvh.right, cbOk);
      }
      if (!bvh.left && !bvh.right) {
        cbOk(bvh);
      }
    }
  }
}

class NodeBVH {
  box;
  depth;
  left;
  right;
  data;
  constructor(box, depth) {
    this.box = box;
    this.depth = depth;
  }
}
var Texture3DLoader_default = new Texture3DLoader;

// studio/src/shared/MPR/utils/VolumeTileController.ts
var EPS = 0.01;
var triangle = new Triangle;
var v1 = new Vector317;
var v2 = new Vector317;
var v3 = new Vector317;
var _a = 0;
var _b = 0;
var _c = 0;
var noMaterial = new SliceMaterialBase;
var DEBUG_DRAW_NODES = localStorage.getItem("DEBUG_DRAW_NODES") === "true";
var DEBUG_DISABLE_MPR = localStorage.getItem("DEBUG_DISABLE_MPR") === "true";

class VolumeTileController {
  volumeSlice;
  nodes = [];
  planeGeometry;
  objects;
  constructor(volumeSlice) {
    this.volumeSlice = volumeSlice;
    this.planeGeometry = volumeSlice.planeGeometry;
    this.objects = new Map;
  }
  get name() {
    return this.volumeSlice.name;
  }
  contrast = 0;
  brightness = 0;
  setContrastBrightness(contrast, brightness) {
    this.contrast = contrast;
    this.brightness = brightness;
    this.objects.forEach((entity) => {
      if (entity.object3d.material instanceof SliceMaterialBase) {
        this.applyContrastBrightness(entity.object3d.material);
      }
    });
  }
  getData() {
    if (!Texture3DLoader_default.isReady) {
      Texture3DLoader_default.onReady.push(() => {
        this.updateActiveNodes();
      });
    } else {
      this.updateActiveNodes();
    }
  }
  getActiveNodes() {
    if (!Texture3DLoader_default.bvh) {
      return [];
    }
    const mprLayer = App2.Instance().view.getLayer("mpr");
    if (!mprLayer) {
      throw new Error("[VolumeSlice] App must have 'mpr' layer for MPR to work");
    }
    const objectsMPR = mprLayer.getObjects();
    if (!objectsMPR) {
      return [];
    }
    const otherSlices = objectsMPR.filter((object) => object instanceof VolumeSliceAbstract2 && object.name !== this.volumeSlice.name);
    const resultsData = [];
    const box = new Box36;
    Texture3DLoader_default.traverseBVH((node) => {
      box.copy(node.box).expandByScalar(EPS);
      if (!box.intersectsPlane(this.volumeSlice.plane))
        return false;
      const position = this.planeGeometry.getAttribute("position").clone().applyMatrix4(this.volumeSlice.object3d.matrix);
      for (let i = 0, il = this.planeGeometry.index.count;i < il; i += 3) {
        _a = this.planeGeometry.index.array[i];
        _b = this.planeGeometry.index.array[i + 1];
        _c = this.planeGeometry.index.array[i + 2];
        triangle.set(v1.set(position.getX(_a), position.getY(_a), position.getZ(_a)), v2.set(position.getX(_b), position.getY(_b), position.getZ(_b)), v3.set(position.getX(_c), position.getY(_c), position.getZ(_c)));
        if (box.intersectsTriangle(triangle)) {
          return true;
        }
      }
      return false;
    }, (n) => {
      const dist = otherSlices.reduce((accumulator, currentValue) => accumulator + Math.abs(currentValue.plane.distanceToPoint(n.box.getCenter(new Vector317))), 0);
      resultsData.push({ n, dist });
    });
    resultsData.sort((a, b) => a.dist - b.dist);
    const results = resultsData.map((rd) => rd.n);
    return results;
  }
  onProgressStart = (progressEvent) => {
  };
  onProgressFinish = (viewport) => {
  };
  onVolumeTileCreated = (tile) => {
  };
  applyContrastBrightness = (material) => {
  };
  nodesDispatched = [];
  progressEvent;
  progressStart() {
    const viewport = App2.Instance().view.getViewport(this.volumeSlice.name);
    if (!viewport) {
      return;
    }
    if (this.progressEvent)
      this.progressFinish();
    this.nodesDispatched = this.nodes.filter((n) => n.data?.material);
    this.progressEvent = { type: "showSpinner", viewports: [viewport], onProgress: () => {
    } };
    this.onProgressStart(this.progressEvent);
    this.progressUpdate();
  }
  progressFinish() {
    const viewport = App2.Instance().view.getViewport(this.volumeSlice.name);
    if (!viewport) {
      return;
    }
    if (!this.progressEvent) {
      return;
    }
    this.onProgressFinish(viewport);
    this.progressEvent = undefined;
  }
  progressUpdate() {
    if (!this.progressEvent) {
      return;
    }
    const progress = this.nodes.length === 0 ? 0 : Math.round(this.nodesDispatched.length / this.nodes.length * 100);
    this.progressEvent.onProgress?.(progress);
    if (this.nodesDispatched.length === this.nodes.length)
      this.progressFinish();
  }
  progressNode(node) {
    const nIndex = this.nodes.indexOf(node);
    if (nIndex !== -1) {
      if (!this.nodesDispatched.includes(node)) {
        this.nodesDispatched.push(node);
      }
      this.progressUpdate();
    }
  }
  updateActiveNodes() {
    if (DEBUG_DISABLE_MPR)
      return;
    const nodesNew = this.getActiveNodes();
    const nodesToAdd = nodesNew.filter((n) => !this.nodes.includes(n));
    const nodesToRemove = this.nodes.filter((n) => !nodesNew.includes(n));
    this.nodes = nodesNew;
    if (!(nodesToAdd.length || nodesToRemove.length)) {
      return;
    }
    this.progressStart();
    nodesToRemove.forEach((n) => {
      const tile = this.objects.get(n);
      if (tile) {
        this.volumeSlice.delete(tile);
        this.objects.delete(n);
      }
      const material = n.data?.material;
      if (material) {
        material.destroy(this);
      }
    });
    nodesToAdd.forEach((n) => {
      let tile = this.objects.get(n);
      if (!tile) {
        tile = new VolumeTile(new Mesh(this.planeGeometry, noMaterial), n);
        this.objects.set(n, tile);
        this.volumeSlice.add(tile);
        this.onVolumeTileCreated(tile);
      }
      const material = n.data?.material;
      if (material) {
        tile.object3d.material = material;
        if (material instanceof SliceMaterialBase) {
          material.increaseUsers(this);
          this.applyContrastBrightness(material);
        }
      } else {
        tile.object3d.visible = false;
      }
    });
    if (DEBUG_DRAW_NODES) {
      const color = new Color4((() => {
        if (this.volumeSlice.name === "Axial")
          return "red";
        if (this.volumeSlice.name === "Coronal")
          return "green";
        if (this.volumeSlice.name === "Sagittal")
          return "blue";
        return "white";
      })());
      const container = DebugUtils.getContainer(`DEBUG_DRAW_NODES_${this.volumeSlice.name}`);
      container.clear();
      nodesNew.forEach((n) => {
        const boxHelper = new Box3Helper(n.box, color);
        container.add(boxHelper);
      });
    }
    Texture3DLoader_default.setNodesRequests(nodesToAdd, nodesToRemove, this);
    App2.Instance().dispatchEvent({ type: "render" });
  }
  onNodeLoaded(node) {
    const tile = this.objects.get(node);
    if (tile) {
      const material = node.data?.material;
      if (material) {
        if (!tile.object3d.visible) {
          if (material instanceof SliceMaterialBase)
            material.increaseUsers(this);
          tile.object3d.material = material;
          tile.object3d.visible = true;
          this.progressNode(node);
        }
        if (material instanceof SliceMaterialBase) {
          this.applyContrastBrightness(material);
        }
      }
    } else {
      const material = node.data?.material;
      if (material) {
        material.destroy(this);
      }
    }
    App2.Instance().dispatchEvent({ type: "render" });
  }
  onNodeError(node) {
    this.progressNode(node);
  }
}

// studio/src/shared/MPR/entities/PlaneEntity.ts
import {Vector3 as Vector318} from "three";
import {Object3D as Object3D3, Plane as Plane2} from "three";

// studio/src/utils/findAll.ts
var findAll = (layerName, constructor) => {
  const layer = App2.Instance().view.getLayer(layerName);
  if (!layer) {
    throw new Error(`[FindAll] Layer ${layerName} not found`);
  }
  return layer.getObjects().filter((object) => object instanceof constructor);
};

// studio/src/utils/find.ts
var find = (name, layerName, constructor) => {
  const layer = App2.Instance().view.getLayer(layerName);
  if (!layer) {
    throw new Error(`[Find] Layer ${layerName} not found`);
  }
  const object = layer.getObjectByName(name);
  return object instanceof constructor ? object : undefined;
};

// studio/src/shared/MPR/entities/PlaneEntity.ts
class PlaneEntity extends Entity {
  plane;
  static findAll = () => {
    return findAll("mpr", PlaneEntity);
  };
  static find = (name) => {
    return find(name, "mpr", PlaneEntity);
  };
  constructor() {
    super(new Object3D3);
    this.plane = new Plane2(new Vector318(0, 0, 1));
    this.setComponent(RaycastChildrenOnly);
  }
  updatePlane() {
    this.object3d.updateMatrix();
    this.object3d.updateMatrixWorld(true);
    this.plane.normal.setFromMatrixColumn(this.object3d.matrix, 2);
    this.plane.constant = -this.object3d.position.dot(this.plane.normal);
  }
  setSize(width, height) {
    this.object3d.scale.set(width, height, 1);
  }
}

// studio/src/shared/MPR/entities/VolumeSliceAbstract.ts
class VolumeSliceAbstract2 extends PlaneEntity {
  linkedCamera;
  planeGeometry;
  tileController;
  color;
  static findAll = () => {
    const layer = App2.Instance().view.getLayer("mpr");
    if (!layer) {
      throw new Error(`[FindAll] Layer mpr not found`);
    }
    return layer.getObjects().filter((object) => object instanceof VolumeSliceAbstract2);
  };
  static find = (name) => {
    const layer = App2.Instance().view.getLayer("mpr");
    if (!layer) {
      throw new Error(`[Find] Layer mpr not found`);
    }
    return layer.getObjectByName(name);
  };
  constructor(name, width = 500, height = 500, linkedCamera, color) {
    super();
    this.color = color;
    this.name = name;
    this.plane = new Plane3(new Vector319(0, 0, 1));
    this.planeGeometry = new PlaneGeometry2(1, 1, 1, 1);
    this.object3d.frustumCulled = false;
    this.tileController = new VolumeTileController(this);
    this.setSize(width, height);
    this.linkedCamera = linkedCamera;
    this.tileController.applyContrastBrightness = (material) => {
      console.log("If you see this, it means that the tileController.applyContrastBrightness is not overridden");
    };
  }
  update() {
    this.updatePlane();
    this.tileController.getData();
  }
  setSize(width, height) {
    this.object3d.scale.set(width, height, 1);
    this.object3d.updateMatrix();
    this.tileController.getData();
  }
  fitToCamera() {
    this.linkedCamera.updateProjectionMatrix();
    const zoom = this.linkedCamera.zoom;
    const width = (this.linkedCamera.right - this.linkedCamera.left) / zoom;
    const height = (this.linkedCamera.top - this.linkedCamera.bottom) / zoom;
    this.setSize(width, height);
  }
}

// studio/src/core/components/SelectComponent.ts
class SelectComponent extends Component {
  selected;
  highlighter;
  constructor(entity, highlighter = new NoHighlighter(entity)) {
    super(entity);
    this.selected = false;
    this.highlighter = highlighter;
  }
  select() {
    this.selected = true;
    this.highlighter.highlight();
  }
  deselect() {
    this.selected = false;
    this.highlighter.unHighlight();
  }
  isSelected() {
    return this.selected;
  }
}

// studio/src/products/MPREmbedded/entities/VolumeSliceMPREmbedded.ts
class VolumeSliceMPREmbedded extends VolumeSliceAbstract2 {
  static findAll = () => {
    return findAll("mpr", VolumeSliceMPREmbedded);
  };
  static find = (name) => {
    return find(name, "mpr", VolumeSliceMPREmbedded);
  };
  constructor(name, width = 500, height = 500, linkedCamera, color) {
    super(name, width, height, linkedCamera, color);
    this.setComponent(SelectComponent);
    this.tileController.onVolumeTileCreated = (tile) => {
      const viewportComponent = tile.getComponent(ViewportComponent);
      viewportComponent.addViewport("Main");
    };
    this.tileController.applyContrastBrightness = (material) => {
      material.setWWWC(MPR2Storage_default.colorProfile.ww, MPR2Storage_default.colorProfile.wc);
      material.uniforms.contrast.value = MPR2Storage_default.contrast;
      material.uniforms.brightness.value = MPR2Storage_default.brightness;
    };
  }
  syncAllWithMPR2Storage() {
    this.tileController.setContrastBrightness(MPR2Storage_default.contrast, MPR2Storage_default.brightness);
  }
}

// studio/src/products/MPREmbedded/tools/MprVolumeSliceSpawner.ts
var colors = {
  Axial: new Color6("yellow"),
  Sagittal: new Color6("cyan"),
  Coronal: new Color6("magenta")
};

class MprVolumeSliceSpawner extends ViewportTool {
  slice;
  constructor(viewport) {
    super(viewport);
    this.onTransform = this.onTransform.bind(this);
    this.onResizeViewport = this.onResizeViewport.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
  }
  activate() {
    if (!this.slice)
      this.makeSlice();
    App2.Instance().addEventListener("transform", this.onTransform, 1);
    this.hostObject.addEventListener("resize", this.onResizeViewport, 5);
    App2.Instance().addEventListener("resize", this.onResizeViewport, 5);
    App2.Instance().mouseController.addListener("pointermove", this.onMouseMove, document);
  }
  deactivate() {
    if (this.slice)
      this.destroySlice();
    App2.Instance().removeEventListener("transform", this.onTransform);
    this.hostObject.removeEventListener("resize", this.onResizeViewport);
    App2.Instance().removeEventListener("resize", this.onResizeViewport);
    App2.Instance().mouseController.removeListener("pointermove", this.onMouseMove, document);
  }
  onResizeViewport() {
    this.slice?.fitToCamera();
  }
  onTransform(event) {
    if (this.slice && event.entities?.includes(this.slice)) {
      this.slice.update();
    }
  }
  cursorPoint = new Vector24;
  viewportBound = new Box2;
  onMouseMove(event) {
    this.cursorPoint.set(event.clientX, event.clientY);
    let viewportInsideNew = undefined;
    this.hostObject.view.viewports.forEach((viewport) => {
      this.viewportBound.min.set(viewport.rect.left, viewport.rect.top);
      this.viewportBound.max.set(viewport.rect.right, viewport.rect.bottom);
      if (this.viewportBound.containsPoint(this.cursorPoint)) {
        viewportInsideNew = viewport;
        return;
      }
    });
    this.viewportInside = viewportInsideNew;
  }
  _viewportInside;
  set viewportInside(value) {
    if (this._viewportInside === value)
      return;
    this._viewportInside = value;
  }
  makeSlice() {
    this.hostObject.camera.far = 1;
    this.hostObject.camera.near = -1;
    const color = colors[this.hostObject.name];
    const tag = this.hostObject.name;
    if (!(this.hostObject.camera instanceof OrthographicCamera3)) {
      throw new Error("[MprVolumeSliceSpawner] Camera is not an OrthographicCamera");
    }
    const DEFAULT_WIDTH_HEIGHT = 500;
    this.slice = new VolumeSliceMPREmbedded(tag, DEFAULT_WIDTH_HEIGHT, DEFAULT_WIDTH_HEIGHT, this.hostObject.camera, color);
    this.slice.name = this.hostObject.name;
    this.slice.update();
    this.hostObject.view.getLayer("mpr")?.add(this.slice);
  }
  destroySlice() {
    if (!this.slice) {
      console.warn("destroySlice: already destroyed=", this.hostObject.name, this);
      return;
    }
    this.hostObject.view.getLayer("mpr")?.delete(this.slice);
    this.slice.dispose();
    this.slice = undefined;
  }
}

// studio/src/core/tuple.ts
var tuple = (...args) => args;

// studio/src/shared/SharedConstants.ts
var MPRViewportNames = ["Coronal", "Sagittal", "Axial"];

// studio/src/shared/waitForApplicationReady.ts
var internalResolve;
var promisifiedReady = new Promise((resolve) => {
  internalResolve = resolve;
});
var resolveWaitForApplicationReady = () => {
  internalResolve();
};
var waitForApplicationReady = () => promisifiedReady;

// studio/src/products/MPREmbedded/tools/MprCrossLineSpawner.ts
import {Matrix4 as Matrix44} from "three";

// studio/src/shared/MPR/entities/CrossLineLegacy.ts
import {BufferAttribute as BufferAttribute2, BufferGeometry, DynamicDrawUsage, LineSegments as LineSegments2, RawShaderMaterial as RawShaderMaterial2} from "three";

// studio/src/utils/shaders/crossLine/frag.glsl
var frag_default = "precision mediump float;\n\nvarying vec4 v_color;\n\nvoid main() {\n\tgl_FragColor = v_color;\n}";

// studio/src/utils/shaders/crossLine/vert.glsl
var vert_default = "precision mediump float;\nprecision mediump int;\n\nvarying vec4 v_color;\nattribute vec3 color;\nattribute vec2 position;\n\nvoid main() {\n\tv_color = vec4(color, 1.0);\n\tgl_Position = vec4(position, -0.5, 1.0);\n}";

// studio/src/shared/components/Visibility.ts
class Visibility extends Component {
  isUser = true;
  show() {
    this.entity.object3d.visible = true;
    App2.Instance().dispatchEvent({ type: "visibility", entity: this.entity });
  }
  hide() {
    this.entity.object3d.visible = false;
    App2.Instance().dispatchEvent({ type: "visibility", entity: this.entity });
  }
  isVisible() {
    return this.entity.object3d.visible;
  }
  toggle() {
    this.isVisible() ? this.hide() : this.show();
  }
  setVisible(visible) {
    if (this.isVisible() !== visible) {
      if (visible)
        this.show();
      else
        this.hide();
    }
  }
}

// studio/src/shared/MPR/entities/CrossLineLegacy.ts
var CROSSLINE_LAYER_NAME = "mpr";

class CrossLineLegacy extends Entity {
  static findAll() {
    return App2.Instance().view.getLayer(CROSSLINE_LAYER_NAME)?.getObjects().filter((child) => child instanceof CrossLineLegacy);
  }
  visibility = new Visibility(this);
  constructor(name = "CrossLine") {
    const positionAttribute = new BufferAttribute2(new Float32Array(8).fill(0), 2);
    positionAttribute.setUsage(DynamicDrawUsage);
    const lineMaterial = new RawShaderMaterial2({
      vertexShader: vert_default,
      fragmentShader: frag_default,
      vertexColors: true
    });
    super(new LineSegments2(new BufferGeometry().setAttribute("position", positionAttribute).setAttribute("color", new BufferAttribute2(new Float32Array(12).fill(1), 3)), lineMaterial));
    this.setComponent(ViewportComponent);
    this.setComponent(Visibility, this.visibility);
    this.object3d.frustumCulled = false;
    this.object3d.renderOrder = 200;
    this.object3d.name = name;
  }
  toJson() {
    throw new Error("Method not implemented.");
  }
}

// studio/src/products/MPREmbedded/tools/MprCrossLineSpawner.ts
class MprCrossLineSpawner extends ViewportTool {
  objects;
  slice;
  line;
  inverseMatrix;
  constructor(viewport) {
    super(viewport);
    this.objects = [];
    this.inverseMatrix = new Matrix44;
    this.update = this.update.bind(this);
    this.onTransform = this.onTransform.bind(this);
    this.onTransformCamera = this.onTransformCamera.bind(this);
  }
  async activate() {
    await waitForApplicationReady();
    this.line = new CrossLineLegacy(this.hostObject.name + "-CrossLine");
    this.line.getComponent(ViewportComponent)?.addViewport(this.hostObject.name);
    App2.Instance().view.getLayer(CROSSLINE_LAYER_NAME)?.add(this.line);
    this.objects = VolumeSliceMPREmbedded.findAll().filter((slice) => slice.name !== this.hostObject.name);
    this.slice = VolumeSliceMPREmbedded.find(this.hostObject.name);
    if (this.slice && this.objects.length === 2) {
      this.update();
      this.setColor();
    }
    this.on("transform", this.onTransform);
    this.on("resize", this.update);
    this.on("transformCamera", this.onTransformCamera);
    this.hostObject.addEventListener("resize", this.update);
  }
  deactivate() {
    if (this.line) {
      App2.Instance().view.getLayer(CROSSLINE_LAYER_NAME)?.delete(this.line);
      this.line = undefined;
    }
    this.slice = undefined;
    this.objects.splice(0);
    this.hostObject.removeEventListener("resize", this.update);
  }
  onTransform(event) {
    if (this.slice && event.entities.some((entity) => entity instanceof PlaneEntity)) {
      this.update();
    }
  }
  onTransformCamera(event) {
    if (this.slice && event.viewport === this.hostObject) {
      this.update();
    }
  }
  update() {
    if (!(this.slice && this.objects.length === 2))
      return;
    this.inverseMatrix.copy(this.slice.object3d.matrix).invert();
    this.objects.forEach((obj, index) => {
      const plane = obj.plane.clone();
      plane.applyMatrix4(this.inverseMatrix);
      const result = [];
      const dist = -plane.constant;
      const EPS2 = 0.0000000001;
      if (Math.abs(plane.normal.y) < EPS2) {
        result.push(plane.normal.x * dist * 2, -1, plane.normal.x * dist * 2, 1);
      } else if (Math.abs(plane.normal.x) < EPS2) {
        result.push(-1, plane.normal.y * dist * 2, 1, plane.normal.y * dist * 2);
      } else {
        const k = -plane.normal.x / plane.normal.y;
        const point = plane.normal.clone().multiplyScalar(dist * 2);
        const b = point.y - k * point.x;
        result.push(-1, -k + b, 1, k + b);
      }
      if (this.line) {
        const positionBuffer = this.line.object3d.geometry.getAttribute("position");
        positionBuffer.array.set(result, index * 4);
        positionBuffer.needsUpdate = true;
      }
    });
  }
  setColor() {
    this.objects.forEach((slice, index) => {
      const color = slice.color;
      if (this.line) {
        const colorBuffer = this.line.object3d.geometry.getAttribute("color");
        colorBuffer.array.set([...color.toArray(), ...color.toArray()], index * 6);
      }
    });
  }
}

// studio/src/core/tools/Pan2D.ts
import {OrthographicCamera as OrthographicCamera4, Vector2 as Vector25, Vector3 as Vector320} from "three";
class Pan2D extends ViewportTool {
  panStart;
  panEnd;
  panDelta;
  panOffset;
  panType;
  vector;
  timer;
  constructor(viewport) {
    super(viewport);
    this.panStart = new Vector25;
    this.panEnd = new Vector25;
    this.panDelta = new Vector25;
    this.panOffset = new Vector320;
    this.vector = new Vector320;
    this.handleLeftMouseButtonDown = this.handleLeftMouseButtonDown.bind(this);
    this.handleMiddleMouseButtonDown = this.handleMiddleMouseButtonDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseMoveOnce = this.handleMouseMoveOnce.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.updatePanType = this.updatePanType.bind(this);
  }
  get cameraController() {
    return this.hostObject.cameraController;
  }
  get camera() {
    return this.hostObject.camera;
  }
  get cameraX() {
    return this.hostObject.cameraController.xDir;
  }
  get cameraY() {
    return this.hostObject.cameraController.yDir;
  }
  activate() {
    App2.Instance().mouseController.addListener("pointerdown", this.handleLeftMouseButtonDown, this.hostObject.getDomElement(), "left");
    App2.Instance().mouseController.addListener("pointerdown", this.handleMiddleMouseButtonDown, this.hostObject.getDomElement(), "middle");
    this.updatePanType();
    this.hostObject.addEventListener("cameraChanged", this.updatePanType);
  }
  deactivate() {
    App2.Instance().mouseController.removeListener("pointerdown", this.handleLeftMouseButtonDown, this.hostObject.getDomElement(), "left");
    App2.Instance().mouseController.removeListener("pointerdown", this.handleMiddleMouseButtonDown, this.hostObject.getDomElement(), "middle");
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMoveOnce, document);
    this.hostObject?.removeEventListener("cameraChanged", this.updatePanType);
  }
  updatePanType() {
    if (this.camera instanceof OrthographicCamera4) {
      this.panType = this.panForOrthographicCamera;
    } else {
      this.panType = this.panForPerspectiveCamera;
    }
  }
  pan(deltaX, deltaY) {
    this.panType(deltaX, deltaY);
    this.hostObject.camera.position.add(this.panOffset);
    this.panOffset.set(0, 0, 0);
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
  }
  panForOrthographicCamera(deltaX, deltaY) {
    const sizeRatio = this.cameraController.orthographicCameraHelper.getSizeRatio();
    this.panOffset.copy(this.cameraX).multiplyScalar(-(deltaX * sizeRatio) / this.camera.zoom);
    this.panOffset.add(this.vector.copy(this.cameraY).multiplyScalar(deltaY * sizeRatio / this.camera.zoom));
  }
  panForPerspectiveCamera(deltaX, deltaY) {
    const distanceFactor = this.cameraController.perspectiveCameraHelper.updateDistanceFactor();
    this.panOffset.copy(this.cameraX).multiplyScalar(-deltaX * distanceFactor);
    this.panOffset.add(this.vector.copy(this.cameraY).multiplyScalar(deltaY * distanceFactor));
  }
  handleMouseMoveOnce(event) {
    this.panStart.set(event.clientX, event.clientY);
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMoveOnce, document);
  }
  handleMouseDown(event) {
    event.preventDefault();
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "middle");
    this.panStart.set(event.clientX, event.clientY);
    App2.Instance().dispatchEvent({ type: "transformCameraStart", viewport: this.hostObject });
  }
  handleLeftMouseButtonDown(event) {
    if (!event.shiftKey && this.enable) {
      this.handleMouseDown(event);
    }
  }
  handleMiddleMouseButtonDown(event) {
    if (!event.shiftKey && this.enable) {
      this.handleMouseDown(event);
    }
  }
  handleMouseMove(event) {
    this.panEnd.set(event.clientX, event.clientY);
    this.panDelta.subVectors(this.panEnd, this.panStart);
    this.pan(this.panDelta.x, this.panDelta.y);
    this.panStart.copy(this.panEnd);
  }
  handleMouseUp(event) {
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "middle");
    this.panStart.set(event.clientX, event.clientY);
    App2.Instance().dispatchEvent({ type: "transformCameraEnd", viewport: this.hostObject });
  }
}

// studio/src/products/MPREmbedded/tools/MprPan2D.ts
class MPRPan2D extends Pan2D {
  slice;
  constructor(viewport) {
    super(viewport);
  }
  async activate() {
    await waitForApplicationReady();
    this.slice = VolumeSliceMPREmbedded.find(this.hostObject.name);
    super.activate();
  }
  deactivate() {
    this.slice = undefined;
    super.deactivate();
  }
  handleMouseDown(event) {
    super.handleMouseDown(event);
  }
  handleMouseUp(event) {
    super.handleMouseUp(event);
  }
  pan(deltaX, deltaY) {
    this.panType(deltaX, deltaY);
    this.hostObject.camera.position.add(this.panOffset);
    if (this.slice) {
      this.slice?.object3d.position.add(this.panOffset);
    }
    this.panOffset.set(0, 0, 0);
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
  }
  handleLeftMouseButtonDown(event) {
    if (!event.shiftKey && (event.ctrlKey || event.metaKey) && this.enable) {
      this.handleMouseDown(event);
    }
  }
}

class MPRPan2DAsMPR1 extends MPRPan2D {
  handleLeftMouseButtonDown(event) {
    if (this.enable) {
      this.handleMouseDown(event);
    }
  }
}

// studio/src/products/MPREmbedded/tools/MprZoom2D.ts
import {Vector2 as Vector26} from "three";

// studio/src/core/tools/Zoom2D.ts
import {OrthographicCamera as OrthographicCamera5, Vector3 as Vector321} from "three";
class Zoom2D extends ViewportTool {
  zoomSpeed;
  minZoom;
  maxZoom;
  zoom;
  vector;
  constructor(viewport) {
    super(viewport);
    this.zoomSpeed = Math.pow(0.95, 0.4);
    this.maxZoom = Infinity;
    this.minZoom = 0;
    this.vector = new Vector321;
    this.handleMouseWheel = this.handleMouseWheel.bind(this);
    this.updateZoomType = this.updateZoomType.bind(this);
  }
  activate() {
    App2.Instance().mouseController.addListener("wheel", this.handleMouseWheel, this.hostObject.domElement);
    this.hostObject.addEventListener("cameraChanged", this.updateZoomType);
    this.updateZoomType();
  }
  deactivate() {
    App2.Instance().mouseController.removeListener("wheel", this.handleMouseWheel, this.hostObject.domElement);
    this.hostObject.removeEventListener("cameraChanged", this.updateZoomType);
  }
  get camera() {
    return this.hostObject.camera;
  }
  handleMouseWheel(event) {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoom(this.zoomSpeed);
    } else if (event.deltaY > 0) {
      this.zoom(1 / this.zoomSpeed);
    }
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
  }
  updateZoomType() {
    if (this.camera instanceof OrthographicCamera5) {
      this.zoom = this.zoomForOrthographicCamera;
    } else {
      this.zoom = this.zoomForPerspectiveCamera;
    }
  }
  zoomForOrthographicCamera(scale) {
    this.camera.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.camera.zoom / scale));
    this.camera.updateProjectionMatrix();
  }
  zoomForPerspectiveCamera(scale) {
    const pp = this.hostObject.cameraController.getPivotPoint();
    this.vector.copy(this.camera.position);
    this.camera.position.copy(pp).add(this.vector.sub(pp).multiplyScalar(scale));
    this.hostObject.cameraController.orthographicCameraHelper.updateDistanceFactor();
  }
}

// studio/src/products/MPREmbedded/config/MPREmbeddedConfig.ts
var MPREmbeddedInstance = App2.Instance();

// studio/src/products/MPREmbedded/tools/MprZoom2D.ts
class MprZoom2D extends Zoom2D {
  slice;
  constructor(viewport) {
    super(viewport);
    this.updateSlice = this.updateSlice.bind(this);
  }
  async activate() {
    await waitForApplicationReady();
    this.slice = VolumeSliceMPREmbedded.find(this.hostObject.name);
    this.updateSlice();
    App2.Instance().addEventListener("resize", this.updateSlice, 5);
    super.activate();
  }
  deactivate() {
    this.slice = undefined;
    super.deactivate();
  }
  handleMouseWheel(event) {
    event.preventDefault();
    if (event.ctrlKey || event.metaKey)
      return;
    super.handleMouseWheel(event);
  }
  zoomForOrthographicCamera(scale) {
    this.camera.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.camera.zoom / scale));
    this.updateSlice();
  }
  updateSlice() {
    this.slice?.fitToCamera();
  }
}

class MprZoom2DByLeftMBWithSync extends MprZoom2D {
  dollyStart;
  dollyEnd;
  dollyDelta;
  syncZoom = false;
  volumeSlicesForSync = [];
  constructor(viewport) {
    super(viewport);
    this.dollyStart = new Vector26;
    this.dollyEnd = new Vector26;
    this.dollyDelta = new Vector26;
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }
  async activate() {
    await waitForApplicationReady();
    this.slice = VolumeSliceMPREmbedded.find(this.hostObject.name);
    this.updateSlice();
    const unsubscribe = [
      MPREmbeddedInstance.addEventListener("enableZoomSync", () => this.syncZoom = true),
      MPREmbeddedInstance.addEventListener("disableZoomSync", () => this.syncZoom = false),
      App2.Instance().mouseController.addListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left"),
      this.hostObject.addEventListener("cameraChanged", this.updateZoomType)
    ];
    this.updateZoomType();
    this.volumeSlicesForSync = VolumeSliceMPREmbedded.findAll().filter((slice) => slice.linkedCamera !== this.camera);
    this.deactivate = () => {
      unsubscribe.forEach((unsubscribe2) => unsubscribe2());
    };
  }
  handleMouseDown(event) {
    if (event.ctrlKey || event.metaKey)
      return;
    this.dollyStart.set(event.clientX, event.clientY);
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
  }
  handleMouseMove(event) {
    this.dollyEnd.set(event.clientX, event.clientY);
    this.dollyDelta.subVectors(this.dollyEnd, this.dollyStart);
    if (this.dollyDelta.y < 0) {
      this.zoom(this.zoomSpeed);
    } else if (this.dollyDelta.y > 0) {
      this.zoom(1 / this.zoomSpeed);
    }
    this.dollyStart.copy(this.dollyEnd);
    App2.Instance().dispatchEvent({ type: "render" });
  }
  zoomForOrthographicCamera(scale) {
    this.camera.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.camera.zoom / scale));
    this.updateSlice();
    if (this.syncZoom) {
      this.volumeSlicesForSync.forEach((slice) => {
        slice.linkedCamera.zoom = this.camera.zoom;
        slice.linkedCamera.updateProjectionMatrix();
        slice.fitToCamera();
      });
    }
  }
  handleMouseUp() {
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
  }
}

// studio/src/products/MPREmbedded/tools/MprRotateSlice2.ts
import {Vector2 as Vector28, Vector3 as Vector323, Matrix4 as Matrix46} from "three";

// studio/src/utils/math/math.ts
import {Line3, Matrix3, Matrix4 as Matrix45, Plane as Plane4, Quaternion as Quaternion4, ShapeUtils, Vector3 as Vector322} from "three";
var getAngle2D = (v12, v22) => {
  return Math.atan2(v12.x * v22.y - v12.y * v22.x, v12.x * v22.x + v12.y * v22.y);
};
var makeRotationAroundPointAndAxis = (() => {
  const tmpMatrix = new Matrix45;
  const tmpQuaternion = new Quaternion4;
  return function makeRotationAroundPointAndAxis(angle, point, axis, target) {
    return target.makeTranslation(point.x, point.y, point.z).multiply(tmpMatrix.makeRotationFromQuaternion(tmpQuaternion.setFromAxisAngle(axis, angle))).multiply(tmpMatrix.makeTranslation(-point.x, -point.y, -point.z));
  };
})();

// studio/src/products/MPREmbedded/tools/MprRotateSlice2.ts
class MprRotateSlice2 extends ViewportTool {
  objects;
  linkedCameras;
  rotateStart;
  current;
  angle;
  centerWorldSpace;
  centerScreenSpace;
  centerCameraSpace;
  transformMatrix;
  constructor(viewport) {
    super(viewport);
    this.rotateStart = new Vector28;
    this.current = new Vector28;
    this.angle = 0;
    this.objects = [];
    this.centerWorldSpace = new Vector323;
    this.centerScreenSpace = new Vector28;
    this.centerCameraSpace = new Vector323;
    this.linkedCameras = new Set;
    this.transformMatrix = new Matrix46;
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }
  async activate() {
    await waitForApplicationReady();
    this.objects = VolumeSliceMPREmbedded.findAll().filter((slice) => slice.name !== this.hostObject.name);
    this.linkedCameras = new Set(this.objects.map((slice) => slice.linkedCamera));
    App2.Instance().mouseController.addListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left", 10 /* Tool */ - 1);
  }
  deactivate() {
    this.objects.splice(0);
    this.linkedCameras.clear();
    App2.Instance().mouseController.removeListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left");
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
  }
  get cameraZ() {
    return this.hostObject.cameraController.zDir;
  }
  handleMouseDown(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    if (!this.objects.length)
      return;
    const app = App2.Instance().view.selectController;
    if (this.objects.some((obj) => !App2.Instance().view.selectController.has(obj.getComponent(SelectComponent))))
      return;
    this.rotateStart.set(event.clientX, event.clientY);
    this.objects.map((slice) => slice.plane.normal.clone().multiplyScalar(-slice.plane.constant)).reduce((a, b) => a.add(b), this.centerWorldSpace.set(0, 0, 0));
    this.centerWorldSpace.divideScalar(this.objects.length / 2);
    const ccs = this.centerCameraSpace.copy(this.centerWorldSpace).project(this.hostObject.camera);
    this.centerScreenSpace.set((ccs.x + 1) * this.hostObject.rect.width / 2 + this.hostObject.rect.left, this.hostObject.rect.top - (ccs.y - 1) * this.hostObject.rect.height / 2);
    this.rotateStart.sub(this.centerScreenSpace);
    App2.Instance().dispatchEvent({ type: "transformStart", entities: [...this.objects] });
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
  }
  handleMouseMove(event) {
    this.current.set(event.clientX, event.clientY).sub(this.centerScreenSpace);
    const angel = getAngle2D(this.current, this.rotateStart);
    makeRotationAroundPointAndAxis(angel, this.centerWorldSpace, this.cameraZ, this.transformMatrix);
    this.rotateStart.copy(this.current);
    this.objects.forEach((slice) => {
      slice.object3d.applyMatrix4(this.transformMatrix);
    });
    this.linkedCameras.forEach((camera) => {
      camera.applyMatrix4(this.transformMatrix);
    });
    App2.Instance().dispatchEvent({ type: "transform", entities: [...this.objects] });
  }
  handleMouseUp() {
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "middle");
    App2.Instance().dispatchEvent({ type: "transformEnd", entities: [...this.objects] });
  }
}

// studio/src/products/MPREmbedded/tools/MprRotateVolume.ts
import {Vector2 as Vector29, Vector3 as Vector324, Matrix4 as Matrix47} from "three";
class MprRotateVolume extends ViewportTool {
  objects;
  linkedCameras;
  rotateStart;
  current;
  angle;
  centerWorldSpace;
  centerScreenSpace;
  centerCameraSpace;
  transformMatrix;
  constructor(viewport) {
    super(viewport);
    this.rotateStart = new Vector29;
    this.current = new Vector29;
    this.angle = 0;
    this.objects = [];
    this.centerWorldSpace = new Vector324;
    this.centerScreenSpace = new Vector29;
    this.centerCameraSpace = new Vector324;
    this.linkedCameras = new Set;
    this.transformMatrix = new Matrix47;
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }
  async activate() {
    await waitForApplicationReady();
    this.objects = VolumeSliceMPREmbedded.findAll();
    this.objects.forEach((slice) => {
      slice.linkedCamera && this.linkedCameras.add(slice.linkedCamera);
    });
    App2.Instance().mouseController.addListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left", 10 /* Tool */ - 2);
  }
  deactivate() {
    this.objects.splice(0);
    this.linkedCameras.clear();
    App2.Instance().mouseController.removeListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left");
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
  }
  get cameraZ() {
    return this.hostObject.cameraController.zDir;
  }
  handleMouseDown(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    if (!this.objects.length)
      return;
    if (this.objects.some((obj) => App2.Instance().view.selectController.has(obj.getComponent(SelectComponent))))
      return;
    this.rotateStart.set(event.clientX, event.clientY);
    this.objects.map((slice) => slice.plane.normal.clone().multiplyScalar(-slice.plane.constant)).reduce((a, b) => a.add(b), this.centerWorldSpace.set(0, 0, 0));
    this.centerWorldSpace.divideScalar(this.objects.length / 3);
    const ccs = this.centerCameraSpace.copy(this.centerWorldSpace).project(this.hostObject.camera);
    this.centerScreenSpace.set((ccs.x + 1) * this.hostObject.rect.width / 2 + this.hostObject.rect.left, this.hostObject.rect.top - (ccs.y - 1) * this.hostObject.rect.height / 2);
    this.rotateStart.sub(this.centerScreenSpace);
    App2.Instance().dispatchEvent({ type: "transformStart", entities: [...this.objects] });
    App2.Instance().dispatchEvent({ type: "transformCameraStart", viewport: this.hostObject });
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
  }
  handleMouseMove(event) {
    this.current.set(event.clientX, event.clientY).sub(this.centerScreenSpace);
    const angel = -getAngle2D(this.current, this.rotateStart);
    makeRotationAroundPointAndAxis(angel, this.centerWorldSpace, this.cameraZ, this.transformMatrix);
    this.rotateStart.copy(this.current);
    this.objects.forEach((slice) => {
      slice.object3d.applyMatrix4(this.transformMatrix);
    });
    this.linkedCameras.forEach((camera) => {
      camera.applyMatrix4(this.transformMatrix);
    });
    App2.Instance().dispatchEvent({ type: "transform", entities: [...this.objects] });
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
  }
  handleMouseUp() {
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
    App2.Instance().dispatchEvent({ type: "transformEnd", entities: [...this.objects] });
    App2.Instance().dispatchEvent({ type: "transformCameraEnd", viewport: this.hostObject });
  }
}

// studio/src/products/MPREmbedded/tools/MprGoThroughVolume.ts
import {Vector2 as Vector210, Vector3 as Vector325} from "three";
class MprGoThroughVolume extends ViewportTool {
  slices;
  speed;
  vector;
  sliceNumber = 2;
  constructor(viewport) {
    super(viewport);
    this.speed = 0.2;
    this.vector = new Vector325;
    this.slices = [];
    this.handleMouseWheel = this.handleMouseWheel.bind(this);
  }
  async activate() {
    await waitForApplicationReady();
    this.slices = VolumeSliceMPREmbedded.findAll().filter((v) => v.name === this.hostObject.name);
    if (this.slices.length === this.sliceNumber) {
      App2.Instance().mouseController.addListener("wheel", this.handleMouseWheel, this.hostObject.domElement);
    }
  }
  deactivate() {
    this.slices.splice(0);
    App2.Instance().mouseController.removeListener("wheel", this.handleMouseWheel, this.hostObject.domElement);
  }
  skipCondition(event) {
    return !(event.ctrlKey || event.metaKey) || !this.slices.length;
  }
  handleMouseWheel(event) {
    event.preventDefault();
    if (this.skipCondition(event))
      return;
    let scale = 0;
    if (event.deltaY < 0) {
      scale = -this.speed;
    } else if (event.deltaY > 0) {
      scale = this.speed;
    }
    this.slices.forEach((slice) => slice.object3d.position.add(this.vector.copy(this.hostObject.cameraController.zDir).multiplyScalar(scale)));
    this.slices[0].linkedCamera?.position.copy(this.slices[0].object3d.position);
    App2.Instance().dispatchEvent({ type: "transform", entities: [...this.slices] });
  }
}

class MprGoThroughVolumeWithOutContours extends MprGoThroughVolume {
  sliceNumber = 1;
}

class MprGoThroughVolumeWithOutContoursCtrlLess extends MprGoThroughVolumeWithOutContours {
  skipCondition() {
    return !this.slices.length;
  }
}

class MprGoThroughVolumeWithOutContoursByLeftMB extends MprGoThroughVolumeWithOutContours {
  dollyStart;
  dollyEnd;
  dollyDelta;
  constructor(viewport) {
    super(viewport);
    this.dollyStart = new Vector210;
    this.dollyEnd = new Vector210;
    this.dollyDelta = new Vector210;
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }
  async activate() {
    await super.activate();
    if (this.slices.length === this.sliceNumber) {
      App2.Instance().mouseController.addListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left");
    }
  }
  deactivate() {
    super.deactivate();
    App2.Instance().mouseController.removeListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left");
  }
  handleMouseDown(event) {
    if (event.ctrlKey || event.metaKey || !this.slices.length)
      return;
    this.dollyStart.set(event.clientX, event.clientY);
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
  }
  handleMouseMove(event) {
    this.dollyEnd.set(event.clientX, event.clientY);
    this.dollyDelta.subVectors(this.dollyEnd, this.dollyStart);
    let scale = 0;
    if (this.dollyDelta.y < 0) {
      scale = -this.speed;
    } else if (this.dollyDelta.y > 0) {
      scale = this.speed;
    } else {
      return;
    }
    this.dollyStart.copy(this.dollyEnd);
    this.slices.forEach((slice) => slice.object3d.position.add(this.vector.copy(this.hostObject.cameraController.zDir).multiplyScalar(scale)));
    this.slices[0].linkedCamera?.position.copy(this.slices[0].object3d.position);
    App2.Instance().dispatchEvent({ type: "transform", entities: [...this.slices] });
  }
  handleMouseUp() {
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
  }
}

// studio/src/products/MPREmbedded/tools/MprMoveSlices2.ts
import {Vector2 as Vector211, Vector3 as Vector326, Vector4 as Vector42} from "three";
var rotateIcon = `url('data:image/svg+xml;utf8,<svg fill="%23FF0000" height="48" viewBox="0 0 24 24" width="48" xmlns="http://www.w3.org/2000/svg"><path d="M0 0h24v24H0z" fill="none"/> <path d="M6.41978 7.6775C6.89763 7.22209 7.22384 6.6415 7.35729 6.00895C7.49073 5.3764 7.42544 4.72021 7.16963 4.12312C6.91383 3.52603 6.47897 3.01477 5.91988 2.6538C5.3608 2.29284 4.70253 2.09834 4.02807 2.09482L4.84272 1.3154C4.90359 1.25439 4.93709 1.17302 4.93607 1.08868C4.93504 1.00434 4.89956 0.923736 4.83723 0.864099C4.77489 0.804462 4.69064 0.770527 4.60249 0.769553C4.51434 0.768578 4.42929 0.800641 4.36553 0.858884L2.94372 2.21921C2.91237 2.24918 2.8875 2.28477 2.87053 2.32394C2.85356 2.3631 2.84483 2.40509 2.84483 2.44749C2.84483 2.48989 2.85356 2.53187 2.87053 2.57104C2.8875 2.61021 2.91237 2.6458 2.94372 2.67577L4.36553 4.03627C4.42926 4.09469 4.51437 4.1269 4.60262 4.12599C4.69088 4.12509 4.77525 4.09114 4.83766 4.03143C4.90006 3.97172 4.93554 3.891 4.93649 3.80656C4.93743 3.72213 4.90377 3.6407 4.84272 3.57972L4.0398 2.81131C4.64102 2.82128 5.22107 3.02533 5.68584 3.39035C6.1506 3.75538 6.47281 4.25995 6.60016 4.8222C6.72752 5.38445 6.65256 5.97138 6.38745 6.48774C6.12233 7.00411 5.68261 7.41961 5.13966 7.66682C4.5967 7.91403 3.98236 7.97844 3.39632 7.84961C2.81029 7.72077 2.28696 7.40625 1.91125 6.95707C1.53554 6.5079 1.32949 5.95043 1.32656 5.37514C1.32362 4.79984 1.52396 4.24048 1.89507 3.78782C1.92652 3.75086 1.94996 3.70827 1.96402 3.66255C1.97808 3.61683 1.98247 3.56888 1.97695 3.52152C1.97143 3.47415 1.9561 3.42831 1.93185 3.38668C1.90761 3.34504 1.87494 3.30844 1.83575 3.27902C1.79656 3.2496 1.75163 3.22795 1.7036 3.21532C1.65556 3.2027 1.60538 3.19935 1.55598 3.20549C1.50658 3.21163 1.45896 3.22712 1.41588 3.25106C1.37281 3.275 1.33515 3.30691 1.30512 3.34493C0.767891 4.00168 0.512874 4.82982 0.592112 5.66034C0.67135 6.49086 1.07887 7.26114 1.7315 7.81397C2.38412 8.3668 3.23266 8.66051 4.10392 8.63515C4.97518 8.60979 5.80348 8.26727 6.41978 7.6775Z" fill="white" stroke="black" stroke-width="0.2"/></svg>') 12 12, auto`;
class MprMoveSlices2AsInMPR1 extends ViewportTool {
  objects;
  slicesForMove;
  moveStart;
  moveEnd;
  moveDelta;
  moveOffset;
  mouseDownAction;
  vector;
  constructor(viewport) {
    super(viewport);
    this.moveStart = new Vector211;
    this.moveEnd = new Vector211;
    this.moveDelta = new Vector211;
    this.moveOffset = new Vector326;
    this.vector = new Vector326;
    this.objects = [];
    this.slicesForMove = [];
    this.mouseDownAction = () => {
    };
    this.handleMouseMoveBefore = this.handleMouseMoveBefore.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }
  async activate() {
    await waitForApplicationReady();
    this.objects = VolumeSliceMPREmbedded.findAll().filter((slice) => slice.name !== this.hostObject.name);
    App2.Instance().mouseController.addListener("pointerdown", this.handleMouseDown, this.hostObject.getDomElement(), "left", 10 /* Tool */);
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMoveBefore, this.hostObject.getDomElement(), 30 /* CursorController */);
  }
  deactivate() {
    this.objects.splice(0);
    this.slicesForMove.splice(0);
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
    App2.Instance().mouseController.removeListener("pointerdown", this.handleMouseDown, this.hostObject.domElement, "left");
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMoveBefore, this.hostObject.domElement);
    this.hostObject.domElement.style.cursor = "default";
  }
  get cameraController() {
    return this.hostObject.cameraController;
  }
  get camera() {
    return this.hostObject.camera;
  }
  get cameraX() {
    return this.hostObject.cameraController.xDir;
  }
  get cameraY() {
    return this.hostObject.cameraController.yDir;
  }
  get distanceFactor() {
    return this.hostObject.cameraController.getDistanceFactor();
  }
  handleMouseMoveBefore(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    if (!this.objects.length)
      return;
    this.slicesForMove.length = 0;
    this.moveStart.set(event.clientX, event.clientY);
    const cursorControllerPosition = App2.Instance().cursorController.position;
    const point = new Vector326().set(cursorControllerPosition.x, cursorControllerPosition.y, 0.1).unproject(this.hostObject.camera);
    const data = this.objects.map((slice) => {
      const delta = slice.plane.distanceToPoint(point);
      const deltaVector = slice.plane.normal.clone().multiplyScalar(delta);
      const cameraSpaceDeltaVector = new Vector42(deltaVector.x, deltaVector.y, deltaVector.z, 0).applyMatrix4(this.hostObject.camera.matrixWorldInverse).applyMatrix4(this.hostObject.camera.projectionMatrix);
      const cameraSpaceDelta = cameraSpaceDeltaVector.length();
      return { slice, delta, cameraSpaceDelta };
    }).sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta));
    const shortestDistanceToToAnyLine = Math.abs(data[0].cameraSpaceDelta);
    const isNearTheLine = shortestDistanceToToAnyLine < 0.05;
    const isFarFromTheCrosshairCenter = data[0].cameraSpaceDelta + data[1].cameraSpaceDelta > 0.7;
    const isOnTheCrosshair = data[0].cameraSpaceDelta + data[1].cameraSpaceDelta < 0.05;
    if (isNearTheLine) {
      if (isFarFromTheCrosshairCenter) {
        this.hostObject.domElement.style.cursor = rotateIcon;
        this.mouseDownAction = this.emptyAction.bind(this);
        App2.Instance().view.selectController.add(this.objects.map((item) => item.getComponent(SelectComponent)));
      } else {
        if (data[0].slice.constructor !== data[1].slice.constructor) {
          this.slicesForMove.push(data[0].slice, data[1].slice);
        } else {
          this.slicesForMove.push(data[0].slice);
        }
        if (isOnTheCrosshair) {
          this.hostObject.domElement.style.cursor = "move";
          this.mouseDownAction = this.moveAllSlices.bind(this);
        } else {
          this.hostObject.domElement.style.cursor = "grab";
          this.mouseDownAction = this.moveSingleSlice.bind(this);
        }
      }
    } else {
      this.hostObject.domElement.style.cursor = rotateIcon;
      this.mouseDownAction = this.emptyAction.bind(this);
      App2.Instance().view.selectController.delete(this.objects.map((item) => item.getComponent(SelectComponent)));
    }
  }
  emptyAction() {
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
  }
  moveSingleSlice(event) {
    const listeners = App2.Instance().mouseController.listeners.get(this.hostObject.getDomElement());
    if (listeners) {
      listeners.disableButtonExcept("pointerdown", this.moveAllSlices, "left");
    }
    this.moveStart.set(event.clientX, event.clientY);
    App2.Instance().dispatchEvent({ type: "transformStart", entities: [...this.objects] });
    App2.Instance().dispatchEvent({ type: "transform", entities: [...this.objects] });
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
  }
  moveAllSlices(event) {
    const listeners = App2.Instance().mouseController.listeners.get(this.hostObject.domElement);
    if (listeners) {
      listeners.disableButtonExcept("pointerdown", this.moveAllSlices, "left");
    }
    this.slicesForMove = [...this.objects];
    this.moveStart.set(event.clientX, event.clientY);
    const point = new Vector326().set((event.clientX - this.hostObject.rect.left) / this.hostObject.rect.width * 2 - 1, -(event.clientY - this.hostObject.rect.top) / this.hostObject.rect.height * 2 + 1, 0.1).unproject(this.hostObject.camera);
    this.objects.forEach((slice) => {
      const delta = slice.plane.normal.clone().multiplyScalar(slice.plane.distanceToPoint(point));
      slice.object3d.position.add(delta);
      slice instanceof VolumeSliceMPREmbedded && slice.linkedCamera?.position.add(delta);
    });
    App2.Instance().dispatchEvent({ type: "transformStart", entities: [...this.objects] });
    App2.Instance().dispatchEvent({ type: "transform", entities: [...this.objects] });
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
    App2.Instance().dispatchEvent({ type: "render" });
  }
  handleMouseDown(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    if (!this.objects.length)
      return;
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMoveBefore, this.hostObject.domElement);
    this.mouseDownAction(event);
  }
  handleMouseMove(event) {
    this.moveEnd.set(event.clientX, event.clientY);
    this.moveDelta.subVectors(this.moveEnd, this.moveStart);
    this.move(this.moveDelta.x, this.moveDelta.y);
    this.moveStart.copy(this.moveEnd);
  }
  handleMouseUp(event) {
    App2.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
    this.moveStart.set(event.clientX, event.clientY);
    App2.Instance().dispatchEvent({ type: "transformCameraEnd", viewport: this.hostObject });
    App2.Instance().dispatchEvent({ type: "transformEnd", entities: [...this.objects] });
    App2.Instance().mouseController.addListener("pointermove", this.handleMouseMoveBefore, this.hostObject.domElement, 20);
    const listeners = App2.Instance().mouseController.listeners.get(this.hostObject.domElement);
    if (listeners) {
      listeners.enableButton("pointerdown", "left");
    }
  }
  move(deltaX, deltaY) {
    const sizeRatio = this.cameraController.orthographicCameraHelper.getSizeRatio();
    this.moveOffset.copy(this.cameraX).multiplyScalar(-(deltaX * sizeRatio) / this.camera.zoom);
    this.moveOffset.add(this.vector.copy(this.cameraY).multiplyScalar(deltaY * sizeRatio / this.camera.zoom));
    this.slicesForMove.forEach((slice) => {
      const delta = slice.plane.normal.clone().multiplyScalar(this.moveOffset.clone().dot(slice.plane.normal));
      slice.object3d.position.sub(delta);
      slice instanceof VolumeSliceMPREmbedded && slice.linkedCamera?.position.sub(delta);
    });
    App2.Instance().dispatchEvent({ type: "transform", entities: [...this.objects] });
  }
}

// studio/src/products/MPREmbedded/config/mprAppViewportToolsAndModes.ts
class MprAppViewportMode extends Mode {
  static except = (tools) => new MprAppViewportMode({ tools: { toolList: tools, include: false } });
  static only = (tools) => new MprAppViewportMode({ tools: { toolList: tools, include: true } });
}
var mprAppViewportServiceTools = new Set([
  Render,
  MprVolumeSliceSpawner,
  MprCrossLineSpawner
]);
var mprAppViewportTools = tuple(MPRPan2DAsMPR1, MprMoveSlices2AsInMPR1, MprZoom2DByLeftMBWithSync, MprRotateSlice2, MprRotateVolume, MprGoThroughVolumeWithOutContoursCtrlLess, MprGoThroughVolumeWithOutContoursByLeftMB);
var mprAppMainViewportTools = tuple(Render, OrbitControls, CameraToggle);
var viewports = tuple(...MPRViewportNames);
var mprViewportModes = {};
viewports.forEach((viewportName) => {
  mprViewportModes[viewportName] = {
    mainMode: MprAppViewportMode.only([
      MprMoveSlices2AsInMPR1,
      MprRotateSlice2,
      MprRotateVolume,
      MprGoThroughVolumeWithOutContoursCtrlLess
    ]),
    contrastBrightnessMode: MprAppViewportMode.only([
      MprGoThroughVolumeWithOutContoursCtrlLess
    ]),
    panMode: MprAppViewportMode.only([
      MPRPan2DAsMPR1,
      MprGoThroughVolumeWithOutContoursCtrlLess
    ]),
    zoomMode: MprAppViewportMode.only([
      MprZoom2DByLeftMBWithSync
    ]),
    scrollThroughVolumeMode: MprAppViewportMode.only([
      MprGoThroughVolumeWithOutContoursCtrlLess,
      MprGoThroughVolumeWithOutContoursByLeftMB
    ])
  };
});
var mprAppMainViewportModes = {
  mainMode: MprAppViewportMode.except([]),
  panMode: MprAppViewportMode.except([]),
  zoomMode: MprAppViewportMode.except([]),
  contrastBrightnessMode: MprAppViewportMode.except([]),
  scrollThroughVolumeMode: MprAppViewportMode.except([])
};

// studio/src/products/MPREmbedded/tools/MPR2ViewportScreenshot.ts
import {WebGLRenderTarget, WebGLRenderer as WebGLRenderer2} from "three";
class MPR2ViewportScreenshot extends ViewTool {
  renderer;
  constructor(view) {
    super(view);
    this.renderer = new WebGLRenderer2({ preserveDrawingBuffer: true });
  }
  activate() {
    this.on("makeScreenshot", this.makeScreenshot);
  }
  makeScreenshot = (event) => {
    const activeViewport = App2.Instance().getActiveView().getActiveViewport();
    if (!activeViewport)
      return;
    const width = Math.round(activeViewport.size.z);
    const height = Math.round(activeViewport.size.w);
    const target = new WebGLRenderTarget(width, height);
    this.renderer.setRenderTarget(target);
    const canvas2d = document.createElement("canvas");
    const context2d = canvas2d.getContext("2d");
    if (!context2d) {
      console.error("Failed to get context2d");
      return;
    }
    this.renderer.setSize(width, height);
    activeViewport.camera.scale.y = -1;
    const crossLines = CrossLineLegacy.findAll();
    const rememberVisibility = crossLines.map((crossLine) => crossLine.visibility.isVisible());
    crossLines.forEach((crossLine) => crossLine.visibility.hide());
    this.renderer.render(activeViewport.view.scene, activeViewport.camera);
    activeViewport.camera.scale.y = 1;
    crossLines.forEach((crossLine, index) => rememberVisibility[index] ? crossLine.visibility.show() : crossLine.visibility.hide());
    const buffer = new Uint8ClampedArray(width * height * 4);
    this.renderer.readRenderTargetPixels(target, 0, 0, width, height, buffer);
    const imageData = new ImageData(buffer, width, height);
    canvas2d.width = width;
    canvas2d.height = height;
    context2d.putImageData(imageData, 0, 0);
    canvas2d.toBlob((blob) => {
      if (blob) {
        event.onBlob(activeViewport.name, blob);
      } else {
        console.error("Failed to convert canvas to blob");
      }
    });
    this.renderer.setRenderTarget(null);
  };
}

// studio/src/core/tools/ActiveViewportSwitcher.ts
class ActiveViewSwitcher extends ViewTool {
  event;
  constructor(view) {
    super(view);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onTransformStart = this.onTransformStart.bind(this);
    this.onTransformEnd = this.onTransformEnd.bind(this);
    this.onMouseOverWhileTransform = this.onMouseOverWhileTransform.bind(this);
  }
  activate() {
    this.hostObject.viewports.forEach((viewport) => {
      App2.Instance().mouseController.addListener("pointerover", this.onMouseOver, viewport.getDomElement());
      App2.Instance().mouseController.addListener("pointermove", this.onMouseMove, viewport.getDomElement());
    });
    App2.Instance().addEventListener("transformStart", this.onTransformStart);
  }
  deactivate() {
    this.hostObject.viewports.forEach((viewport) => {
      App2.Instance().mouseController.removeListener("pointerover", this.onMouseOver, viewport.getDomElement());
      App2.Instance().mouseController.removeListener("pointermove", this.onMouseMove, viewport.getDomElement());
    });
    App2.Instance().removeEventListener("transformStart", this.onTransformStart);
    App2.Instance().removeEventListener("transformEnd", this.onTransformEnd);
  }
  onTransformStart() {
    this.event = undefined;
    this.hostObject.viewports.forEach((viewport) => {
      App2.Instance().mouseController.removeListener("pointerover", this.onMouseOver, viewport.getDomElement());
      App2.Instance().mouseController.addListener("pointerover", this.onMouseOverWhileTransform, viewport.getDomElement());
    });
    App2.Instance().addEventListener("transformEnd", this.onTransformEnd);
  }
  onTransformEnd() {
    this.hostObject.viewports.forEach((viewport) => {
      App2.Instance().mouseController.addListener("pointerover", this.onMouseOver, viewport.getDomElement());
      App2.Instance().mouseController.removeListener("pointerover", this.onMouseOverWhileTransform, viewport.getDomElement());
    });
    App2.Instance().removeEventListener("transformEnd", this.onTransformEnd);
    if (this.event) {
      this.onMouseOver(this.event);
    }
  }
  onMouseOverWhileTransform(event) {
    this.event = event;
  }
  onMouseOver(event) {
    for (let [name, viewport] of this.hostObject.viewports) {
      if (viewport.isEnabled() && viewport.getDomElement() === event.target) {
        App2.Instance().setActiveView(this.hostObject);
        this.hostObject.setActiveViewport(name);
        return true;
      }
    }
    return false;
  }
  onMouseMove(event) {
    if (this.onMouseOver(event)) {
      this.hostObject.viewports.forEach((viewport) => {
        App2.Instance().mouseController.removeListener("pointermove", this.onMouseMove, viewport.getDomElement());
      });
    }
  }
}

// studio/src/products/MPREmbedded/tools/ListenAndControlCrossLineVisibility.ts
class ListenAndControlCrossLineVisibility extends ViewTool {
  activate() {
    MPREmbeddedInstance.addEventListener("hideCrossLines", this.hideCrossLines);
    MPREmbeddedInstance.addEventListener("showCrossLines", this.showCrossLines);
  }
  hideCrossLines = () => {
    const crossLines = CrossLineLegacy.findAll();
    crossLines.forEach((obj) => obj.getComponent(Visibility)?.hide());
    MPREmbeddedInstance.dispatchEvent({ type: "render" });
  };
  showCrossLines = () => {
    const crossLines = CrossLineLegacy.findAll();
    crossLines.forEach((obj) => obj.getComponent(Visibility)?.show());
    MPREmbeddedInstance.dispatchEvent({ type: "render" });
  };
  deactivate() {
    MPREmbeddedInstance.removeEventListener("hideCrossLines", this.hideCrossLines);
    MPREmbeddedInstance.removeEventListener("showCrossLines", this.showCrossLines);
  }
}

// studio/src/products/MPREmbedded/entities/ToothLike.ts
import {ArrowHelper as ArrowHelper2, AxesHelper, Vector3 as Vector328} from "three";

// studio/src/products/MPREmbedded/entities/NumerableEntity.ts
class NumerableEntity extends Entity {
  numeration;
  constructor(object3D, numeration) {
    super(object3D);
    this.numeration = Object.freeze(numeration);
  }
  get iso() {
    return this.numeration.iso;
  }
  get idx() {
    return this.numeration.idx;
  }
}

// studio/src/utils/ToothLikePose/ToothLikePose.ts
import {Quaternion as Quaternion5} from "three";

// studio/src/utils/ToothLikePose/anatomicalBasisToRightBasis.ts
import {Matrix4 as Matrix48} from "three";

// studio/src/utils/NumerationUtils.ts
class NumerationUtils {
  static fromProtobuf(toothNumeration) {
    return { iso: toothNumeration.ISO, idx: toothNumeration.SupernumeraryIndex };
  }
  static quadrantNumber(numeration) {
    let result = Math.floor(numeration.iso / 10);
    return result > 4 ? result - 4 : result;
  }
  static isTopQuadrant(numeration) {
    const q = NumerationUtils.quadrantNumber(numeration);
    return q === 1 || q === 2 || q === 5 || q === 6;
  }
  static isBottomQuadrant(numeration) {
    const q = NumerationUtils.quadrantNumber(numeration);
    return q === 3 || q === 4 || q === 7 || q === 8;
  }
  static isLeftQuadrant(numeration) {
    const q = NumerationUtils.quadrantNumber(numeration);
    return q === 2 || q === 3 || q === 6 || q === 7;
  }
  static isRightQuadrant(numeration) {
    const q = NumerationUtils.quadrantNumber(numeration);
    return q === 1 || q === 4 || q === 5 || q === 8;
  }
  static toNumerationKey(numeration) {
    return numeration.iso * 10 + (numeration.idx || 0);
  }
  static fromNumerationKey(numerationKey) {
    const iso = Math.floor(numerationKey / 10);
    const idx = numerationKey - iso * 10;
    return { iso, idx };
  }
  static isEqual(numerationA, numerationB) {
    return numerationA.iso === numerationB.iso && (numerationA.idx ?? 0) === (numerationB.idx ?? 0);
  }
  static isSupernumerary(numeration) {
    return numeration.idx !== undefined && numeration.idx !== 0;
  }
}

// studio/src/utils/ToothLikePose/anatomicalBasisToRightBasis.ts
var anatomicalBasisToRightBasis = (anatomicalBasis, numeration) => {
  const quadrant = NumerationUtils.quadrantNumber(numeration);
  if (quadrant === 1) {
    return new Matrix48().makeBasis(anatomicalBasis.mesioDistal.clone().negate(), anatomicalBasis.vestibuloOral, anatomicalBasis.crownRoot);
  }
  if (quadrant === 2) {
    return new Matrix48().makeBasis(anatomicalBasis.mesioDistal, anatomicalBasis.vestibuloOral, anatomicalBasis.crownRoot);
  }
  if (quadrant === 3) {
    return new Matrix48().makeBasis(anatomicalBasis.mesioDistal, anatomicalBasis.vestibuloOral, anatomicalBasis.crownRoot.clone().negate());
  }
  if (quadrant === 4) {
    return new Matrix48().makeBasis(anatomicalBasis.mesioDistal.clone().negate(), anatomicalBasis.vestibuloOral, anatomicalBasis.crownRoot.clone().negate());
  }
  return new Matrix48().identity();
};

// studio/src/utils/ToothLikePose/directionsToAnatomicalBasis.ts
var directionsToAnatomicalBasis = (directions, numeration) => {
  const mesioDistal = directions.x.clone();
  if (NumerationUtils.isLeftQuadrant(numeration)) {
    mesioDistal.negate();
  }
  const vestibuloOral = directions.y.clone();
  const crownRoot = directions.z.clone();
  if (NumerationUtils.isBottomQuadrant(numeration)) {
    crownRoot.negate();
  }
  return {
    mesioDistal,
    vestibuloOral,
    crownRoot
  };
};

// studio/src/utils/ToothLikePose/ToothLikePose.ts
class ToothLikePose {
  origin;
  numeration;
  anatomicalBasis;
  static fromProtobuf(value, numeration) {
    const wsOrigin = FloatPoint3DToVector3(value.Origin);
    if (value.AnatomicalBasis) {
      const anatomialBasis = {
        mesioDistal: FloatPoint3DToVector3(value.AnatomicalBasis.MesioDistal),
        vestibuloOral: FloatPoint3DToVector3(value.AnatomicalBasis.VestibuloOral),
        crownRoot: FloatPoint3DToVector3(value.AnatomicalBasis.Axial)
      };
      return new ToothLikePose(wsOrigin, numeration, anatomialBasis);
    } else {
      const wsDirections = OriginatedPositionToVector3Set(value.Directions);
      const anatomialBasis = directionsToAnatomicalBasis(wsDirections, numeration);
      return new ToothLikePose(wsOrigin, numeration, anatomialBasis);
    }
  }
  rightBasis;
  invRightBasisQuaternion;
  constructor(origin, numeration, anatomicalBasis) {
    this.origin = origin;
    this.numeration = numeration;
    this.anatomicalBasis = anatomicalBasis;
    this.rightBasis = anatomicalBasisToRightBasis(anatomicalBasis, numeration);
    this.rightBasis.setPosition(origin);
    this.invRightBasisQuaternion = new Quaternion5().setFromRotationMatrix(this.rightBasis).invert();
  }
  copy(pose) {
    this.origin.copy(pose.origin);
    this.numeration = { iso: pose.numeration.iso, idx: pose.numeration.idx };
    this.anatomicalBasis.crownRoot.copy(pose.anatomicalBasis.crownRoot);
    this.anatomicalBasis.mesioDistal.copy(pose.anatomicalBasis.mesioDistal);
    this.anatomicalBasis.vestibuloOral.copy(pose.anatomicalBasis.vestibuloOral);
    this.rightBasis.copy(pose.rightBasis);
    this.invRightBasisQuaternion.copy(pose.invRightBasisQuaternion);
    return this;
  }
  clone() {
    return new ToothLikePose(this.origin.clone(), { iso: this.numeration.iso, idx: this.numeration.idx }, {
      mesioDistal: this.anatomicalBasis.mesioDistal.clone(),
      vestibuloOral: this.anatomicalBasis.vestibuloOral.clone(),
      crownRoot: this.anatomicalBasis.crownRoot.clone()
    });
  }
  copyCrownTo(out) {
    const crown = out.copy(this.anatomicalBasis.crownRoot).negate();
    return crown;
  }
  copyRootTo(out) {
    const root = out.copy(this.anatomicalBasis.crownRoot);
    return root;
  }
  copyMesialTo(out) {
    const mesial = out.copy(this.anatomicalBasis.mesioDistal).negate();
    return mesial;
  }
  copyDistalTo(out) {
    const distal = out.copy(this.anatomicalBasis.mesioDistal);
    return distal;
  }
  copyVestibularTo(out) {
    const vestibular = out.copy(this.anatomicalBasis.vestibuloOral).negate();
    return vestibular;
  }
  copyOralTo(out) {
    const oral = out.copy(this.anatomicalBasis.vestibuloOral);
    return oral;
  }
}

// studio/src/products/MPREmbedded/entities/ToothLike.ts
var DEBUG_RIGHT_BASIS = localStorage.getItem("DEBUG_RIGHT_BASIS") === "true";
var DEBUG_ANATOMICAL_BASIS = localStorage.getItem("DEBUG_ANATOMICAL_BASIS") === "true";

class ToothLike extends NumerableEntity {
  pose;
  constructor(object3d, numeration) {
    super(object3d, numeration);
    this.pose = new ToothLikePose(Object.freeze(new Vector328), { iso: 0 }, Object.freeze({
      mesioDistal: Object.freeze(new Vector328(1, 0, 0)),
      vestibuloOral: Object.freeze(new Vector328(0, 1, 0)),
      crownRoot: Object.freeze(new Vector328(0, 0, 1))
    }));
    this.numeration = Object.freeze(numeration);
    this.object3d.renderOrder = 10;
  }
  static isBabyTooth(isoNumber) {
    return isoNumber >= 51 && isoNumber <= 85;
  }
  static isUpper(isoNumber) {
    return isoNumber >= 11 && isoNumber <= 28 || isoNumber >= 51 && isoNumber <= 65;
  }
  static isLower(isoNumber) {
    return isoNumber >= 31 && isoNumber <= 48 || isoNumber >= 71 && isoNumber <= 85;
  }
  setPose(pose) {
    this.pose = pose;
    if (DEBUG_RIGHT_BASIS) {
      const axesHelper = new AxesHelper(10);
      axesHelper.applyMatrix4(pose.rightBasis);
      App2.Instance().view.scene.add(axesHelper);
    }
    if (DEBUG_ANATOMICAL_BASIS) {
      App2.Instance().view.scene.add(new ArrowHelper2(pose.anatomicalBasis.mesioDistal, this.pose.origin, 15, "red"));
      App2.Instance().view.scene.add(new ArrowHelper2(pose.anatomicalBasis.vestibuloOral, this.pose.origin, 15, "green"));
      App2.Instance().view.scene.add(new ArrowHelper2(pose.anatomicalBasis.crownRoot, this.pose.origin, 15, "blue"));
    }
  }
}

// studio/src/products/MPREmbedded/entities/MPR2Tooth.ts
class MPR2Tooth extends ToothLike {
  setPose(pose) {
    super.setPose(pose);
    pose.rightBasis.decompose(this.object3d.position, this.object3d.quaternion, this.object3d.scale);
  }
}

// studio/src/products/MPREmbedded/tools/MPR2Reset.ts
class MPR2Reset extends ViewTool {
  activate() {
    MPREmbeddedInstance.addEventListener("reset", this.reset);
  }
  deactivate() {
    MPREmbeddedInstance.removeEventListener("reset", this.reset);
  }
  reset = () => {
    MPR2Storage_default.resetColorProfileToInitial();
    MPREmbeddedInstance.dispatchEvent({ type: "colorProfileChanged" });
    const mpr2tooth = findAll("mpr", MPR2Tooth)[0];
    MPREmbeddedInstance.dispatchEvent({
      type: "setCameraByEntityAxes",
      object: mpr2tooth,
      changeZoom: true,
      viewportsNames: ["Axial", "Sagittal", "Coronal"]
    });
  };
}

// studio/src/shared/MPR/tools/MPR2SyncVolumeSlicesWithColorProfile.ts
class MPR2SyncVolumeSlicesWithColorProfile extends ViewTool {
  slices = [];
  async activate() {
    await waitForApplicationReady();
    this.slices = VolumeSliceMPREmbedded.findAll();
    this.syncSlicesWithColorProfile();
    this.on("colorProfileChanged", this.syncSlicesWithColorProfile);
  }
  syncSlicesWithColorProfile = () => {
    this.slices.forEach((slice) => {
      slice.syncAllWithMPR2Storage();
    });
    App2.Instance().dispatchEvent({ type: "render" });
  };
}

// studio/src/shared/MPR/tools/MprAlignVolumeSliceToEntityOnSelect.ts
import {Mesh as Mesh2, OrthographicCamera as OrthographicCamera6, Vector3 as Vector333} from "three";

// studio/src/utils/ToothLikePose/PoseHelper.ts
import {AxesHelper as AxesHelper2, Group as Group4, Vector3 as Vector330} from "three";

// studio/src/utils/ArrowWithText.ts
import {Group as Group3, Vector3 as Vector329, ArrowHelper as ArrowHelper3} from "three";

// node_modules/three-spritetext/dist/three-spritetext.mjs
import {CanvasTexture, Sprite, SpriteMaterial, SRGBColorSpace} from "three";
function _arrayLikeToArray(r, a) {
  (a == null || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a);e < a; e++)
    n[e] = r[e];
  return n;
}
function _arrayWithHoles(r) {
  if (Array.isArray(r))
    return r;
}
function _arrayWithoutHoles(r) {
  if (Array.isArray(r))
    return _arrayLikeToArray(r);
}
function _assertThisInitialized(e) {
  if (e === undefined)
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  return e;
}
function _callSuper(t, o, e) {
  return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e, _getPrototypeOf(t).constructor) : o.apply(t, e));
}
function _classCallCheck(a, n) {
  if (!(a instanceof n))
    throw new TypeError("Cannot call a class as a function");
}
function _defineProperties(e, r) {
  for (var t = 0;t < r.length; t++) {
    var o = r[t];
    o.enumerable = o.enumerable || false, o.configurable = true, "value" in o && (o.writable = true), Object.defineProperty(e, _toPropertyKey(o.key), o);
  }
}
function _createClass(e, r, t) {
  return _defineProperties(e.prototype, r), Object.defineProperty(e, "prototype", {
    writable: false
  }), e;
}
function _getPrototypeOf(t) {
  return _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function(t2) {
    return t2.__proto__ || Object.getPrototypeOf(t2);
  }, _getPrototypeOf(t);
}
function _inherits(t, e) {
  if (typeof e != "function" && e !== null)
    throw new TypeError("Super expression must either be null or a function");
  t.prototype = Object.create(e && e.prototype, {
    constructor: {
      value: t,
      writable: true,
      configurable: true
    }
  }), Object.defineProperty(t, "prototype", {
    writable: false
  }), e && _setPrototypeOf(t, e);
}
function _isNativeReflectConstruct() {
  try {
    var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {
    }));
  } catch (t2) {
  }
  return (_isNativeReflectConstruct = function() {
    return !!t;
  })();
}
function _iterableToArray(r) {
  if (typeof Symbol != "undefined" && r[Symbol.iterator] != null || r["@@iterator"] != null)
    return Array.from(r);
}
function _iterableToArrayLimit(r, l) {
  var t = r == null ? null : typeof Symbol != "undefined" && r[Symbol.iterator] || r["@@iterator"];
  if (t != null) {
    var e, n, i, u, a = [], f = true, o = false;
    try {
      if (i = (t = t.call(r)).next, l === 0) {
        if (Object(t) !== t)
          return;
        f = false;
      } else
        for (;!(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = true)
          ;
    } catch (r2) {
      o = true, n = r2;
    } finally {
      try {
        if (!f && t.return != null && (u = t.return(), Object(u) !== u))
          return;
      } finally {
        if (o)
          throw n;
      }
    }
    return a;
  }
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _possibleConstructorReturn(t, e) {
  if (e && (typeof e == "object" || typeof e == "function"))
    return e;
  if (e !== undefined)
    throw new TypeError("Derived constructors may only return object or undefined");
  return _assertThisInitialized(t);
}
function _setPrototypeOf(t, e) {
  return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function(t2, e2) {
    return t2.__proto__ = e2, t2;
  }, _setPrototypeOf(t, e);
}
function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}
function _toConsumableArray(r) {
  return _arrayWithoutHoles(r) || _iterableToArray(r) || _unsupportedIterableToArray(r) || _nonIterableSpread();
}
function _toPrimitive(t, r) {
  if (typeof t != "object" || !t)
    return t;
  var e = t[Symbol.toPrimitive];
  if (e !== undefined) {
    var i = e.call(t, r);
    if (typeof i != "object")
      return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return String(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return typeof i == "symbol" ? i : i + "";
}
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if (typeof r == "string")
      return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return t === "Object" && r.constructor && (t = r.constructor.name), t === "Map" || t === "Set" ? Array.from(r) : t === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : undefined;
  }
}
var three = typeof window !== "undefined" && window.THREE ? window.THREE : {
  CanvasTexture,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace
};
var _default = /* @__PURE__ */ function(_three$Sprite) {
  function _default2() {
    var _this;
    var text = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
    var textHeight = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 10;
    var color = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "rgba(255, 255, 255, 1)";
    _classCallCheck(this, _default2);
    _this = _callSuper(this, _default2, [new three.SpriteMaterial]);
    _this._text = "".concat(text);
    _this._textHeight = textHeight;
    _this._color = color;
    _this._backgroundColor = false;
    _this._padding = 0;
    _this._borderWidth = 0;
    _this._borderRadius = 0;
    _this._borderColor = "white";
    _this._strokeWidth = 0;
    _this._strokeColor = "white";
    _this._fontFace = "system-ui";
    _this._fontSize = 90;
    _this._fontWeight = "normal";
    _this._canvas = document.createElement("canvas");
    _this._genCanvas();
    return _this;
  }
  _inherits(_default2, _three$Sprite);
  return _createClass(_default2, [{
    key: "text",
    get: function get() {
      return this._text;
    },
    set: function set(text) {
      this._text = text;
      this._genCanvas();
    }
  }, {
    key: "textHeight",
    get: function get() {
      return this._textHeight;
    },
    set: function set(textHeight) {
      this._textHeight = textHeight;
      this._genCanvas();
    }
  }, {
    key: "color",
    get: function get() {
      return this._color;
    },
    set: function set(color) {
      this._color = color;
      this._genCanvas();
    }
  }, {
    key: "backgroundColor",
    get: function get() {
      return this._backgroundColor;
    },
    set: function set(color) {
      this._backgroundColor = color;
      this._genCanvas();
    }
  }, {
    key: "padding",
    get: function get() {
      return this._padding;
    },
    set: function set(padding) {
      this._padding = padding;
      this._genCanvas();
    }
  }, {
    key: "borderWidth",
    get: function get() {
      return this._borderWidth;
    },
    set: function set(borderWidth) {
      this._borderWidth = borderWidth;
      this._genCanvas();
    }
  }, {
    key: "borderRadius",
    get: function get() {
      return this._borderRadius;
    },
    set: function set(borderRadius) {
      this._borderRadius = borderRadius;
      this._genCanvas();
    }
  }, {
    key: "borderColor",
    get: function get() {
      return this._borderColor;
    },
    set: function set(borderColor) {
      this._borderColor = borderColor;
      this._genCanvas();
    }
  }, {
    key: "fontFace",
    get: function get() {
      return this._fontFace;
    },
    set: function set(fontFace) {
      this._fontFace = fontFace;
      this._genCanvas();
    }
  }, {
    key: "fontSize",
    get: function get() {
      return this._fontSize;
    },
    set: function set(fontSize) {
      this._fontSize = fontSize;
      this._genCanvas();
    }
  }, {
    key: "fontWeight",
    get: function get() {
      return this._fontWeight;
    },
    set: function set(fontWeight) {
      this._fontWeight = fontWeight;
      this._genCanvas();
    }
  }, {
    key: "strokeWidth",
    get: function get() {
      return this._strokeWidth;
    },
    set: function set(strokeWidth) {
      this._strokeWidth = strokeWidth;
      this._genCanvas();
    }
  }, {
    key: "strokeColor",
    get: function get() {
      return this._strokeColor;
    },
    set: function set(strokeColor) {
      this._strokeColor = strokeColor;
      this._genCanvas();
    }
  }, {
    key: "_genCanvas",
    value: function _genCanvas() {
      var _this2 = this;
      var canvas = this._canvas;
      var ctx = canvas.getContext("2d");
      var relFactor = 1 / this.textHeight;
      var border = Array.isArray(this.borderWidth) ? this.borderWidth : [this.borderWidth, this.borderWidth];
      var relBorder = border.map(function(b) {
        return b * _this2.fontSize * relFactor;
      });
      var borderRadius = Array.isArray(this.borderRadius) ? this.borderRadius : [this.borderRadius, this.borderRadius, this.borderRadius, this.borderRadius];
      var relBorderRadius = borderRadius.map(function(b) {
        return b * _this2.fontSize * relFactor;
      });
      var padding = Array.isArray(this.padding) ? this.padding : [this.padding, this.padding];
      var relPadding = padding.map(function(p) {
        return p * _this2.fontSize * relFactor;
      });
      var lines = this.text.split("\n");
      var font = "".concat(this.fontWeight, " ").concat(this.fontSize, "px ").concat(this.fontFace);
      ctx.font = font;
      var innerWidth = Math.max.apply(Math, _toConsumableArray(lines.map(function(line) {
        return ctx.measureText(line).width;
      })));
      var innerHeight = this.fontSize * lines.length;
      canvas.width = innerWidth + relBorder[0] * 2 + relPadding[0] * 2;
      canvas.height = innerHeight + relBorder[1] * 2 + relPadding[1] * 2;
      if (this.borderWidth) {
        ctx.strokeStyle = this.borderColor;
        if (relBorder[0]) {
          var hb = relBorder[0] / 2;
          ctx.lineWidth = relBorder[0];
          ctx.beginPath();
          ctx.moveTo(hb, relBorderRadius[0]);
          ctx.lineTo(hb, canvas.height - relBorderRadius[3]);
          ctx.moveTo(canvas.width - hb, relBorderRadius[1]);
          ctx.lineTo(canvas.width - hb, canvas.height - relBorderRadius[2]);
          ctx.stroke();
        }
        if (relBorder[1]) {
          var _hb = relBorder[1] / 2;
          ctx.lineWidth = relBorder[1];
          ctx.beginPath();
          ctx.moveTo(Math.max(relBorder[0], relBorderRadius[0]), _hb);
          ctx.lineTo(canvas.width - Math.max(relBorder[0], relBorderRadius[1]), _hb);
          ctx.moveTo(Math.max(relBorder[0], relBorderRadius[3]), canvas.height - _hb);
          ctx.lineTo(canvas.width - Math.max(relBorder[0], relBorderRadius[2]), canvas.height - _hb);
          ctx.stroke();
        }
        if (this.borderRadius) {
          var cornerWidth = Math.max.apply(Math, _toConsumableArray(relBorder));
          var _hb2 = cornerWidth / 2;
          ctx.lineWidth = cornerWidth;
          ctx.beginPath();
          [!!relBorderRadius[0] && [relBorderRadius[0], _hb2, _hb2, relBorderRadius[0]], !!relBorderRadius[1] && [canvas.width - relBorderRadius[1], canvas.width - _hb2, _hb2, relBorderRadius[1]], !!relBorderRadius[2] && [canvas.width - relBorderRadius[2], canvas.width - _hb2, canvas.height - _hb2, canvas.height - relBorderRadius[2]], !!relBorderRadius[3] && [relBorderRadius[3], _hb2, canvas.height - _hb2, canvas.height - relBorderRadius[3]]].filter(function(d) {
            return d;
          }).forEach(function(_ref) {
            var _ref2 = _slicedToArray(_ref, 4), x0 = _ref2[0], x1 = _ref2[1], y0 = _ref2[2], y1 = _ref2[3];
            ctx.moveTo(x0, y0);
            ctx.quadraticCurveTo(x1, y0, x1, y1);
          });
          ctx.stroke();
        }
      }
      if (this.backgroundColor) {
        ctx.fillStyle = this.backgroundColor;
        if (!this.borderRadius) {
          ctx.fillRect(relBorder[0], relBorder[1], canvas.width - relBorder[0] * 2, canvas.height - relBorder[1] * 2);
        } else {
          ctx.beginPath();
          ctx.moveTo(relBorder[0], relBorderRadius[0]);
          [
            [relBorder[0], relBorderRadius[0], canvas.width - relBorderRadius[1], relBorder[1], relBorder[1], relBorder[1]],
            [canvas.width - relBorder[0], canvas.width - relBorder[0], canvas.width - relBorder[0], relBorder[1], relBorderRadius[1], canvas.height - relBorderRadius[2]],
            [canvas.width - relBorder[0], canvas.width - relBorderRadius[2], relBorderRadius[3], canvas.height - relBorder[1], canvas.height - relBorder[1], canvas.height - relBorder[1]],
            [relBorder[0], relBorder[0], relBorder[0], canvas.height - relBorder[1], canvas.height - relBorderRadius[3], relBorderRadius[0]]
          ].forEach(function(_ref3) {
            var _ref4 = _slicedToArray(_ref3, 6), x0 = _ref4[0], x1 = _ref4[1], x2 = _ref4[2], y0 = _ref4[3], y1 = _ref4[4], y2 = _ref4[5];
            ctx.quadraticCurveTo(x0, y0, x1, y1);
            ctx.lineTo(x2, y2);
          });
          ctx.closePath();
          ctx.fill();
        }
      }
      ctx.translate.apply(ctx, _toConsumableArray(relBorder));
      ctx.translate.apply(ctx, _toConsumableArray(relPadding));
      ctx.font = font;
      ctx.fillStyle = this.color;
      ctx.textBaseline = "bottom";
      var drawTextStroke = this.strokeWidth > 0;
      if (drawTextStroke) {
        ctx.lineWidth = this.strokeWidth * this.fontSize / 10;
        ctx.strokeStyle = this.strokeColor;
      }
      lines.forEach(function(line, index) {
        var lineX = (innerWidth - ctx.measureText(line).width) / 2;
        var lineY = (index + 1) * _this2.fontSize;
        drawTextStroke && ctx.strokeText(line, lineX, lineY);
        ctx.fillText(line, lineX, lineY);
      });
      if (this.material.map)
        this.material.map.dispose();
      var texture = this.material.map = new three.CanvasTexture(canvas);
      texture.colorSpace = three.SRGBColorSpace;
      var yScale = this.textHeight * lines.length + border[1] * 2 + padding[1] * 2;
      this.scale.set(yScale * canvas.width / canvas.height, yScale, 0);
    }
  }, {
    key: "clone",
    value: function clone() {
      return new this.constructor(this.text, this.textHeight, this.color).copy(this);
    }
  }, {
    key: "copy",
    value: function copy(source) {
      three.Sprite.prototype.copy.call(this, source);
      this.color = source.color;
      this.backgroundColor = source.backgroundColor;
      this.padding = source.padding;
      this.borderWidth = source.borderWidth;
      this.borderColor = source.borderColor;
      this.fontFace = source.fontFace;
      this.fontSize = source.fontSize;
      this.fontWeight = source.fontWeight;
      this.strokeWidth = source.strokeWidth;
      this.strokeColor = source.strokeColor;
      return this;
    }
  }]);
}(three.Sprite);

// studio/src/utils/ArrowWithText.ts
class ArrowWithText extends Group3 {
  arrow;
  text;
  length;
  set to(to) {
    this.arrow.setDirection(to.clone().normalize());
    this.text.position.copy(to).multiplyScalar(this.length);
  }
  constructor(props) {
    const { to, text, color, length: length2 = 100, fontSize = 15 } = props;
    super();
    this.length = length2;
    this.arrow = new ArrowHelper3(to.normalize(), new Vector329(0, 0, 0), length2, color, 5, 5);
    this.add(this.arrow);
    this.text = new _default(text, fontSize, color.toString());
    this.text.position.copy(to).multiplyScalar(length2);
    this.add(this.text);
  }
}

// studio/src/utils/ToothLikePose/PoseHelper.ts
class PoseHelper extends Group4 {
  arrowVestibular = new ArrowWithText({ to: new Vector330(0, 0, 1), text: "V", color: "white", length: 20 });
  arrowOral = new ArrowWithText({ to: new Vector330(0, 0, 1), text: "O", color: "white", length: 20 });
  arrowMesial = new ArrowWithText({ to: new Vector330(0, 0, 1), text: "M", color: "white", length: 20 });
  arrowDistal = new ArrowWithText({ to: new Vector330(0, 0, 1), text: "D", color: "white", length: 20 });
  arrowRoot = new ArrowWithText({ to: new Vector330(0, 0, 1), text: "R", color: "white", length: 20 });
  arrowCrown = new ArrowWithText({ to: new Vector330(0, 0, 1), text: "C", color: "white", length: 20 });
  constructor() {
    super();
    this.add(this.arrowVestibular);
    this.add(this.arrowOral);
    this.add(this.arrowMesial);
    this.add(this.arrowDistal);
    this.add(this.arrowRoot);
    this.add(this.arrowCrown);
    const axesHelper = new AxesHelper2(100);
    this.add(axesHelper);
    const scene = App2.Instance().view.scene;
    scene.add(this);
  }
  setPose(pose) {
    const { crownRoot, mesioDistal, vestibuloOral } = pose.anatomicalBasis;
    this.arrowVestibular.to = vestibuloOral.clone().negate();
    this.arrowOral.to = vestibuloOral;
    this.arrowMesial.to = mesioDistal.clone().negate();
    this.arrowDistal.to = mesioDistal;
    this.arrowRoot.to = crownRoot;
    this.arrowCrown.to = crownRoot.clone().negate();
    this.position.copy(pose.origin);
  }
}

// studio/src/shared/MPR/utils/OrientationMath/BaselineOrientationMath.ts
import {Vector3 as Vector331} from "three";
class BaselineOrientationMath {
  up = new Vector331;
  forward = new Vector331;
  pose;
  setup(pose) {
    if (!pose) {
      throw new Error("Entity has no pose. For entities that don't have pose, use WorldOrientationMath");
    }
    this.pose = pose;
  }
  getOrientationForAxialCamera(out) {
    const quadrant = NumerationUtils.quadrantNumber(this.pose.numeration);
    if (quadrant === 1) {
      this.pose.copyCrownTo(this.forward);
    }
    if (quadrant === 2) {
      this.pose.copyCrownTo(this.forward);
    }
    if (quadrant === 3) {
      this.pose.copyRootTo(this.forward);
    }
    if (quadrant === 4) {
      this.pose.copyRootTo(this.forward);
    }
    this.up.set(0, -1, 0);
    out.setFromLookRotation(this.forward, this.up);
  }
  getOrientationForVestibuloOralCamera(out) {
    const quadrant = NumerationUtils.quadrantNumber(this.pose.numeration);
    if (quadrant === 1) {
      this.pose.copyVestibularTo(this.forward);
      this.pose.copyRootTo(this.up);
    }
    if (quadrant === 2) {
      this.pose.copyVestibularTo(this.forward);
      this.pose.copyRootTo(this.up);
    }
    if (quadrant === 3) {
      this.pose.copyVestibularTo(this.forward);
      this.pose.copyCrownTo(this.up);
    }
    if (quadrant === 4) {
      this.pose.copyVestibularTo(this.forward);
      this.pose.copyCrownTo(this.up);
    }
    out.setFromLookRotation(this.forward, this.up);
  }
  getOrientationForMesioDistalCamera(out) {
    const quadrant = NumerationUtils.quadrantNumber(this.pose.numeration);
    if (quadrant === 1) {
      this.pose.copyMesialTo(this.forward);
      this.pose.copyRootTo(this.up);
    }
    if (quadrant === 2) {
      this.pose.copyMesialTo(this.forward);
      this.pose.copyRootTo(this.up);
    }
    if (quadrant === 3) {
      this.pose.copyMesialTo(this.forward);
      this.pose.copyCrownTo(this.up);
    }
    if (quadrant === 4) {
      this.pose.copyMesialTo(this.forward);
      this.pose.copyCrownTo(this.up);
    }
    out.setFromLookRotation(this.forward, this.up);
  }
}

// studio/src/shared/MPR/utils/OrientationMath/WorldOrientationMath.ts
import {Vector3 as Vector332} from "three";

class WorldOrientationMath {
  up = new Vector332;
  forward = new Vector332;
  getOrientationForAxialCamera(out) {
    this.forward.set(0, 0, -1);
    this.up.set(0, -1, 0);
    out.setFromLookRotation(this.forward, this.up);
  }
  getOrientationForVestibuloOralCamera(out) {
    this.forward.set(0, -1, 0);
    this.up.set(0, 0, 1);
    out.setFromLookRotation(this.forward, this.up);
  }
  getOrientationForMesioDistalCamera(out) {
    this.forward.set(1, 0, 0);
    this.up.set(0, 0, 1);
    out.setFromLookRotation(this.forward, this.up);
  }
}

// studio/src/shared/MPR/utils/OrientationMath/OrientationMath.ts
class OrientationMathAbstract {
  baselineOrientationMath = new BaselineOrientationMath;
  worldOrientationMath = new WorldOrientationMath;
  math = this.baselineOrientationMath;
  pose;
  poseHelper = null;
  constructor() {
    if (DEBUG_ORIENTATION_MATH)
      this.poseHelper = new PoseHelper;
  }
  useWorldOrientation() {
    this.math = this.worldOrientationMath;
  }
  useBaselineOrientation(pose) {
    this.baselineOrientationMath.setup(pose);
    this.math = this.baselineOrientationMath;
  }
  getOrientationForAxialCamera(out) {
    this.math.getOrientationForAxialCamera(out);
  }
  getOrientationForVestibuloOralCamera(out) {
    this.math.getOrientationForVestibuloOralCamera(out);
  }
  getOrientationForMesioDistalCamera(out) {
    this.math.getOrientationForMesioDistalCamera(out);
  }
}

// studio/src/products/MPREmbedded/utils/OrientationMath/OrientationMathMPREmbedded.ts
class OrientationMathMPREmbedded extends OrientationMathAbstract {
  async setup(entity) {
    if (entity instanceof ToothLike) {
      this.useBaselineOrientation(entity.pose);
      return;
    }
    this.useWorldOrientation();
  }
}

// studio/src/shared/MPR/tools/MprAlignVolumeSliceToEntityOnSelect.ts
class MprAlignVolumeSliceToEntityOnSelect extends ViewTool {
  slices;
  cameras;
  orientationMath = new OrientationMathMPREmbedded;
  constructor(view) {
    super(view);
    this.slices = {};
    this.cameras = {};
    this.onSelect = this.onSelect.bind(this);
    this.onSetCameraByEntityAxes = this.onSetCameraByEntityAxes.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onAdd = this.onAdd.bind(this);
  }
  activate() {
    const objects = App2.Instance().view.getLayer("mpr")?.getObjects();
    if (objects) {
      objects.forEach((object) => {
        if (object instanceof VolumeSliceMPREmbedded) {
          this.slices[object.name] = object;
          if (object.linkedCamera) {
            this.cameras[object.name] = object.linkedCamera;
          }
        }
      });
    }
    App2.Instance().addEventListener("select", this.onSelect, 1);
    MPREmbeddedInstance.addEventListener("setCameraByEntityAxes", this.onSetCameraByEntityAxes);
    App2.Instance().addEventListener("add", this.onAdd);
    App2.Instance().addEventListener("delete", this.onDelete);
  }
  deactivate() {
    this.slices = {};
    this.cameras = {};
    App2.Instance().removeEventListener("select", this.onSelect);
    MPREmbeddedInstance.removeEventListener("setCameraByEntityAxes", this.onSetCameraByEntityAxes);
    App2.Instance().removeEventListener("add", this.onAdd);
    App2.Instance().removeEventListener("delete", this.onDelete);
  }
  alignCameraSliceContourWithEntity(kind, entity, zoom) {
    const camera = this.cameras[kind];
    if (!camera)
      return;
    if (kind === "Axial")
      this.orientationMath.getOrientationForAxialCamera(camera.quaternion);
    else if (kind === "Coronal")
      this.orientationMath.getOrientationForVestibuloOralCamera(camera.quaternion);
    else if (kind === "Sagittal")
      this.orientationMath.getOrientationForMesioDistalCamera(camera.quaternion);
    if (entity.object3d instanceof Mesh2) {
      const geometry = entity.object3d.geometry;
      if (!geometry.boundingBox)
        geometry.computeBoundingBox();
      const box = geometry.boundingBox.clone();
      box.applyMatrix4(entity.object3d.matrixWorld);
      box.getCenter(camera.position);
    } else {
      camera.position.copy(entity.object3d.position);
    }
    const slice = this.slices[kind];
    slice?.object3d.position.copy(camera.position);
    slice?.object3d.quaternion.copy(camera.quaternion);
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: App2.Instance().view.getViewport(kind) });
    if (slice)
      App2.Instance().dispatchEvent({ type: "transform", entities: [slice] });
    const canAdjustZoom = zoom && camera instanceof OrthographicCamera6;
    if (canAdjustZoom) {
      if (entity instanceof ToothLike) {
        const viewHeight = 33;
        const cameraFrustumHeight = camera.top - camera.bottom;
        camera.zoom = cameraFrustumHeight / viewHeight;
      } else {
        camera.zoom = 4;
        const isAdjusted = this.adjustCameraByMPRFOV(kind, camera);
        if (isAdjusted) {
          camera.updateProjectionMatrix();
          App2.Instance().dispatchEvent({ type: "transformCamera", viewport: this.hostObject.getViewport(kind) });
          App2.Instance().dispatchEvent({ type: "render" });
        }
      }
      slice?.fitToCamera();
    }
    MPREmbeddedInstance.dispatchEvent({ type: "\u0441ameraFocused", entity, viewport: this.hostObject.getViewport(kind) });
  }
  adjustCameraByMPRFOV(kind, camera) {
    const box = MPR2Storage_default.boundingBox;
    const cameraFrustumHeight = camera.top - camera.bottom;
    const cameraViewportHeight = (camera.top - camera.bottom) * 1 / camera.zoom;
    const boxCenter = box.getCenter(new Vector333);
    if (kind === "Axial") {
      const mprHeight = box.max.y - box.min.y;
      if (cameraViewportHeight > mprHeight) {
        camera.zoom = cameraFrustumHeight / mprHeight;
        camera.position.setY(boxCenter.y);
        return true;
      }
    } else if (kind === "Coronal" || kind === "Sagittal") {
      const mprHeight = box.max.z - box.min.z;
      if (cameraViewportHeight > mprHeight) {
        camera.zoom = cameraFrustumHeight / mprHeight;
        camera.position.setZ(boxCenter.z);
        return true;
      }
    }
    return false;
  }
  async alignMPREntitiesWithEntity(entity, updateZoom = true, viewportsNames = ["Axial", "Coronal", "Sagittal"]) {
    if (!entity)
      return;
    if (entity instanceof VolumeSliceMPREmbedded)
      return;
    this.orientationMath.setup(entity);
    for (const vpName of viewportsNames)
      this.alignCameraSliceContourWithEntity(vpName, entity, updateZoom);
  }
  onSelect(event) {
    const object = event.objects[0]?.entity;
    if (object instanceof ToothLike) {
      this.alignMPREntitiesWithEntity(object);
    }
  }
  onSetCameraByEntityAxes(event) {
    this.alignMPREntitiesWithEntity(event.object, event.changeZoom, event.viewportsNames);
  }
  onAdd(event) {
    if (event.container instanceof Layer ? event.container.getName() !== "mpr" : false)
      return;
    event.entities.forEach((object) => {
      if (object instanceof VolumeSliceMPREmbedded) {
        this.slices[object.name] = object;
        if (object.linkedCamera) {
          this.cameras[object.name] = object.linkedCamera;
        }
      }
    });
  }
  onDelete(event) {
    if (event.container instanceof Layer ? event.container.getName() !== "mpr" : false)
      return;
    event.entities.forEach((object) => {
      if (object instanceof VolumeSliceMPREmbedded) {
        this.slices[object.name] = undefined;
      }
    });
  }
}

// studio/src/products/MPREmbedded/tools/ContrastBrightness.ts
import {Vector2 as Vector212, MathUtils as MathUtils4} from "three";
class ContrastBrightness extends ViewTool {
  prevCursor = "";
  start = new Vector212;
  delta = new Vector212;
  clickedViewport;
  domElementMouseDown;
  constructor(view) {
    super(view);
    this.leftMouseDown = this.leftMouseDown.bind(this);
    this.rightMouseDown = this.rightMouseDown.bind(this);
    this.pointerMove = this.pointerMove.bind(this);
    this.pointerUp = this.pointerUp.bind(this);
  }
  activate() {
    this.domElementMouseDown = this.hostObject.getContainer();
    App2.Instance().mouseController.addListener("pointerdown", this.rightMouseDown, this.domElementMouseDown, "right", 20);
    App2.Instance().mouseController.addListener("pointerdown", this.leftMouseDown, this.domElementMouseDown, "left", 20);
  }
  deactivate() {
    App2.Instance().mouseController.removeListener("pointerdown", this.rightMouseDown, this.domElementMouseDown, "right");
    App2.Instance().mouseController.removeListener("pointerdown", this.leftMouseDown, this.domElementMouseDown, "left");
    App2.Instance().mouseController.removeListener("pointermove", this.pointerMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.pointerUp, document, "left");
  }
  getViewport() {
    const viewport = this.hostObject.getActiveViewport();
    if (!viewport)
      return;
    if (!MPRViewportNames.includes(viewport.name))
      return;
    return viewport;
  }
  rightMouseDown(event) {
    event.preventDefault();
    if (event.shiftKey || event.ctrlKey || event.metaKey)
      return;
    const viewport = this.getViewport();
    if (!viewport)
      return;
    MPR2Storage_default.resetColorProfileToInitial();
    MPREmbeddedInstance.dispatchEvent({ type: "colorProfileChanged" });
    App2.Instance().dispatchEvent({ type: "render" });
  }
  dragMoved = false;
  leftMouseDown(event) {
    this.dragMoved = false;
    event.preventDefault();
    if (event.shiftKey || event.ctrlKey || event.metaKey)
      return;
    const viewport = this.getViewport();
    if (!viewport)
      return;
    this.clickedViewport = viewport;
    this.prevCursor = this.domElementMouseDown.style.cursor;
    this.domElementMouseDown.style.cursor = "all-scroll";
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    App2.Instance().mouseController.addListener("pointermove", this.pointerMove, document, 9);
    App2.Instance().mouseController.addListener("pointerup", this.pointerUp, document, "left", 20);
  }
  pointerMove(event) {
    const vp = this.clickedViewport;
    if (!vp)
      return;
    this.delta.x = event.clientX - this.start.x;
    this.delta.y = -(event.clientY - this.start.y);
    MPR2Storage_default.contrast = MathUtils4.clamp(MPR2Storage_default.contrast + this.delta.x / vp.rect.width, -1, 1);
    MPR2Storage_default.brightness = MathUtils4.clamp(MPR2Storage_default.brightness + this.delta.y / vp.rect.height, -1, 1);
    MPREmbeddedInstance.dispatchEvent({ type: "colorProfileChanged" });
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    App2.Instance().dispatchEvent({ type: "render" });
    this.dragMoved = true;
  }
  pointerUp() {
    this.domElementMouseDown.style.cursor = this.prevCursor;
    this.prevCursor = "";
    App2.Instance().mouseController.removeListener("pointermove", this.pointerMove, document);
    App2.Instance().mouseController.removeListener("pointerup", this.pointerUp, document, "left");
  }
}

// studio/src/products/MPREmbedded/config/mprAppViewToolsAndModes.ts
var mprAppServiceTools = new Set([
  MPR2Reset,
  MPR2SyncVolumeSlicesWithColorProfile
]);
var mprAppViewTools = tuple(ActiveViewSwitcher, ContrastBrightness, MPR2ViewportScreenshot, ListenAndControlCrossLineVisibility, MprAlignVolumeSliceToEntityOnSelect);

class MprAppViewMode extends Mode {
  static except = (tools) => new MprAppViewMode({ tools: { toolList: tools, include: false } });
  static only = (tools) => new MprAppViewMode({ tools: { toolList: tools, include: true } });
}
var mprAppViewModes = {
  mainMode: MprAppViewMode.except([ContrastBrightness]),
  contrastBrightnessMode: MprAppViewMode.only([ActiveViewSwitcher, ContrastBrightness, MPR2ViewportScreenshot]),
  panMode: MprAppViewMode.except([ContrastBrightness]),
  zoomMode: MprAppViewMode.except([ContrastBrightness]),
  scrollThroughVolumeMode: MprAppViewMode.except([ContrastBrightness])
};

// studio/src/products/MPREmbedded/utils/MPR2Layout.ts
class MPR2Layout {
  axial = document.createElement("div");
  sagittal = document.createElement("div");
  coronal = document.createElement("div");
  mainView = document.createElement("div");
  main = document.createElement("div");
  element = document.createElement("div");
  getViewportElement = (viewportName) => {
    switch (viewportName) {
      case "Axial":
        return this.axial;
      case "Sagittal":
        return this.sagittal;
      case "Coronal":
        return this.coronal;
      case "Main":
        return this.main;
      default: {
        console.error(`Viewport ${viewportName} not found`);
        return;
      }
    }
  };
  constructor() {
    this.element.dataset.id = "MPR2Layout";
    this.axial.dataset.id = "axial";
    this.sagittal.dataset.id = "sagittal";
    this.coronal.dataset.id = "coronal";
    this.mainView.dataset.id = "mainView";
    this.main.dataset.id = "main";
    this.element.style.position = "relative";
    this.element.style.width = "100%";
    this.element.style.height = "100%";
    this.mainView.style.position = "absolute";
    this.mainView.style.top = "0";
    this.mainView.style.left = "0";
    this.mainView.style.width = "100%";
    this.mainView.style.height = "100%";
    this.row3();
    this.mainView.appendChild(this.main);
    this.mainView.appendChild(this.axial);
    this.mainView.appendChild(this.sagittal);
    this.mainView.appendChild(this.coronal);
    this.element.appendChild(this.mainView);
  }
  row3 = () => {
    this.coronal.style.position = "absolute";
    this.coronal.style.top = "0";
    this.coronal.style.left = "0";
    this.coronal.style.width = "33.3%";
    this.coronal.style.height = "100%";
    this.sagittal.style.position = "absolute";
    this.sagittal.style.top = "0";
    this.sagittal.style.left = "33.3%";
    this.sagittal.style.width = "33.3%";
    this.sagittal.style.height = "100%";
    this.axial.style.position = "absolute";
    this.axial.style.top = "0";
    this.axial.style.left = "66.6%";
    this.axial.style.width = "33.3%";
    this.axial.style.height = "100%";
    this.main.style.position = "absolute";
    this.main.style.top = "0";
    this.main.style.left = "0";
    this.main.style.width = "0";
    this.main.style.height = "0";
  };
  twoByTwo = () => {
    this.axial.style.position = "absolute";
    this.axial.style.top = "0";
    this.axial.style.left = "0";
    this.axial.style.width = "50%";
    this.axial.style.height = "50%";
    this.sagittal.style.position = "absolute";
    this.sagittal.style.top = "0";
    this.sagittal.style.left = "50%";
    this.sagittal.style.width = "50%";
    this.sagittal.style.height = "50%";
    this.coronal.style.position = "absolute";
    this.coronal.style.top = "50%";
    this.coronal.style.left = "0";
    this.coronal.style.width = "50%";
    this.coronal.style.height = "50%";
    this.main.style.position = "absolute";
    this.main.style.top = "50%";
    this.main.style.left = "50%";
    this.main.style.width = "50%";
    this.main.style.height = "50%";
  };
  row4 = () => {
    this.coronal.style.position = "absolute";
    this.coronal.style.top = "0";
    this.coronal.style.left = "0";
    this.coronal.style.width = "25%";
    this.coronal.style.height = "100%";
    this.sagittal.style.position = "absolute";
    this.sagittal.style.top = "0";
    this.sagittal.style.left = "25%";
    this.sagittal.style.width = "25%";
    this.sagittal.style.height = "100%";
    this.axial.style.position = "absolute";
    this.axial.style.top = "0";
    this.axial.style.left = "50%";
    this.axial.style.width = "25%";
    this.axial.style.height = "100%";
    this.main.style.position = "absolute";
    this.main.style.top = "0";
    this.main.style.left = "75%";
    this.main.style.width = "25%";
    this.main.style.height = "100%";
  };
}

// studio/src/products/MPREmbedded/config/mprStartupsConfig.ts
import {Vector3 as Vector340} from "three";

// studio/src/shared/MPR/startupSystems/AddCameraBodyHelperToVolumeSlice/CameraBodyHelper.ts
import {BoxGeometry, ConeGeometry, CylinderGeometry, EdgesGeometry, Group as Group5, LineBasicMaterial as LineBasicMaterial2, LineSegments as LineSegments4} from "three";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";

class CameraBodyHelper extends Group5 {
  constructor(color = "white") {
    super();
    const cone = new ConeGeometry(0.5, 1, 4);
    cone.rotateY(Math.PI / 4);
    cone.scale(1, 0.5, 1);
    cone.translate(0, 0.25, 0);
    const box = new BoxGeometry(0.75, 1, 1);
    box.translate(0, 1, 0);
    const cylinder = new CylinderGeometry(0.25, 0.25, 0.5, 8);
    const cylinder2 = new CylinderGeometry(0.25, 0.25, 0.5, 8);
    cylinder.rotateZ(Math.PI / 2);
    cylinder2.rotateZ(Math.PI / 2);
    cylinder.translate(0, 0.75, 0.75);
    cylinder2.translate(0, 1.25, 0.75);
    const cameraGeometry = new EdgesGeometry(BufferGeometryUtils.mergeBufferGeometries([cone, box, cylinder, cylinder2]));
    cameraGeometry.rotateX(-Math.PI / 2);
    cameraGeometry.scale(1, 1, -1);
    const line = new LineSegments4(cameraGeometry, new LineBasicMaterial2({ color }));
    this.add(line);
    line.scale.set(20, 20, 20);
  }
}

// studio/src/shared/MPR/startupSystems/AddCameraBodyHelperToVolumeSlice/AddCameraBodyHelperToVolumeSlice.ts
var AddCameraBodyHelperToVolumeSlice = async () => {
  await waitForApplicationReady();
  const bind = (name) => {
    const slice = VolumeSliceAbstract2.find(name);
    if (!slice) {
      console.log(`[MPR2AddCameraBodyHelperToVolumeSlice] ${name} volume slice not found`);
      return;
    }
    let color = "";
    if (slice.name === "Axial") {
      color = "red";
    }
    if (slice.name === "Coronal") {
      color = "green";
    }
    if (slice.name === "Sagittal") {
      color = "blue";
    }
    const cameraBody = new CameraBodyHelper(color);
    slice.linkedCamera.add(cameraBody);
    App2.Instance().view.scene.add(slice.linkedCamera);
  };
  bind("Axial");
  bind("Coronal");
  bind("Sagittal");
};

// studio/src/core/StartupSystem.ts
class StartupSystem {
  async start() {
  }
}

// studio/src/shared/startupSystems/FireApplicationReadyEvent.ts
class FireApplicationReadyEvent extends StartupSystem {
  async start() {
    App2.Instance().dispatchEvent({ type: "applicationReady" });
    resolveWaitForApplicationReady();
  }
}

// studio/src/shared/startupSystems/SetupCameras.ts
import {Vector3 as Vector334} from "three";
var SetupViewportCameras = () => {
  const axial = App2.Instance().view.getViewport("Axial");
  const sagittal = App2.Instance().view.getViewport("Sagittal");
  const coronal = App2.Instance().view.getViewport("Coronal");
  const pivot = new Vector334(0, 0, 0);
  if (axial) {
    axial.camera.zoom = 3;
    axial.camera.updateProjectionMatrix();
    axial.camera.name = "Axial";
  }
  if (sagittal) {
    sagittal.camera.zoom = 3;
    sagittal.camera.updateProjectionMatrix();
    sagittal.camera.name = "Sagittal";
  }
  if (coronal) {
    coronal.camera.zoom = 3;
    coronal.camera.updateProjectionMatrix();
    coronal.camera.name = "Coronal";
  }
  if (axial)
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: axial });
  if (sagittal)
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: sagittal });
  if (coronal)
    App2.Instance().dispatchEvent({ type: "transformCamera", viewport: coronal });
};

// studio/src/products/MPREmbedded/startupSystems/InstantiateTooth.ts
import {Vector3 as Vector338, Object3D as Object3D5} from "three";

// studio/src/shared/protocol/datatypes/primitives.ts
import {Vector3 as Vector335} from "three";
var Vector3FromFloatPoint3D = (floatPoint3D) => {
  return new Vector335(floatPoint3D.X, floatPoint3D.Y, floatPoint3D.Z);
};

// studio/src/shared/protocol/datatypes/AnatomicalBasis3D.ts
var fromAnatomicalBasis3D = (anatomicalBasis3D) => {
  if (!anatomicalBasis3D.MesioDistal || !anatomicalBasis3D.VestibuloOral || !anatomicalBasis3D.Axial) {
    throw new Error("[fromAnatomicalBasis3D] anatomicalBasis3D is missing required fields");
  }
  return {
    mesioDistal: Vector3FromFloatPoint3D(anatomicalBasis3D.MesioDistal),
    vestibuloOral: Vector3FromFloatPoint3D(anatomicalBasis3D.VestibuloOral),
    crownRoot: Vector3FromFloatPoint3D(anatomicalBasis3D.Axial)
  };
};

// studio/src/utils/anatomicalBasisSanityCheck.ts
import {Vector3 as Vector336} from "three";
var anatomicalBasisSanityCheck = (anatomicalBasis, numeration) => {
  const quadrant = NumerationUtils.quadrantNumber(numeration);
  if (numeration.idx !== undefined && numeration.idx > 0) {
    console.log("Supernumerary tooth are unpredicatable, skipping sanity check");
    return { valid: true };
  }
  {
    const distal = anatomicalBasis.mesioDistal;
    const dot = distal.dot(new Vector336(0, -1, 0));
    const TOLERANCE = 0.2;
    const sane = dot < TOLERANCE;
    if (!sane) {
      return { valid: false, message: `mesioDistal should never be the same direction as +Y`, code: 1 };
    }
  }
  {
    const oral = anatomicalBasis.vestibuloOral;
    const dot = oral.dot(new Vector336(0, -1, 0));
    const TOLERANCE = 0.3;
    const sane = dot < TOLERANCE;
    if (!sane) {
      return { valid: false, message: `vestibuloOral should never be the same direction as -Y`, code: 2 };
    }
  }
  {
    const root = anatomicalBasis.crownRoot;
    const z = new Vector336(0, 0, 1);
    const dot = root.dot(z);
    if (quadrant === 1 || quadrant === 2) {
      const sane = dot > 0;
      if (!sane) {
        return { valid: false, message: `crownRoot should be approximately collinear and in the same direction as +Z`, code: 3 };
      }
    }
    if (quadrant === 3 || quadrant === 4) {
      const sane = dot < 0;
      if (!sane) {
        return { valid: false, message: `crownRoot should be approximately collinear and in the same direction as -Z`, code: 4 };
      }
    }
  }
  {
    const rootCrossVestibular = anatomicalBasis.crownRoot.clone().cross(anatomicalBasis.vestibuloOral.clone().negate());
    const rootCrossVestibularDotDistal = rootCrossVestibular.dot(anatomicalBasis.mesioDistal);
    if (quadrant === 1 || quadrant === 3) {
      const sane = rootCrossVestibularDotDistal < 0;
      if (!sane) {
        return {
          valid: false,
          message: `Root cross Vestibular should be approximately collinear and in the same direction as Mesial`,
          code: 4
        };
      }
    }
    if (quadrant === 2 || quadrant === 4) {
      const sane = rootCrossVestibularDotDistal > 0;
      if (!sane) {
        return {
          valid: false,
          message: `Root cross Vestibular should be approximately collinear and in the same direction as Distal`,
          code: 4
        };
      }
    }
  }
  return { valid: true };
};

// studio/src/products/common/utils/Report/HACKTeethReaderMedioDistalReverter.ts
class HACKTeethReaderMedioDistalReverter {
  static isPoseFailedMedioDistalSanityCheck(pose) {
    const sanityCheck = anatomicalBasisSanityCheck(pose.anatomicalBasis, pose.numeration);
    if (!sanityCheck.valid) {
      if (DEBUG_SANITY_CHECK_LOGS)
        console.error(`Anatomical basis sanity check failed for Tooth ${pose.numeration.iso}: ${sanityCheck.message}`);
      if (sanityCheck.code === 1) {
        return true;
      }
      return false;
    }
    return false;
  }
  static repairMesioDistalAxis(pose) {
    return new ToothLikePose(pose.origin, pose.numeration, {
      mesioDistal: pose.anatomicalBasis.mesioDistal.clone().negate(),
      vestibuloOral: pose.anatomicalBasis.vestibuloOral.clone(),
      crownRoot: pose.anatomicalBasis.crownRoot.clone()
    });
  }
}

// studio/src/products/MPREmbedded/utils/approximateAnatomicalBasis.ts
import {MathUtils as MathUtils5, Vector3 as Vector337} from "three";
var USUAL_AMOUNT_OF_TEETH_IN_A_QUADRANT = 7;
var approximateAnatomicalBasis = (numeration) => {
  const quadrant = NumerationUtils.quadrantNumber(numeration);
  const subnumber = numeration.iso % 10;
  const X = new Vector337(1, 0, 0);
  const Y = new Vector337(0, 1, 0);
  const Z = new Vector337(0, 0, 1);
  const alpha = MathUtils5.clamp(subnumber / USUAL_AMOUNT_OF_TEETH_IN_A_QUADRANT, 0, 1);
  if (quadrant === 1) {
    return {
      mesioDistal: new Vector337().lerpVectors(X.clone().negate(), Y, alpha).normalize(),
      vestibuloOral: new Vector337().lerpVectors(Y, X, alpha).normalize(),
      crownRoot: Z
    };
  }
  if (quadrant === 2) {
    return {
      mesioDistal: new Vector337().lerpVectors(X, Y, alpha).normalize(),
      vestibuloOral: new Vector337().lerpVectors(Y, X.clone().negate(), alpha).normalize(),
      crownRoot: Z
    };
  }
  if (quadrant === 3) {
    return {
      mesioDistal: new Vector337().lerpVectors(X, Y, alpha).normalize(),
      vestibuloOral: new Vector337().lerpVectors(Y, X.clone().negate(), alpha).normalize(),
      crownRoot: Z.clone().negate()
    };
  }
  if (quadrant === 4) {
    return {
      mesioDistal: new Vector337().lerpVectors(X.clone().negate(), Y, alpha).normalize(),
      vestibuloOral: new Vector337().lerpVectors(Y, X, alpha).normalize(),
      crownRoot: Z.clone().negate()
    };
  }
  throw new Error(`Unknown quadrant: ${quadrant}`);
};

// studio/src/products/MPREmbedded/startupSystems/InstantiateTooth.ts
var InstantiateTooth = (request) => () => {
  const findCenterOfTile = (numeration2) => {
    const subvolume = MPR2Storage_default.subvolumes.find((subvolume2) => NumerationUtils.isEqual(subvolume2.numeration, numeration2));
    if (!subvolume) {
      throw new Error(`SubVolume not found ${numeration2.iso}, ${numeration2.idx}`);
    }
    return Box3FromBox3Json(subvolume.box).getCenter(new Vector338);
  };
  const numeration = request.tooth.numeration;
  const position = request.tooth.origin ? new Vector338(request.tooth.origin.X, request.tooth.origin.Y, request.tooth.origin.Z) : findCenterOfTile(numeration);
  const mpr2tooth = new MPR2Tooth(new Object3D5, numeration);
  const layer = App2.Instance().view.getLayer("mpr");
  layer.add(mpr2tooth);
  const anatomicalBasis = request.tooth.anatomicalBasis ? fromAnatomicalBasis3D(request.tooth.anatomicalBasis) : approximateAnatomicalBasis(numeration);
  let pose = new ToothLikePose(position, numeration, anatomicalBasis);
  const isPoseFailedMedioDistalSanityCheck = HACKTeethReaderMedioDistalReverter.isPoseFailedMedioDistalSanityCheck(pose);
  if (isPoseFailedMedioDistalSanityCheck) {
    pose = HACKTeethReaderMedioDistalReverter.repairMesioDistalAxis(pose);
    if (DEBUG_SANITY_CHECK_LOGS)
      console.error(`Repairing pose for Tooth ${pose.numeration.iso}`);
  }
  mpr2tooth.setPose(pose);
  MPREmbeddedInstance.dispatchEvent({
    type: "setCameraByEntityAxes",
    object: mpr2tooth,
    changeZoom: true,
    viewportsNames: ["Axial", "Sagittal", "Coronal"]
  });
};

// studio/src/utils/PatientSpaceAxesHelper.ts
import {Group as Group6, Vector3 as Vector339} from "three";
class PatientSpaceAxesHelper extends Group6 {
  sagittalArrow = new ArrowWithText({ to: new Vector339(1, 0, 0), text: "Sagittal\nPlane\nAxis (X)", color: "white" });
  coronalArrow = new ArrowWithText({ to: new Vector339(0, 1, 0), text: "Coronal\nPlane\nAxis (Y)", color: "white" });
  axialArrow = new ArrowWithText({ to: new Vector339(0, 0, 1), text: "Axial\nPlane\nAxis (Z)", color: "white" });
  constructor() {
    super();
    this.add(this.sagittalArrow);
    this.add(this.coronalArrow);
    this.add(this.axialArrow);
    const scene = App2.Instance().view.scene;
    scene.add(this);
  }
}

// studio/src/products/MPREmbedded/config/mprStartupsConfig.ts
var mprStartupSystems = (request) => [
  SetupViewportCameras,
  () => {
    App2.Instance().view.getViewport("Axial").dispatchEvent({ type: "updatePivotPoint", point: new Vector340 });
    App2.Instance().view.getViewport("Sagittal").dispatchEvent({ type: "updatePivotPoint", point: new Vector340 });
    App2.Instance().view.getViewport("Coronal").dispatchEvent({ type: "updatePivotPoint", point: new Vector340 });
    VolumeSliceMPREmbedded.findAll().forEach((slice) => {
      slice.fitToCamera();
    });
    App2.Instance().view.viewports.forEach((viewport) => {
      viewport.init();
    });
  },
  InstantiateTooth(request),
  FireApplicationReadyEvent,
  () => DEBUG_CAMERA_BODY_HELPERS && AddCameraBodyHelperToVolumeSlice(),
  () => DEBUG_PATIENT_SPACE_HELPER && new PatientSpaceAxesHelper
];

// studio/src/core/CoreEvents.ts
var coreEventNames = [
  "transformCameraStart",
  "transformCameraEnd",
  "transformCamera",
  "WillReunload",
  "resize",
  "add",
  "delete",
  "addLayer",
  "deleteLayer",
  "ActiveViewportWillChange",
  "ActiveViewportChanged",
  "hover",
  "unhover",
  "select",
  "deselect",
  "render",
  "transform",
  "transformStart",
  "transformEnd",
  "resourceAdded",
  "contextMenuOpen",
  "contextMenuClose",
  "modeChanged",
  "visibility",
  "viewportSchemeChanged",
  "geometryChanged",
  "materialChanged",
  "setRenderingStyle",
  "fatal",
  "makeScreenshot",
  "applicationProgress",
  "applicationReady"
];

// studio/src/products/MPREmbedded/config/MprAppEvents.ts
var mprAppEventsNames = [
  ...coreEventNames,
  "reset",
  "applicationReady",
  "hideCrossLines",
  "showCrossLines",
  "addLayer",
  "setCameraByEntityAxes",
  "colorProfileChanged",
  "\u0441ameraFocused",
  "enableZoomSync",
  "disableZoomSync"
];

// studio/src/products/MPREmbedded/config/mprAppLayerNames.ts
var mprAppLayerNames = [
  "other",
  "mpr"
];

// studio/src/utils/assetUrl.ts
import urlJoin from "url-join";
var assetUrl = (fileID, propagateExt = undefined) => urlJoin(API_URI_BASE, "api/storage/download/file", fileID, propagateExt ? `#${propagateExt}` : "");

// studio/src/shared/Credentials.ts
var DEFAULT_CREDENTIALS_POLICY = (url) => IS_LOCALHOST_OR_FEATURE_BRANCH && url.includes(API_URI_BASE) ? "include" : "same-origin";

class Credentials {
  policy = DEFAULT_CREDENTIALS_POLICY;
  setPolicy(policy) {
    if (policy === undefined) {
      throw new Error("Policy is undefined");
    }
    this.policy = policy;
  }
  signLoader(loader) {
    loader.setCrossOrigin("use-credentials");
    loader.setWithCredentials(true);
  }
  unsignLoader(loader) {
    loader.setCrossOrigin("anonymous");
    loader.setWithCredentials(false);
  }
  fetchCredentials(url) {
    return this.policy(url);
  }
  corsifyLoader(loader, url) {
    if (IS_LOCALHOST_OR_FEATURE_BRANCH && url.includes(API_URI_BASE)) {
      this.signLoader(loader);
    } else {
      this.unsignLoader(loader);
    }
  }
}
var Credentials_default = new Credentials;

// studio/src/utils/fileFetcherService/FileFetcherServiceCore.ts
class FileFetcherServiceCore {
  async getFileByIDAsBinary(fileID, signal) {
    const url = assetUrl(fileID);
    const response = await fetch(url, {
      credentials: Credentials_default.fetchCredentials(url),
      signal
    });
    return response.arrayBuffer();
  }
  async getFileByIDAsJson(fileID, signal) {
    const url = assetUrl(fileID);
    const response = await fetch(url, {
      credentials: Credentials_default.fetchCredentials(url),
      signal
    });
    return response.json();
  }
}

// studio/src/utils/textureLoader/TextureLoaderBrowser.ts
import {PMREMGenerator, TextureLoader} from "three";
import {RGBELoader as RGBELoader2} from "three/examples/jsm/loaders/RGBELoader.js";

class TextureManagerBrowser {
  createTextureLoader(manager) {
    return new TextureLoader;
  }
  createRGBELoader(manager) {
    return new RGBELoader2;
  }
  createPMREMGenerator(renderer) {
    return new PMREMGenerator(renderer);
  }
}

// studio/src/products/MPREmbedded/api/MPRLauncher.ts
var MPR_LAUNCHER_LOGS = localStorage.getItem("MPR_LAUNCHER_LOGS") || "false";

class MPRLauncher {
  layout = new MPR2Layout;
  app = MPREmbeddedInstance;
  isCold = true;
  view;
  constructor() {
    this.view = this.app.view;
  }
  async start(request) {
    if (MPR_LAUNCHER_LOGS) {
      console.log("[MPRLauncher] start");
    }
    if (this.app.isInitialized) {
      console.log("[MPRLauncher] start is skipped because app is already initialized.                 Probably the react component was re-rendered if you are in dev mode");
      return Promise.resolve();
    }
    if (this.isCold) {
      await this.warmup(request);
      this.isCold = false;
    }
    const canvas = this.layout.mainView.querySelector("canvas") || undefined;
    const viewProps = {
      container: this.layout.mainView,
      rendererProps: {
        preserveDrawingBuffer: true,
        canvas
      },
      name: "MprApp"
    };
    this.app.init(viewProps);
    this.view.viewports.forEach((viewport) => {
      viewport.init();
    });
    this.view.activateMode("mainMode");
    MPR2Storage_default.put(request.volumes, request.colorProfile);
    Texture3DLoader_default.start(request.volumes.map((v) => ({ box: Box3FromBox3Json(v.box), fileId: v.fileId })), MPR2Storage_default.boundingBox, MPR2Storage_default.colorProfile);
    this.app.startupSystems.push(...mprStartupSystems(request));
    this.app.runStartups();
    return waitForApplicationReady();
  }
  async warmup(request) {
    if (MPR_LAUNCHER_LOGS) {
      console.log("[MPRLauncher] warmup");
    }
    const fileFetcherService = new FileFetcherServiceCore;
    Services.register("textureManager", new TextureManagerBrowser);
    Services.register("fileFetcherService", fileFetcherService);
    if (request.debug?.includeMainViewport) {
      this.layout.row4();
    }
    this.app.setListeners(mprAppEventsNames);
    this.view.toolController.setTools(mprAppViewTools);
    this.view.toolController.setServiceTools(mprAppServiceTools);
    this.view.toolController.setModes(mprAppViewModes);
    mprAppLayerNames.forEach((name) => this.view.addLayer(name));
    const coronalViewport = this.view.addViewport("Coronal", this.layout.coronal);
    coronalViewport.toolController.setTools(mprAppViewportTools);
    coronalViewport.toolController.setServiceTools(mprAppViewportServiceTools);
    coronalViewport.toolController.setModes(mprViewportModes.Coronal);
    const sagittalViewport = this.view.addViewport("Sagittal", this.layout.sagittal);
    sagittalViewport.toolController.setTools(mprAppViewportTools);
    sagittalViewport.toolController.setServiceTools(mprAppViewportServiceTools);
    sagittalViewport.toolController.setModes(mprViewportModes.Sagittal);
    const axialViewport = this.view.addViewport("Axial", this.layout.axial);
    axialViewport.toolController.setTools(mprAppViewportTools);
    axialViewport.toolController.setServiceTools(mprAppViewportServiceTools);
    axialViewport.toolController.setModes(mprViewportModes.Axial);
    if (request.debug?.includeMainViewport) {
      const mainViewport = App2.Instance().view.addViewport("Main", this.layout.main, { cameraControllerProps: { up: [0, 0, 1] } });
      mainViewport.toolController.setTools(mprAppMainViewportTools);
      mainViewport.toolController.setModes(mprAppMainViewportModes);
      mainViewport.camera.updateMatrixWorld();
      mainViewport.clearColor = 985626;
      mainViewport.alpha = 0;
    }
  }
  async dispose() {
    this.app.dispose();
    MPR2Storage_default.clear();
  }
}

// studio/src/products/MPREmbedded/api/MPREmbeddedApi.ts
var MPREMBEDDED_DETAILED_STATUS_LOGGING = localStorage.getItem("MPREMBEDDED_DETAILED_STATUS_LOGGING") === "true";

class MPREmbeddedApi {
  launcher = new MPRLauncher;
  app = this.launcher.app;
  eventsToApiBindings = [];
  getElement = () => this.launcher.layout.element;
  applicationReady = slot();
  modeChanged = slot();
  activateMode = slot();
  getCurrentMode = slot();
  makeActiveViewportScreenshot = slot();
  hideCrossLines = slot();
  showCrossLines = slot();
  reset = slot();
  setCredentialsPolicy = slot();
  activeViewportChanged = slot();
  enableZoomSync = slot();
  disableZoomSync = slot();
  disposeManager;
  constructor() {
    const start = async (request) => {
      await this.launcher.start(request);
      this.bindEventsToAPI();
    };
    const dispose = async () => {
      this.unbindEventsFromAPI();
      await this.launcher.dispose();
    };
    this.disposeManager = new DisposeScheduler(start, dispose);
  }
  start(request) {
    this.disposeManager.askForStart(request);
  }
  dispose() {
    this.disposeManager.askForDispose();
  }
  bindEventsToAPI() {
    this.eventsToApiBindings = [
      this.app.addEventListener("applicationReady", () => {
        this.applicationReady();
      }),
      this.app.addEventListener("modeChanged", (e) => {
        this.modeChanged({ previous: e.previous, mode: e.mode });
      }),
      this.activateMode.on((mode) => {
        this.app.view.activateMode(mode);
        this.app.dispatchEvent({ type: "render" });
      }),
      this.getCurrentMode.on(() => {
        return this.app.view.toolController.getModeName();
      }),
      this.makeActiveViewportScreenshot.on(() => {
        return new Promise((resolve) => {
          this.app.dispatchEvent({
            type: "makeScreenshot",
            onBlob: (viewportName, blob) => {
              resolve(blob);
            }
          });
        });
      }),
      this.hideCrossLines.on(() => {
        this.app.dispatchEvent({ type: "hideCrossLines" });
      }),
      this.showCrossLines.on(() => {
        this.app.dispatchEvent({ type: "showCrossLines" });
      }),
      this.reset.on(() => {
        this.app.dispatchEvent({ type: "reset" });
      }),
      this.setCredentialsPolicy.on((policy) => {
        Credentials_default.setPolicy(policy);
      }),
      this.enableZoomSync.on(() => {
        this.app.dispatchEvent({ type: "enableZoomSync" });
      }),
      this.disableZoomSync.on(() => {
        this.app.dispatchEvent({ type: "disableZoomSync" });
      }),
      this.app.addEventListener("ActiveViewportChanged", (e) => {
        const viewport = e.current;
        if (!viewport)
          return;
        const viewportName = viewport.name;
        const layoutElement = this.launcher.layout.getViewportElement(viewportName);
        if (!layoutElement)
          return;
        this.activeViewportChanged({ name: viewportName, layoutElement });
      })
    ];
  }
  unbindEventsFromAPI() {
    this.eventsToApiBindings.forEach((unsubscribe) => unsubscribe());
  }
}

class DisposeScheduler {
  start;
  dispose;
  DISPOSE_DELAY = 1e4;
  timeout = null;
  currentRequest = null;
  _previousStatus = "idle";
  _status = "idle";
  set status(value) {
    this._previousStatus = this._status;
    this._status = value;
    if (MPREMBEDDED_DETAILED_STATUS_LOGGING) {
      console.log("[MPREmbedded]", this._previousStatus, "=>", this._status, this.currentRequest?.tooth.numeration.iso);
    }
  }
  get status() {
    return this._status;
  }
  constructor(start, dispose) {
    this.start = start;
    this.dispose = dispose;
  }
  askForDispose = () => {
    if (MPREMBEDDED_DETAILED_STATUS_LOGGING) {
      console.log("[MPREmbedded] askForDispose", this.currentRequest?.tooth.numeration.iso, ". status:", this.status);
    }
    switch (this.status) {
      case "idle":
        return;
      case "starting":
        this.status = "queuedToDispose";
        this.setupDisposeTimeout();
        break;
      case "ready":
        this.status = "queuedToDispose";
        this.setupDisposeTimeout();
        break;
      case "queuedToDispose":
        this.setupDisposeTimeout();
        break;
      case "disposing":
        break;
    }
  };
  askForStart = async (request) => {
    if (MPREMBEDDED_DETAILED_STATUS_LOGGING) {
      console.log("[MPREmbedded] askForStart ", request.tooth.numeration.iso, ". status:", this.status);
    }
    switch (this.status) {
      case "idle":
        this.currentRequest = request;
        this.status = "starting";
        try {
          await this.start(request);
          if (this.status !== "starting") {
            if (this.status === "queuedToDispose") {
              await this.executeDispose();
            }
            return;
          }
          this.status = "ready";
        } catch (error) {
          console.error("[MPR2] Error starting app", error);
          this.status = "idle";
        }
        break;
      case "starting":
        if (this.areRequestsEqual(this.currentRequest, request)) {
        } else {
          const nextRequest = request;
          const currentStatus = this.status;
          const interval = setInterval(async () => {
            if (this.status !== currentStatus) {
              clearInterval(interval);
              await this.executeDisposeAndStart(nextRequest);
            }
          }, 100);
        }
        break;
      case "ready":
        if (this.areRequestsEqual(this.currentRequest, request)) {
        } else {
          await this.executeDisposeAndStart(request);
        }
        break;
      case "queuedToDispose":
        if (this.areRequestsEqual(this.currentRequest, request)) {
          this.cancelDispose();
          this.status = this._previousStatus;
        } else {
          this.cancelDispose();
          await this.executeDisposeAndStart(request);
        }
        break;
      case "disposing":
        const disposingStatus = this.status;
        const checkInterval = setInterval(async () => {
          if (this.status !== disposingStatus) {
            clearInterval(checkInterval);
            this.currentRequest = request;
            this.status = "starting";
            try {
              await this.start(request);
              if (this.status !== "starting") {
                if (this.status === "queuedToDispose") {
                  await this.executeDispose();
                }
                return;
              }
              this.status = "ready";
            } catch (error) {
              console.error("[MPR2] Error starting app after disposal", error);
              this.status = "idle";
            }
          }
        }, 100);
        break;
    }
  };
  executeDisposeAndStart = async (request) => {
    this.status = "disposing";
    try {
      await this.dispose();
      this.status = "idle";
      this.currentRequest = request;
      this.status = "starting";
      await this.start(request);
      if (this.status !== "starting") {
        if (this.status === "queuedToDispose") {
          await this.executeDispose();
        }
        return;
      }
      this.status = "ready";
    } catch (error) {
      this.status = "idle";
    }
  };
  areRequestsEqual = (a, b) => {
    if (!a || !b)
      return false;
    return NumerationUtils.isEqual(a.tooth.numeration, b.tooth.numeration);
  };
  setupDisposeTimeout = () => {
    this.cancelDispose();
    this.timeout = window.setTimeout(async () => {
      await this.executeDispose();
    }, this.DISPOSE_DELAY);
  };
  cancelDispose = () => {
    if (this.timeout)
      window.clearTimeout(this.timeout);
    this.timeout = null;
  };
  executeDispose = async () => {
    if (this.status === "idle")
      return;
    this.status = "disposing";
    this.cancelDispose();
    try {
      await this.dispose();
      this.currentRequest = null;
      this.status = "idle";
    } catch (error) {
      this.status = "idle";
    }
  };
}
export {
  MPREmbeddedApi
};
